import React, { useState, useEffect } from "react";
import { db, auth } from "../firebase";
import { collection, getDocs, doc, setDoc, deleteDoc, getDoc, addDoc, writeBatch } from "firebase/firestore";
import { signOut } from "firebase/auth";
import { useNavigate } from "react-router-dom";
import { useToast, ToastContainer } from "../hooks/useToast";
import { getDefaultSettings } from "../hooks/useSettings";
import { TIER_COLORS } from "../constants/tiers";
import { ALL_ROLES } from "./StudentPortal";

const MAROON = "#6f0000";
const DARK = "#1C0A0A";
const FUNCTIONS_BASE = "https://us-central1-tamutrip-e5d5f.cloudfunctions.net";

function generatePresentationSlots(startTime, endTime, slotMinutes) {
  const slots = [];
  const toMin = t => { const [h,m] = t.split(":").map(Number); return h*60+m; };
  const toDisplay = m => { const h = Math.floor(m/60); const min = m%60; const ampm = h>=12?"PM":"AM"; const hh = h>12?h-12:h===0?12:h; return `${hh}:${min.toString().padStart(2,"0")} ${ampm}`; };
  let cur = toMin(startTime);
  const end = toMin(endTime);
  while (cur + slotMinutes <= end) {
    slots.push({ time: toDisplay(cur), minutes: cur });
    cur += slotMinutes;
  }
  return slots;
}

// Convert "HH:MM" (24hr) to "H:MM AM/PM"
function fmt12(t) {
  if (!t) return "";
  if (/am|pm/i.test(t)) return t;
  const [hh, mm] = t.split(":").map(Number);
  const ap = hh >= 12 ? "PM" : "AM";
  const h = hh > 12 ? hh - 12 : hh === 0 ? 12 : hh;
  return `${h}:${String(mm).padStart(2, "0")} ${ap}`;
}

// AM/PM time picker — stores as "HH:MM" internally
function TimeSelect({ value, onChange }) {
  const parse = v => {
    if (!v) return { h: 8, m: "00", ap: "AM" };
    const [hh, mm] = v.split(":").map(Number);
    return { h: hh > 12 ? hh - 12 : hh === 0 ? 12 : hh, m: String(mm).padStart(2, "0"), ap: hh >= 12 ? "PM" : "AM" };
  };
  const { h, m, ap } = parse(value);
  const emit = (nh, nm, nap) => {
    let h24 = parseInt(nh);
    if (nap === "PM" && h24 !== 12) h24 += 12;
    if (nap === "AM" && h24 === 12) h24 = 0;
    onChange(String(h24).padStart(2, "0") + ":" + nm);
  };
  const sel = { border: "1.5px solid #ebebeb", borderRadius: 8, fontSize: 13, outline: "none", background: "white", padding: "9px 6px", cursor: "pointer", appearance: "none", WebkitAppearance: "none", textAlign: "center" };
  return (
    <div style={{ display: "flex", gap: 4, alignItems: "center" }}>
      <select value={h} onChange={e => emit(e.target.value, m, ap)} style={{ ...sel, width: 52 }}>
        {[1,2,3,4,5,6,7,8,9,10,11,12].map(n => <option key={n} value={n}>{n}</option>)}
      </select>
      <span style={{ fontWeight: 700, color: "#555", fontSize: 15 }}>:</span>
      <select value={m} onChange={e => emit(h, e.target.value, ap)} style={{ ...sel, width: 52 }}>
        {["00","05","10","15","20","25","30","35","40","45","50","55"].map(n => <option key={n} value={n}>{n}</option>)}
      </select>
      <select value={ap} onChange={e => emit(h, m, e.target.value)} style={{ ...sel, width: 58 }}>
        <option value="AM">AM</option>
        <option value="PM">PM</option>
      </select>
    </div>
  );
}

function BarChart({ label, value, max, color = MAROON }) {
  const pct = max > 0 ? Math.round((value / max) * 100) : 0;
  return (
    <div style={{ marginBottom: 10 }}>
      <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 5 }}>
        <span style={{ fontSize: 13, color: "#555", fontWeight: 500 }}>{label}</span>
        <span style={{ fontSize: 13, fontWeight: 700, color: "#333" }}>{value}</span>
      </div>
      <div style={{ background: "#f0f0f0", borderRadius: 4, height: 8 }}>
        <div style={{ background: color, height: 8, borderRadius: 4, width: `${pct}%`, transition: "width 0.6s" }} />
      </div>
    </div>
  );
}

function useAnimatedValue(target) {
  const numStr = String(target);
  const num = parseInt(numStr.replace(/\D/g, "")) || 0;
  const [disp, setDisp] = React.useState(num ? 0 : target);
  React.useEffect(() => {
    if (!num) { setDisp(target); return; }
    let cur = 0;
    const steps = 22;
    const inc = num / steps;
    const id = setInterval(() => {
      cur = Math.min(cur + inc, num);
      if (cur >= num) { setDisp(target); clearInterval(id); }
      else setDisp(Math.round(cur) + numStr.slice(numStr.search(/\D/)));
    }, 28);
    return () => clearInterval(id);
  }, [target]); // eslint-disable-line
  return disp;
}

function Skeleton({ w = "100%", h = 16, r = 8, mb = 0 }) {
  return <div className="skeleton" style={{ width: w, height: h, borderRadius: r, marginBottom: mb }} />;
}

function StatCard({ label, value, sub, color, icon, onClick, sparkData }) {
  const displayed = useAnimatedValue(value);
  return (
    <div onClick={onClick} className="lift"
      style={{ background: "white", borderRadius: 14, padding: "20px 22px", boxShadow: "0 1px 3px rgba(0,0,0,0.06), 0 4px 16px rgba(0,0,0,0.05)", borderLeft: `4px solid ${color}`, cursor: onClick ? "pointer" : "default" }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start" }}>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontSize: 11, fontWeight: 700, color: "#9ca3af", textTransform: "uppercase", letterSpacing: 0.7, marginBottom: 10 }}>{label}</div>
          <div style={{ fontSize: 30, fontWeight: 900, color: "#0f172a", letterSpacing: -1, lineHeight: 1, marginBottom: 6 }}>{displayed}</div>
          {sub && <div style={{ fontSize: 11, color, fontWeight: 700 }}>{sub}</div>}
        </div>
        <div style={{ display: "flex", flexDirection: "column", alignItems: "flex-end", gap: 6, flexShrink: 0, marginLeft: 12 }}>
          <div style={{ width: 38, height: 38, background: color + "18", borderRadius: 10, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 18 }}>{icon}</div>
          {sparkData && <Sparkline data={sparkData} color={color} />}
        </div>
      </div>
    </div>
  );
}

function AlertItem({ type, message, action, onAction }) {
  const styles = {
    warning: { bg: "#fffbeb", border: "#fcd34d", color: "#92400e", icon: "⚠" },
    error: { bg: "#fff1f0", border: "#fca5a5", color: "#991b1b", icon: "✗" },
    success: { bg: "#f0fdf4", border: "#86efac", color: "#166534", icon: "✓" },
  };
  const s = styles[type] || styles.warning;
  return (
    <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "10px 14px", borderRadius: 8, background: s.bg, border: `1px solid ${s.border}`, marginBottom: 8 }}>
      <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
        <span style={{ color: s.color }}>{s.icon}</span>
        <span style={{ fontSize: 13, color: s.color, fontWeight: 500 }}>{message}</span>
      </div>
      {action && <button onClick={onAction} style={{ background: "none", border: `1px solid ${s.border}`, color: s.color, padding: "4px 12px", borderRadius: 6, fontSize: 11, cursor: "pointer", fontWeight: 700, marginLeft: 10 }}>{action}</button>}
    </div>
  );
}

function MiniBar({ pct }) {
  return (
    <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
      <div style={{ flex: 1, background: "#f0f0f0", borderRadius: 3, height: 6, minWidth: 60 }}>
        <div style={{ background: pct === 100 ? "#22c55e" : pct > 60 ? MAROON : "#f59e0b", height: 6, borderRadius: 3, width: `${pct}%`, transition: "width 0.4s" }} />
      </div>
      <span style={{ fontSize: 11, color: "#aaa", minWidth: 28 }}>{pct}%</span>
    </div>
  );
}

function Sparkline({ data = [], color = MAROON, height = 28, width = 72 }) {
  if (data.length < 2) return null;
  const min = Math.min(...data);
  const max = Math.max(...data);
  const range = max - min || 1;
  const pts = data.map((v, i) => {
    const x = (i / (data.length - 1)) * width;
    const y = height - 2 - ((v - min) / range) * (height - 6);
    return `${x.toFixed(1)},${y.toFixed(1)}`;
  }).join(" ");
  const last = pts.split(" ").pop().split(",");
  return (
    <svg width={width} height={height} style={{ overflow: "visible", display: "block" }}>
      <polyline points={pts} fill="none" stroke={color} strokeWidth={1.5} strokeLinecap="round" strokeLinejoin="round" opacity={0.55} />
      <circle cx={last[0]} cy={last[1]} r={2.5} fill={color} opacity={0.85} />
    </svg>
  );
}

function StatusPill({ status }) {
  const map = {
    complete: { bg: "#f0fdf4", color: "#166534", border: "#86efac", label: "Complete" },
    missing_photo: { bg: "#fffbeb", color: "#92400e", border: "#fcd34d", label: "No Photo" },
    missing_resume: { bg: "#fff1f0", color: "#991b1b", border: "#fca5a5", label: "No Resume" },
    missing_both: { bg: "#fdf2f8", color: "#6b21a8", border: "#d8b4fe", label: "Incomplete" },
    in_progress: { bg: "#eff6ff", color: "#1d4ed8", border: "#93c5fd", label: "In Progress" },
  };
  const s = map[status] || map.in_progress;
  return <span style={{ background: s.bg, color: s.color, border: `1px solid ${s.border}`, padding: "3px 10px", borderRadius: 20, fontSize: 11, fontWeight: 700 }}>{s.label}</span>;
}

function SettingsTab({ settings, onSave, card, btn, MAROON }) {
  const [draft, setDraft] = useState(() => {
    const s = JSON.parse(JSON.stringify(settings));
    const defs = getDefaultSettings();
    if (!s.boardWeek) s.boardWeek = defs.boardWeek;
    return s;
  });
  const [activeSection, setActiveSection] = useState("general");
  const [saved, setSaved] = useState(false);
  const [dragEvent, setDragEvent] = useState(null); // { dayIdx, idx } of the event currently being dragged
  const [dragOverIdx, setDragOverIdx] = useState(null);

  const EVENT_TYPES = [
    { value: "interviews", label: "Interviews", needsTier: true },
    { value: "draft", label: "Draft", needsTier: true },
    { value: "showcase", label: "Showcase", needsTier: false },
    { value: "networking", label: "Networking", needsTier: false },
    { value: "breakfast", label: "Breakfast", needsTier: false },
    { value: "lunch", label: "Lunch", needsTier: false },
    { value: "dinner", label: "Dinner", needsTier: false },
    { value: "custom", label: "Custom", needsTier: false },
  ];

  function updateSlotMinutes(value) {
    setDraft(prev => ({ ...prev, boardWeek: { ...prev.boardWeek, slotMinutes: parseInt(value) || 30 } }));
  }
  function addDay() {
    setDraft(prev => {
      const next = JSON.parse(JSON.stringify(prev));
      next.boardWeek.days.push({ id: `d_${Date.now()}`, label: "New Day", events: [] });
      return next;
    });
  }
  function removeDay(dayIdx) {
    if (!window.confirm("Remove this day and all its events?")) return;
    setDraft(prev => {
      const next = JSON.parse(JSON.stringify(prev));
      next.boardWeek.days.splice(dayIdx, 1);
      return next;
    });
  }
  function updateDayLabel(dayIdx, value) {
    setDraft(prev => {
      const next = JSON.parse(JSON.stringify(prev));
      next.boardWeek.days[dayIdx].label = value;
      return next;
    });
  }
  function updateScheduleEvent(dayIdx, idx, field, value) {
    setDraft(prev => {
      const next = JSON.parse(JSON.stringify(prev));
      next.boardWeek.days[dayIdx].events[idx][field] = value;
      return next;
    });
  }
  function addScheduleEvent(dayIdx) {
    setDraft(prev => {
      const next = JSON.parse(JSON.stringify(prev));
      const id = `evt_${dayIdx}_${Date.now()}`;
      next.boardWeek.days[dayIdx].events.push({ id, type: "custom", name: "New Event", startTime: "", endTime: "", enabled: true });
      return next;
    });
  }
  function removeScheduleEvent(dayIdx, idx) {
    setDraft(prev => {
      const next = JSON.parse(JSON.stringify(prev));
      next.boardWeek.days[dayIdx].events.splice(idx, 1);
      return next;
    });
  }
  function moveScheduleEvent(dayIdx, fromIdx, toIdx) {
    if (fromIdx === toIdx) return;
    setDraft(prev => {
      const next = JSON.parse(JSON.stringify(prev));
      const events = next.boardWeek.days[dayIdx].events;
      const [moved] = events.splice(fromIdx, 1);
      events.splice(toIdx, 0, moved);
      return next;
    });
  }

  function updateDraft(path, value) {
    const keys = path.split(".");
    const updated = JSON.parse(JSON.stringify(draft));
    let obj = updated;
    for (let i = 0; i < keys.length - 1; i++) obj = obj[keys[i]];
    obj[keys[keys.length - 1]] = value;
    setDraft(updated);
  }

  function handleSave() { onSave(draft); setSaved(true); setTimeout(() => setSaved(false), 3000); }
  function handleReset() { if (!window.confirm("Reset all settings to defaults?")) return; const d = getDefaultSettings(); setDraft(d); onSave(d); }

  const inp = { width: "100%", padding: "10px 14px", border: "1.5px solid #ebebeb", borderRadius: 10, fontSize: 14, outline: "none", boxSizing: "border-box", background: "white" };
  const lbl = { display: "block", fontSize: 11, fontWeight: 700, marginBottom: 7, color: "#9ca3af", textTransform: "uppercase", letterSpacing: 0.6 };
  const sections = [{ id: "general", label: "General" }, { id: "deadlines", label: "Dates & Deadlines" }, { id: "events", label: "Board Week" }, { id: "groups", label: "Student Groups" }, { id: "presentations", label: "Presentations" }, { id: "topics", label: "Industry Topics" }];

  return (
    <div style={{ animation: "fadeUp 0.4s ease both" }}>
      <div style={{ marginBottom: 22 }}>
        <h1 style={{ fontSize: 24, fontWeight: 800, color: "#0f0f0f", marginBottom: 4, letterSpacing: -0.5 }}>Program Settings</h1>
        <p style={{ color: "#9ca3af", fontSize: 14 }}>Customize dates, group definitions, and program details for each year</p>
      </div>
      {saved && <div style={{ background: "#f0fdf4", border: "1px solid #86efac", color: "#166534", padding: "12px 16px", borderRadius: 10, marginBottom: 18, fontSize: 13 }}>✓ Settings saved</div>}
      <div style={{ display: "flex", gap: 20, alignItems: "flex-start" }}>
        <div style={{ width: 180, flexShrink: 0 }}>
          <div style={{ background: "white", borderRadius: 12, padding: "8px", boxShadow: "0 1px 3px rgba(0,0,0,0.05)" }}>
            {sections.map(s => (
              <div key={s.id} onClick={() => setActiveSection(s.id)}
                style={{ padding: "10px 14px", borderRadius: 8, cursor: "pointer", fontWeight: activeSection === s.id ? 700 : 500, fontSize: 13, color: activeSection === s.id ? MAROON : "#555", background: activeSection === s.id ? "#fff0f0" : "transparent", transition: "all 0.15s", marginBottom: 2 }}
                onMouseOver={e => { if (activeSection !== s.id) e.currentTarget.style.background = "#f8f8f8"; }}
                onMouseOut={e => { if (activeSection !== s.id) e.currentTarget.style.background = "transparent"; }}>
                {s.label}
              </div>
            ))}
          </div>
          <button onClick={handleReset} style={{ width: "100%", marginTop: 10, background: "none", border: "1px solid #fca5a5", color: "#dc2626", padding: "9px", borderRadius: 8, fontSize: 12, cursor: "pointer", fontWeight: 600 }}>Reset to Defaults</button>
        </div>
        <div style={{ flex: 1 }}>
          {activeSection === "general" && (
            <div style={card}>
              <div style={{ fontSize: 12, fontWeight: 700, color: "#9ca3af", textTransform: "uppercase", letterSpacing: 0.6, marginBottom: 18 }}>General</div>
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16 }}>
                <div><label style={lbl}>Program Year</label><input value={draft.programYear} onChange={e => updateDraft("programYear", e.target.value)} style={inp} /></div>
                <div><label style={lbl}>Submission Deadline Time</label><input value={draft.submissionDeadlineTime} onChange={e => updateDraft("submissionDeadlineTime", e.target.value)} placeholder="11:59 PM" style={inp} /></div>
              </div>
            </div>
          )}
          {activeSection === "deadlines" && (
            <div style={card}>
              <div style={{ fontSize: 12, fontWeight: 700, color: "#9ca3af", textTransform: "uppercase", letterSpacing: 0.6, marginBottom: 18 }}>Dates & Deadlines</div>
              <div style={{ marginBottom: 16 }}>
                <label style={lbl}>Student Submission Deadline</label>
                <input type="date" value={draft.submissionDeadline} onChange={e => updateDraft("submissionDeadline", e.target.value)} style={inp} />
              </div>
            </div>
          )}
          {activeSection === "events" && (
            <div>
              <div style={{ fontSize: 12, color: "#9ca3af", marginBottom: 16, lineHeight: 1.6 }}>
                Build the interview week from scratch: any number of days, each with its own label and events. Only <strong>Interviews</strong> events feed the scheduler (pick which tier they're for) — Draft/Showcase/Networking/meal events are informational and shown on RSVPs and schedules. An event that overlaps an Interviews event on the same day (like Lunch) is automatically carved out of that day's interview slots.
              </div>
              <div style={{ ...card, marginBottom: 16, display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                <div>
                  <div style={{ fontSize: 13, fontWeight: 800, color: "#0f0f0f" }}>Interview Slot Length</div>
                  <div style={{ fontSize: 12, color: "#9ca3af" }}>Minutes per interview slot when the scheduler runs</div>
                </div>
                <input type="number" min={5} step={5} value={draft.boardWeek.slotMinutes} onChange={e => updateSlotMinutes(e.target.value)} style={{ ...inp, width: 90 }} />
              </div>
              {(draft.boardWeek.days || []).map((day, dayIdx) => (
                <div key={day.id} style={{ ...card, marginBottom: 16 }}>
                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 14, gap: 10 }}>
                    <input value={day.label} onChange={e => updateDayLabel(dayIdx, e.target.value)} placeholder="e.g. Monday, August 18" style={{ ...inp, fontSize: 14, fontWeight: 700, flex: 1 }} />
                    <button onClick={() => addScheduleEvent(dayIdx)} style={{ background: "white", border: `1.5px solid ${MAROON}`, color: MAROON, padding: "8px 14px", borderRadius: 8, fontSize: 12, cursor: "pointer", fontWeight: 700, whiteSpace: "nowrap" }}>+ Add Event</button>
                    <button onClick={() => removeDay(dayIdx)} style={{ background: "none", border: "1px solid #fca5a5", color: "#dc2626", padding: "8px 12px", borderRadius: 8, fontSize: 12, cursor: "pointer", fontWeight: 700, whiteSpace: "nowrap" }}>Remove Day</button>
                  </div>
                  {/* column headers */}
                  <div style={{ display: "grid", gridTemplateColumns: "16px 20px 1.3fr 100px 90px 90px 100px 28px", gap: 6, marginBottom: 6, paddingBottom: 6, borderBottom: "1px solid #f0f0f0" }}>
                    <div />
                    <div />
                    <div style={{ fontSize: 10, fontWeight: 700, color: "#9ca3af", textTransform: "uppercase", letterSpacing: 0.5 }}>Event Name</div>
                    <div style={{ fontSize: 10, fontWeight: 700, color: "#9ca3af", textTransform: "uppercase", letterSpacing: 0.5 }}>Type</div>
                    <div style={{ fontSize: 10, fontWeight: 700, color: "#9ca3af", textTransform: "uppercase", letterSpacing: 0.5 }}>Tier</div>
                    <div style={{ fontSize: 10, fontWeight: 700, color: "#9ca3af", textTransform: "uppercase", letterSpacing: 0.5 }}>Start</div>
                    <div style={{ fontSize: 10, fontWeight: 700, color: "#9ca3af", textTransform: "uppercase", letterSpacing: 0.5 }}>End</div>
                    <div />
                  </div>
                  {(day.events || []).length === 0 && (
                    <div style={{ fontSize: 13, color: "#9ca3af", textAlign: "center", padding: "12px 0" }}>No events — click "+ Add Event" to start</div>
                  )}
                  {(day.events || []).map((evt, idx) => {
                    const typeInfo = EVENT_TYPES.find(t => t.value === evt.type) || EVENT_TYPES[EVENT_TYPES.length - 1];
                    const isDragging = dragEvent && dragEvent.dayIdx === dayIdx && dragEvent.idx === idx;
                    const isDragOver = dragEvent && dragEvent.dayIdx === dayIdx && dragOverIdx === idx && !isDragging;
                    return (
                      <div key={evt.id || idx}
                        draggable
                        onDragStart={e => { setDragEvent({ dayIdx, idx }); e.dataTransfer.effectAllowed = "move"; }}
                        onDragOver={e => { e.preventDefault(); if (dragEvent && dragEvent.dayIdx === dayIdx) setDragOverIdx(idx); }}
                        onDrop={e => {
                          e.preventDefault();
                          if (dragEvent && dragEvent.dayIdx === dayIdx) moveScheduleEvent(dayIdx, dragEvent.idx, idx);
                          setDragEvent(null); setDragOverIdx(null);
                        }}
                        onDragEnd={() => { setDragEvent(null); setDragOverIdx(null); }}
                        style={{ display: "grid", gridTemplateColumns: "16px 20px 1.3fr 100px 90px 90px 100px 28px", gap: 6, alignItems: "center", marginBottom: 6, opacity: isDragging ? 0.4 : evt.enabled ? 1 : 0.45, background: isDragOver ? "#fff0f0" : "transparent", borderRadius: 6, boxShadow: isDragOver ? "0 0 0 1.5px #fca5a5 inset" : "none", transition: "background 0.1s, box-shadow 0.1s" }}>
                        <div style={{ cursor: "grab", color: "#c8c8c8", fontSize: 14, textAlign: "center", userSelect: "none", lineHeight: 1 }} title="Drag to reorder">⠿</div>
                        <input type="checkbox" checked={!!evt.enabled} onChange={e => updateScheduleEvent(dayIdx, idx, "enabled", e.target.checked)} style={{ accentColor: MAROON, width: 15, height: 15, cursor: "pointer" }} />
                        <input value={evt.name} onChange={e => updateScheduleEvent(dayIdx, idx, "name", e.target.value)} placeholder="Event name" style={{ ...inp, fontSize: 13, padding: "7px 10px" }} />
                        <select value={evt.type || "custom"} onChange={e => updateScheduleEvent(dayIdx, idx, "type", e.target.value)} style={{ ...inp, fontSize: 12, padding: "7px 6px" }}>
                          {EVENT_TYPES.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
                        </select>
                        {typeInfo.needsTier ? (
                          <select value={evt.tier || "platinum"} onChange={e => updateScheduleEvent(dayIdx, idx, "tier", e.target.value)} style={{ ...inp, fontSize: 12, padding: "7px 6px" }}>
                            <option value="platinum">Platinum</option>
                            {evt.type === "interviews" ? (
                              <option value="goldsilver">Gold + Silver</option>
                            ) : (
                              <>
                                <option value="gold">Gold</option>
                                <option value="silver">Silver</option>
                              </>
                            )}
                          </select>
                        ) : <div />}
                        <input value={evt.startTime} onChange={e => updateScheduleEvent(dayIdx, idx, "startTime", e.target.value)} placeholder="e.g. 8:00 AM" style={{ ...inp, fontSize: 12, padding: "7px 8px" }} />
                        <input value={evt.endTime} onChange={e => updateScheduleEvent(dayIdx, idx, "endTime", e.target.value)} placeholder="e.g. 5:00 PM" style={{ ...inp, fontSize: 12, padding: "7px 8px" }} />
                        <button onClick={() => removeScheduleEvent(dayIdx, idx)} style={{ background: "none", border: "1px solid #fca5a5", color: "#dc2626", borderRadius: 6, width: 26, height: 26, cursor: "pointer", fontSize: 14, display: "flex", alignItems: "center", justifyContent: "center", padding: 0, flexShrink: 0 }}>×</button>
                      </div>
                    );
                  })}
                </div>
              ))}
              <button onClick={addDay} style={{ background: "white", border: `1.5px solid ${MAROON}`, color: MAROON, padding: "10px 20px", borderRadius: 8, fontSize: 13, cursor: "pointer", fontWeight: 700 }}>+ Add Day</button>
            </div>
          )}
          {activeSection === "groups" && (
            <div style={card}>
              <div style={{ fontSize: 12, fontWeight: 700, color: "#9ca3af", textTransform: "uppercase", letterSpacing: 0.6, marginBottom: 18 }}>Student Group Definitions</div>
              {draft.groups.map((group, i) => (
                <div key={group.value} style={{ background: "#fafafa", borderRadius: 12, padding: "18px 20px", marginBottom: 14, border: "1px solid #f0f0f0" }}>
                  <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 14 }}>
                    <div style={{ width: 30, height: 30, background: `linear-gradient(135deg, ${MAROON}, #a00000)`, borderRadius: 8, display: "flex", alignItems: "center", justifyContent: "center", color: "white", fontWeight: 800, fontSize: 13 }}>{group.value}</div>
                    <div style={{ fontWeight: 700, fontSize: 14 }}>Group {group.value}</div>
                  </div>
                  <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
                    <div><label style={lbl}>Display Label</label><input value={group.label} onChange={e => { const g = [...draft.groups]; g[i] = {...g[i], label: e.target.value}; updateDraft("groups", g); }} style={inp} /></div>
                    <div><label style={lbl}>Description</label><input value={group.description} onChange={e => { const g = [...draft.groups]; g[i] = {...g[i], description: e.target.value}; updateDraft("groups", g); }} style={inp} /></div>
                    <div><label style={lbl}>Presents?</label>
                      <select value={group.presents ? "yes" : "no"} onChange={e => { const g = [...draft.groups]; g[i] = {...g[i], presents: e.target.value === "yes"}; updateDraft("groups", g); }} style={inp}>
                        <option value="yes">Yes</option><option value="no">No</option>
                      </select>
                    </div>
                    {group.presents && (
                      <div><label style={lbl}>Slot Duration (minutes)</label><input type="number" value={group.slotMinutes} onChange={e => { const g = [...draft.groups]; g[i] = {...g[i], slotMinutes: parseInt(e.target.value)||20}; updateDraft("groups", g); }} style={inp} /></div>
                    )}
                    <div>
                      <label style={lbl}>Minimum Interviews</label>
                      <input type="number" min={0} value={group.minInterviews ?? 0} onChange={e => { const g = [...draft.groups]; g[i] = {...g[i], minInterviews: Math.max(0, parseInt(e.target.value) || 0)}; updateDraft("groups", g); }} style={inp} />
                      <div style={{ fontSize: 11, color: "#9ca3af", marginTop: 4 }}>Scheduler prioritizes students in this group until they reach this many interviews, before filling other random slots.</div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
          {activeSection === "presentations" && (
            <div style={card}>
              <div style={{ fontSize: 12, fontWeight: 700, color: "#9ca3af", textTransform: "uppercase", letterSpacing: 0.6, marginBottom: 18 }}>Presentation Timing</div>
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16 }}>
                <div><label style={lbl}>Start Time</label><TimeSelect value={draft.presentationStartTime} onChange={v => updateDraft("presentationStartTime", v)} /></div>
                <div><label style={lbl}>End Time</label><TimeSelect value={draft.presentationEndTime} onChange={v => updateDraft("presentationEndTime", v)} /></div>
                <div><label style={lbl}>Max Number of Rooms</label><input type="number" min={1} max={20} value={draft.maxPresentationRooms || 4} onChange={e => updateDraft("maxPresentationRooms", parseInt(e.target.value) || 1)} style={inp} /></div>
              </div>
            </div>
          )}
          {activeSection === "topics" && (
            <div style={card}>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 18 }}>
                <div style={{ fontSize: 12, fontWeight: 700, color: "#9ca3af", textTransform: "uppercase", letterSpacing: 0.6 }}>Industry Topics</div>
                <button onClick={() => updateDraft("presentationTopics", [...draft.presentationTopics, "New Topic"])} style={{ background: "white", border: `1.5px solid ${MAROON}`, color: MAROON, padding: "6px 14px", borderRadius: 8, fontSize: 12, cursor: "pointer", fontWeight: 700 }}>+ Add</button>
              </div>
              {draft.presentationTopics.map((topic, i) => (
                <div key={i} style={{ display: "flex", gap: 10, marginBottom: 10, alignItems: "center" }}>
                  <input value={topic} onChange={e => { const t = [...draft.presentationTopics]; t[i] = e.target.value; updateDraft("presentationTopics", t); }} style={{ ...inp, flex: 1 }} />
                  <button onClick={() => updateDraft("presentationTopics", draft.presentationTopics.filter((_,j) => j !== i))} style={{ background: "none", border: "1px solid #fca5a5", color: "#dc2626", padding: "9px 12px", borderRadius: 8, fontSize: 13, cursor: "pointer" }}>×</button>
                </div>
              ))}
            </div>
          )}
          {activeSection === "documents" && (
            <div style={card}>
              <div style={{ fontSize: 12, fontWeight: 700, color: "#9ca3af", textTransform: "uppercase", letterSpacing: 0.6, marginBottom: 6 }}>FERPA Agreement Templates</div>
              <div style={{ fontSize: 13, color: "#6b7280", lineHeight: 1.6, marginBottom: 20 }}>
                Upload your Word documents to Google Drive, OneDrive, or any file host and paste the public download links below. Students and board members will download, sign, and upload a signed copy back.
              </div>
              <div style={{ marginBottom: 16 }}>
                <label style={lbl}>Student FERPA Template URL</label>
                <input value={draft.studentFerpaURL||""} onChange={e => updateDraft("studentFerpaURL", e.target.value)} placeholder="https://drive.google.com/uc?export=download&id=..." style={inp} />
                <div style={{ fontSize: 11, color: "#9ca3af", marginTop: 5 }}>Students will see a "Download Agreement" button linking here, then upload their signed copy.</div>
              </div>
              <div>
                <label style={lbl}>Board Member FERPA Template URL</label>
                <input value={draft.boardFerpaURL||""} onChange={e => updateDraft("boardFerpaURL", e.target.value)} placeholder="https://drive.google.com/uc?export=download&id=..." style={inp} />
                <div style={{ fontSize: 11, color: "#9ca3af", marginTop: 5 }}>Board members will see a "Download Agreement" button linking here, then upload their signed copy.</div>
              </div>
            </div>
          )}
          <button onClick={handleSave} style={{ ...btn, marginTop: 8 }}>Save Settings →</button>
        </div>
      </div>
    </div>
  );
}

function PresentationsTab({ students, card, btn, MAROON, settings, db, presentationsPublished, presentationsPublishedBoard, onPublishStudents, onPublishBoard }) {
  const s = settings || getDefaultSettings();
  const grp17Config = s.groups.find(g => g.value === "17") || { slotMinutes: 30 };
  const grp18Config = s.groups.find(g => g.value === "18") || { slotMinutes: 20 };
  const grp17Presents = grp17Config.presents !== false;
  const grp18Presents = grp18Config.presents !== false;
  const maxRooms = s.maxPresentationRooms || 4;
  const [rooms, setRooms] = useState(s.presentationTopics.slice(0, Math.min(4, maxRooms)).map((topic, i) => ({ id: i+1, topic, name: `Room ${i+1}` })));
  const [schedule, setSchedule] = useState(null);
  const [viewMode, setViewMode] = useState("room");
  const [focused, setFocused] = useState(null);

  const group17 = grp17Presents ? students.filter(st => st.group === "17") : [];
  const group18 = grp18Presents ? students.filter(st => st.group === "18") : [];
  const allPresenters = [...group17, ...group18];
  const slots17 = generatePresentationSlots(s.presentationStartTime, s.presentationEndTime, grp17Config.slotMinutes);
  const slots18 = generatePresentationSlots(s.presentationStartTime, s.presentationEndTime, grp18Config.slotMinutes);
  // Split rooms proportionally: if both groups present, split in half; if only one, all rooms go to that group
  const split = (grp17Presents && grp18Presents) ? Math.ceil(rooms.length / 2) : (grp17Presents ? rooms.length : 0);
  const g17Rooms = rooms.slice(0, split);
  const g18Rooms = rooms.slice(split);
  const canFit = (!grp17Presents || (g17Rooms.length * slots17.length) >= group17.length) && (!grp18Presents || (Math.max(g18Rooms.length,1) * slots18.length) >= group18.length);

  const toDisplay = t => fmt12(t);

  function addRoom() { if (rooms.length >= maxRooms) return; const id = Math.max(...rooms.map(r=>r.id),0)+1; setRooms(r=>[...r,{id,topic:s.presentationTopics[0]||"Other",name:`Room ${id}`}]); }
  function removeRoom(id) { if(rooms.length<=1)return; setRooms(r=>r.filter(rm=>rm.id!==id)); }
  function updateRoom(id,field,val) { setRooms(r=>r.map(rm=>rm.id===id?{...rm,[field]:val}:rm)); }

  async function generateSchedule() {
    const result = {};
    rooms.forEach(r => { result[r.id] = []; });
    const sorted17 = [...group17].sort((a,b)=>(a.lastName||"").localeCompare(b.lastName||""));
    sorted17.forEach((student,i) => {
      const room = g17Rooms[i % g17Rooms.length];
      const slot = slots17[Math.floor(i / g17Rooms.length)];
      if(room && slot) result[room.id].push({student, slot:slot.time, grp:"17", duration:grp17Config.slotMinutes});
    });
    // Group 18: assign to the room whose topic matches the student's presentationTopic, fallback to rotation.
    const effectiveG18Rooms = g18Rooms.length > 0 ? g18Rooms : rooms;
    const sorted18 = [...group18].sort((a,b)=>(a.lastName||"").localeCompare(b.lastName||""));
    const g18Counters = {};
    effectiveG18Rooms.forEach(r => { g18Counters[r.id] = 0; });
    sorted18.forEach((student) => {
      const topicRoom = effectiveG18Rooms.find(r => r.topic === student.presentationTopic);
      const fallback = effectiveG18Rooms.reduce((min,r) => g18Counters[r.id] < g18Counters[min.id] ? r : min, effectiveG18Rooms[0]);
      const room = topicRoom || fallback;
      const idx = g18Counters[room.id];
      const slot = slots18[idx];
      if(room && slot) { result[room.id].push({student, slot:slot.time, grp:"18", duration:grp18Config.slotMinutes}); g18Counters[room.id]++; }
    });
    setSchedule(result);
    // Save to Firestore so it can be published.
    if (db) {
      const entries = [];
      rooms.forEach(room => { (result[room.id]||[]).forEach(({student,slot,grp,duration}) => { entries.push({studentId:student.id,firstName:student.firstName,lastName:student.lastName,group:grp,room:room.name,topic:room.topic,slot,duration}); }); });
      await setDoc(doc(db,"metadata","presentationSchedule"),{generatedAt:new Date().toISOString(),entries},{merge:false}).catch(()=>{});
    }
  }

  const inp2 = id => ({ width:"100%", padding:"9px 12px", border:`1.5px solid ${focused===id?MAROON:"#ebebeb"}`, borderRadius:8, fontSize:13, outline:"none", boxSizing:"border-box", background:"white", transition:"all 0.2s" });

  return (
    <div style={{ animation: "fadeUp 0.4s ease both" }}>
      <div style={{ marginBottom: 22 }}>
        <h1 style={{ fontSize: 24, fontWeight: 800, color: "#0f0f0f", marginBottom: 4, letterSpacing: -0.5 }}>Presentations</h1>
        <p style={{ color: "#9ca3af", fontSize: 14 }}>{toDisplay(s.presentationStartTime)} – {toDisplay(s.presentationEndTime)} · {[grp17Presents&&"Group 17",grp18Presents&&"Group 18"].filter(Boolean).join(" & ") || "No groups"} present</p>
      </div>

      <div style={{ display: "grid", gridTemplateColumns: `repeat(${2+(grp17Presents?1:0)+(grp18Presents?1:0)},1fr)`, gap: 14, marginBottom: 20 }}>
        {[
          grp17Presents && { label: `Group 17 (${grp17Config.slotMinutes}min slots)`, value: group17.length, sub: "Past internship", color: "#1d4ed8" },
          grp18Presents && { label: `Group 18 (${grp18Config.slotMinutes}min slots)`, value: group18.length, sub: "Commodities analysis or internship", color: "#7c3aed" },
          { label: "Total Presenters", value: allPresenters.length, sub: canFit ? "All fit in schedule" : "Add more rooms", color: canFit ? "#0f766e" : "#dc2626" },
          { label: "Rooms Configured", value: rooms.length, sub: `Max: ${maxRooms} · ${split} for G17 · ${rooms.length-split} for G18`, color: MAROON },
        ].filter(Boolean).map(stat => (
          <div key={stat.label} style={{ background: "white", borderRadius: 12, padding: "18px 20px", boxShadow: "0 1px 4px rgba(0,0,0,0.05)", borderTop: `3px solid ${stat.color}` }}>
            <div style={{ fontSize: 24, fontWeight: 900, color: stat.color, marginBottom: 4 }}>{stat.value}</div>
            <div style={{ fontSize: 12, fontWeight: 700, color: "#333", marginBottom: 2, lineHeight: 1.3 }}>{stat.label}</div>
            <div style={{ fontSize: 11, color: "#aaa" }}>{stat.sub}</div>
          </div>
        ))}
      </div>

      <div style={{ ...card, background: "#f9fafb", border: "1px solid #e8e8e8", padding: "16px 22px", marginBottom: 20 }}>
        <div style={{ fontSize: 11, fontWeight: 700, color: "#9ca3af", textTransform: "uppercase", letterSpacing: 0.6, marginBottom: 12 }}>Schedule Details</div>
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 16 }}>
          <div>
            <div style={{ fontSize: 12, fontWeight: 700, color: "#333", marginBottom: 4 }}>Timing</div>
            <div style={{ fontSize: 13, color: "#6b7280" }}>{toDisplay(s.presentationStartTime)} – {toDisplay(s.presentationEndTime)}</div>
          </div>
          {grp17Presents ? (
            <div>
              <div style={{ fontSize: 12, fontWeight: 700, color: "#333", marginBottom: 4 }}>Group 17 — Internship</div>
              <div style={{ fontSize: 13, color: "#6b7280" }}>{grp17Config.slotMinutes}-min slots · {slots17.length} available per room</div>
              <div style={{ fontSize: 12, color: "#aaa", marginTop: 2 }}>Assigned to first {split} room{split!==1?"s":""}</div>
            </div>
          ) : (
            <div>
              <div style={{ fontSize: 12, fontWeight: 700, color: "#d1d5db", marginBottom: 4 }}>Group 17 — Not Presenting</div>
              <div style={{ fontSize: 12, color: "#d1d5db" }}>Set "Presents?" to Yes in Settings to enable</div>
            </div>
          )}
          {grp18Presents ? (
            <div>
              <div style={{ fontSize: 12, fontWeight: 700, color: "#333", marginBottom: 4 }}>Group 18 — Commodities Analysis / Internship</div>
              <div style={{ fontSize: 13, color: "#6b7280" }}>{grp18Config.slotMinutes}-min slots · {slots18.length} available per room</div>
              <div style={{ fontSize: 12, color: "#aaa", marginTop: 2 }}>Assigned to remaining {rooms.length-split} room{rooms.length-split!==1?"s":""}</div>
            </div>
          ) : (
            <div>
              <div style={{ fontSize: 12, fontWeight: 700, color: "#d1d5db", marginBottom: 4 }}>Group 18 — Not Presenting</div>
              <div style={{ fontSize: 12, color: "#d1d5db" }}>Set "Presents?" to Yes in Settings to enable</div>
            </div>
          )}
        </div>
        <div style={{ marginTop: 10, fontSize: 12, color: "#9ca3af" }}>Adjust timing in <strong>Program Settings → Presentations</strong></div>
      </div>

      <div style={card}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 18 }}>
          <div style={{ fontSize: 11, fontWeight: 700, color: "#9ca3af", textTransform: "uppercase", letterSpacing: 0.6 }}>Presentation Rooms</div>
          <button onClick={addRoom} disabled={rooms.length >= maxRooms} style={{ background: "white", border: `1.5px solid ${rooms.length >= maxRooms ? "#e8e8e8" : MAROON}`, color: rooms.length >= maxRooms ? "#d1d5db" : MAROON, padding: "7px 16px", borderRadius: 8, fontSize: 12, cursor: rooms.length >= maxRooms ? "not-allowed" : "pointer", fontWeight: 700 }}>{rooms.length >= maxRooms ? `Max ${maxRooms} rooms` : "+ Add Room"}</button>
        </div>
        <div style={{ display: "grid", gridTemplateColumns: "repeat(2,1fr)", gap: 12, marginBottom: 20 }}>
          {rooms.map((room, i) => {
            const isG17 = i < split;
            return (
              <div key={room.id} style={{ background: "#fafafa", borderRadius: 10, padding: "14px 16px", border: `1px solid ${isG17?"#dbeafe":"#ede9fe"}` }}>
                <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 10 }}>
                  <div style={{ width: 28, height: 28, background: `linear-gradient(135deg,${MAROON},#a00000)`, borderRadius: 7, display: "flex", alignItems: "center", justifyContent: "center", color: "white", fontWeight: 800, fontSize: 12 }}>{room.id}</div>
                  <span style={{ fontSize: 11, fontWeight: 700, padding: "2px 8px", borderRadius: 6, background: isG17?"#dbeafe":"#ede9fe", color: isG17?"#1d4ed8":"#7c3aed" }}>{isG17 ? (grp17Presents ? "Group 17" : "Group 17 (inactive)") : (grp18Presents ? "Group 18" : "Group 18 (inactive)")}</span>
                  {rooms.length > 1 && <button onClick={()=>removeRoom(room.id)} style={{ marginLeft:"auto",background:"none",border:"1px solid #fca5a5",color:"#dc2626",width:26,height:26,borderRadius:6,fontSize:13,cursor:"pointer",display:"flex",alignItems:"center",justifyContent:"center" }}>×</button>}
                </div>
                <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8 }}>
                  <div>
                    <label style={{ fontSize:10,fontWeight:700,color:"#9ca3af",textTransform:"uppercase",letterSpacing:0.4,marginBottom:4,display:"block" }}>Room Name</label>
                    <input value={room.name} onChange={e=>updateRoom(room.id,"name",e.target.value)} style={inp2(`rn${room.id}`)} onFocus={()=>setFocused(`rn${room.id}`)} onBlur={()=>setFocused(null)} />
                  </div>
                  <div>
                    <label style={{ fontSize:10,fontWeight:700,color:"#9ca3af",textTransform:"uppercase",letterSpacing:0.4,marginBottom:4,display:"block" }}>Topic</label>
                    <select value={room.topic} onChange={e=>updateRoom(room.id,"topic",e.target.value)} style={inp2(`rt${room.id}`)} onFocus={()=>setFocused(`rt${room.id}`)} onBlur={()=>setFocused(null)}>
                      {s.presentationTopics.map(t=><option key={t}>{t}</option>)}
                    </select>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
        {!canFit && allPresenters.length > 0 && (
          <div style={{ background:"#fff1f0",border:"1px solid #fca5a5",borderRadius:8,padding:"10px 14px",marginBottom:14,fontSize:13,color:"#991b1b" }}>
            Not enough slots for all students. Add more rooms or adjust timing in Settings.
          </div>
        )}
        <button onClick={generateSchedule} style={btn}>Generate Presentation Schedule →</button>
      </div>

      {schedule && (
        <div style={card}>
          <div style={{ display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:14,flexWrap:"wrap",gap:10 }}>
            <div style={{ fontSize:11,fontWeight:700,color:"#9ca3af",textTransform:"uppercase",letterSpacing:0.6 }}>Schedule Output</div>
            <div style={{ display:"flex",gap:8,alignItems:"center",flexWrap:"wrap" }}>
              <div style={{ display:"flex",background:"#f5f5f5",borderRadius:8,overflow:"hidden",border:"1px solid #e8e8e8" }}>
                {[["room","By Room"],["student","By Student"]].map(([val,lbl2])=>(
                  <button key={val} onClick={()=>setViewMode(val)} style={{ padding:"7px 16px",border:"none",background:viewMode===val?MAROON:"transparent",color:viewMode===val?"white":"#555",fontWeight:600,fontSize:12,cursor:"pointer" }}>{lbl2}</button>
                ))}
              </div>
              <button onClick={()=>{
                const rows = rooms.flatMap(room=>(schedule[room.id]||[]).map(({student,slot,grp,duration})=>({student,slot,room,grp,duration}))).sort((a,b)=>a.slot.localeCompare(b.slot));
                const html = `<html><head><title>TRIP Presentation Schedule</title><style>body{font-family:Arial,sans-serif;margin:32px;color:#1a1a1a}h1{color:#6f0000;font-size:22px;margin-bottom:4px}p{color:#666;font-size:13px;margin-bottom:20px}table{width:100%;border-collapse:collapse;font-size:13px}th{background:#6f0000;color:white;padding:10px 12px;text-align:left;font-size:11px;text-transform:uppercase;letter-spacing:0.5px}td{padding:9px 12px;border-bottom:1px solid #f0f0f0}tr:nth-child(even){background:#fafafa}@media print{body{margin:16px}}</style></head><body><h1>TRIP Presentation Schedule</h1><p>Generated ${new Date().toLocaleString()}</p><table><thead><tr><th>Time</th><th>Student</th><th>Group</th><th>Room</th><th>Topic</th><th>Duration</th></tr></thead><tbody>${rows.map(({student,slot,room,grp,duration})=>`<tr><td><b>${slot}</b></td><td>${student.firstName} ${student.lastName}</td><td>Group ${grp}</td><td>${room.name}</td><td>${room.topic}</td><td>${duration} min</td></tr>`).join("")}</tbody></table></body></html>`;
                const w = window.open("","_blank"); w.document.write(html); w.document.close(); w.focus(); w.print();
              }} style={{ background:"white",border:`1.5px solid ${MAROON}`,color:MAROON,padding:"7px 16px",borderRadius:8,fontSize:12,fontWeight:700,cursor:"pointer" }}>Print / PDF</button>
            </div>
          </div>
          {/* Publish controls */}
          <div style={{ display:"flex",gap:10,alignItems:"center",flexWrap:"wrap",marginBottom:18,padding:"12px 16px",background:"#f9fafb",borderRadius:10,border:"1px solid #e8e8e8" }}>
            <span style={{ fontSize:12,fontWeight:700,color:"#555",marginRight:4 }}>Publish:</span>
            {onPublishStudents && (
              <button onClick={()=>onPublishStudents(!presentationsPublished)} style={{ padding:"6px 14px",borderRadius:8,background:presentationsPublished?"white":"linear-gradient(135deg,#6f0000,#a00000)",color:presentationsPublished?"#dc2626":"white",border:presentationsPublished?"1.5px solid #fca5a5":"none",fontWeight:700,fontSize:12,cursor:"pointer" }}>
                {presentationsPublished?"✓ Students — Unpublish":"Publish to Students →"}
              </button>
            )}
            {onPublishBoard && (
              <button onClick={()=>onPublishBoard(!presentationsPublishedBoard)} style={{ padding:"6px 14px",borderRadius:8,background:presentationsPublishedBoard?"white":"linear-gradient(135deg,#1d4ed8,#2563eb)",color:presentationsPublishedBoard?"#dc2626":"white",border:presentationsPublishedBoard?"1.5px solid #fca5a5":"none",fontWeight:700,fontSize:12,cursor:"pointer" }}>
                {presentationsPublishedBoard?"✓ Board — Unpublish":"Publish to Board →"}
              </button>
            )}
          </div>

          {viewMode === "room" && (
            <div style={{ display:"grid",gridTemplateColumns:"1fr 1fr",gap:16 }}>
              {rooms.map((room,i) => {
                const isG17 = i < split;
                return (
                  <div key={room.id} style={{ border:"1px solid #f0f0f0",borderRadius:12,overflow:"hidden" }}>
                    <div style={{ background:`linear-gradient(135deg,${MAROON},#a00000)`,padding:"12px 16px" }}>
                      <div style={{ color:"white",fontWeight:700,fontSize:14 }}>{room.name} — {room.topic}</div>
                      <div style={{ color:"rgba(255,255,255,0.6)",fontSize:11,marginTop:2 }}>
                        {isG17?`Group 17 · ${grp17Config.slotMinutes}min`:`Group 18 · ${grp18Config.slotMinutes}min`} · {(schedule[room.id]||[]).length} presentation{(schedule[room.id]||[]).length!==1?"s":""}
                      </div>
                    </div>
                    <table style={{ width:"100%",borderCollapse:"collapse",fontSize:13 }}>
                      <thead>
                        <tr style={{ background:"#f8f8f8",borderBottom:"1px solid #f0f0f0" }}>
                          {["Time","Student","Type"].map(h=>(
                            <th key={h} style={{ textAlign:"left",padding:"8px 12px",fontSize:11,fontWeight:700,color:"#9ca3af",textTransform:"uppercase",letterSpacing:0.4 }}>{h}</th>
                          ))}
                        </tr>
                      </thead>
                      <tbody>
                        {(schedule[room.id]||[]).sort((a,b)=>a.slot.localeCompare(b.slot)).map(({student,slot,grp,duration},idx)=>(
                          <tr key={student.id} style={{ borderBottom:"1px solid #f8f8f8",background:idx%2===0?"white":"#fafafa" }}>
                            <td style={{ padding:"10px 12px",fontWeight:700,color:MAROON,fontSize:12,whiteSpace:"nowrap" }}>{slot}</td>
                            <td style={{ padding:"10px 12px" }}>
                              <div style={{ fontWeight:600,color:"#1a1a1a" }}>{student.firstName} {student.lastName}</div>
                              <div style={{ fontSize:11,color:"#aaa",marginTop:1 }}>{duration} min</div>
                            </td>
                            <td style={{ padding:"10px 12px",fontSize:12,color:"#6b7280" }}>
                              {grp==="18"?(student.internshipType==="investment"?"Commodities Analysis":"Internship"):"Internship"}
                            </td>
                          </tr>
                        ))}
                        {(schedule[room.id]||[]).length===0&&<tr><td colSpan={3} style={{ padding:16,textAlign:"center",color:"#d1d5db",fontSize:13 }}>No presentations assigned</td></tr>}
                      </tbody>
                    </table>
                  </div>
                );
              })}
            </div>
          )}

          {viewMode === "student" && (
            <table style={{ width:"100%",borderCollapse:"collapse",fontSize:13 }}>
              <thead>
                <tr style={{ borderBottom:"2px solid #f0f0f0" }}>
                  {["Student","Group","Type","Room","Topic","Time","Duration"].map(h=>(
                    <th key={h} style={{ textAlign:"left",padding:"10px 14px",color:"#9ca3af",fontWeight:700,fontSize:11,textTransform:"uppercase",letterSpacing:0.4 }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {rooms.flatMap(room=>(schedule[room.id]||[]).map(({student,slot,grp,duration})=>({student,slot,room,grp,duration}))).sort((a,b)=>a.slot.localeCompare(b.slot)).map(({student,slot,room,grp,duration},i)=>(
                  <tr key={student.id} style={{ borderBottom:"1px solid #f8f8f8",background:i%2===0?"white":"#fafafa" }}>
                    <td style={{ padding:"12px 14px",fontWeight:700,color:"#1a1a1a" }}>{student.firstName} {student.lastName}</td>
                    <td style={{ padding:"12px 14px" }}>
                      <span style={{ background:grp==="17"?"#eff6ff":"#f5f3ff",color:grp==="17"?"#1d4ed8":"#7c3aed",border:`1px solid ${grp==="17"?"#93c5fd":"#c4b5fd"}`,padding:"3px 10px",borderRadius:20,fontSize:11,fontWeight:700 }}>Group {grp}</span>
                    </td>
                    <td style={{ padding:"12px 14px",color:"#555",fontSize:13 }}>{grp==="18"?(student.internshipType==="investment"?"Presentation":"Internship"):"Past Internship"}</td>
                    <td style={{ padding:"12px 14px",fontWeight:600,color:"#333" }}>{room.name}</td>
                    <td style={{ padding:"12px 14px",color:"#555" }}>{room.topic}</td>
                    <td style={{ padding:"12px 14px",fontWeight:700,color:MAROON }}>{slot}</td>
                    <td style={{ padding:"12px 14px",color:"#888",fontSize:12 }}>{duration} min</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      )}
    </div>
  );
}


function BulkImportCard({ MAROON, card, btn, auth, FUNCTIONS_BASE, onDone }) {
  const [importing, setImporting] = useState(false);
  const [preview, setPreview] = useState([]);
  const [errors, setErrors] = useState([]);
  const [results, setResults] = useState(null);
  const [dragOver, setDragOver] = useState(false);

  function parseExcel(file) {
    const reader = new FileReader();
    reader.onload = (e) => {
      try {
        const XLSX = window.XLSX;
        if (!XLSX) { setErrors(["SheetJS not loaded — please refresh the page"]); return; }

        const data = new Uint8Array(e.target.result);
        const workbook = XLSX.read(data, { type: "array" });
        const sheetName = workbook.SheetNames[0];
        const sheet = workbook.Sheets[sheetName];
        const jsonRows = XLSX.utils.sheet_to_json(sheet, { defval: "" });

        if (!jsonRows.length) { setErrors(["File appears empty"]); return; }

        // Normalize keys — strip BOM, whitespace, lowercase
        const normalize = k => k.replace(/^\uFEFF/, "").trim().toLowerCase();
        const normalizedRows = jsonRows.map(row => {
          const out = {};
          Object.entries(row).forEach(([k, v]) => { out[normalize(k)] = String(v).trim(); });
          return out;
        });

        // Flexible column matching
        const getKey = (row, names) => {
          for (const n of names) {
            const found = Object.keys(row).find(k => k === n || k.includes(n));
            if (found) return found;
          }
          return null;
        };

        const sample = normalizedRows[0];
        const emailKey = getKey(sample, ["email","e-mail","email address"]);
        const nameKey = getKey(sample, ["full name","fullname","name","student name"]);
        const firstKey = getKey(sample, ["first name","firstname","first"]);
        const lastKey = getKey(sample, ["last name","lastname","last","surname"]);
        const roleKey = getKey(sample, ["role","type","user type"]);
        const passKey = getKey(sample, ["password","pass","pwd"]);
        const groupKey = getKey(sample, ["group","group number","grp"]);
        const memberTypeKey = getKey(sample, ["member_type","membertype","member type","type"]);

        if (!emailKey) {
          setErrors([`No 'email' column found. Columns detected: [${Object.keys(sample).join(", ")}]`]);
          return;
        }

        const rows = [];
        const errs = [];

        normalizedRows.forEach((row, i) => {
          const email = row[emailKey]?.replace(/['"]/g,"").trim();
          const rawRole2 = roleKey ? row[roleKey]?.toLowerCase().trim() : "";
          const isBoardRow = rawRole2 === "board";
          if (!email) {
            errs.push(`Row ${i+2}: missing email/company name`);
            return;
          }
          // Allow company names for board members, require @ for others
          if (!isBoardRow && !email.includes("@")) {
            errs.push(`Row ${i+2}: invalid email "${email}" — students need a valid email address`);
            return;
          }

          let name = "";
          if (nameKey && row[nameKey]) name = row[nameKey];
          else if (firstKey && lastKey) name = `${row[firstKey]||""} ${row[lastKey]||""}`.trim();
          else if (firstKey) name = row[firstKey] || "";
          else if (lastKey) name = row[lastKey] || "";

          const rawRole = roleKey ? row[roleKey]?.toLowerCase().trim() : "";
          const validRole = ["student","board","admin"].includes(rawRole) ? rawRole : "student";
          const defaultPass = passKey && row[passKey] ? row[passKey] : (isBoardRow ? email.toLowerCase().replace(/[^a-z0-9]/g, "") : email.split("@")[0]) + "Trip2025!";
          const group = groupKey ? row[groupKey]?.trim() : "";

          const member_type = memberTypeKey ? row[memberTypeKey]?.toLowerCase().trim() : "";
          rows.push({ email, name, role: validRole, password: defaultPass, group, member_type });
        });

        if (rows.length === 0) {
          setErrors(["No valid rows found. Make sure your file has email addresses."]);
          return;
        }

        setErrors(errs);
        setPreview(rows);
        setResults(null);
      } catch(err) {
        setErrors(["Failed to parse file: " + err.message]);
      }
    };
    reader.readAsArrayBuffer(file);
  }

  function handleFile(file) {
    if (!file) return;
    setPreview([]); setErrors([]); setResults(null);
    // Accept CSV or Excel saved as CSV
    parseExcel(file);
  }

  async function runImport() {
    if (!preview.length) return;
    setImporting(true);
    const succeeded = [];
    const failed = [];

    for (const user of preview) {
      try {
        const token = await auth.currentUser.getIdToken();
        const res = await fetch(`${FUNCTIONS_BASE}/bulk_create_user`, {
          method: "POST",
          headers: { "Content-Type": "application/json", "Authorization": `Bearer ${token}` },
          body: JSON.stringify(user)
        });
        const data = await res.json();
        if (data.success) succeeded.push(user.email);
        else failed.push({ email: user.email, reason: data.error });
      } catch(err) {
        failed.push({ email: user.email, reason: err.message });
      }
    }

    setResults({ succeeded, failed });
    setImporting(false);
    setPreview([]);
    if (succeeded.length > 0) onDone();
  }

  return (
    <div style={{ ...card, marginBottom: 18 }}>
      <div style={{ fontSize: 11, fontWeight: 700, color: "#9ca3af", textTransform: "uppercase", letterSpacing: 0.6, marginBottom: 6 }}>Bulk Import from Excel / CSV</div>
      <p style={{ fontSize: 13, color: "#6b7280", marginBottom: 16, lineHeight: 1.6 }}>
        <strong>Students:</strong> required — <strong>email</strong>, <strong>group</strong> (16/17/18). Optional: <strong>name</strong>, <strong>password</strong>.<br/>
        <strong>Board Members:</strong> required — <strong>email</strong> (company name), <strong>role</strong> (board), <strong>member_type</strong> (platinum/gold/silver). Password auto-generated.
      </p>

      {/* Download template */}
      <button onClick={() => {
        const csv = "email,name,role,group,password,member_type\nexample@tamu.edu,Jane Smith,student,17,,\nExxon Mobil,,board,,,platinum\nShell Trading,,board,,,gold\nGunvor,,board,,,silver";
        const blob = new Blob([csv], { type: "text/csv" });
        const a = document.createElement("a"); a.href = URL.createObjectURL(blob); a.download = "trip_import_template.csv"; a.click();
      }} style={{ background:"white", border:"1.5px solid #e8e8e8", color:"#555", padding:"8px 16px", borderRadius:8, fontSize:12, cursor:"pointer", fontWeight:600, marginBottom:16, display:"inline-block" }}>
        Download Template CSV
      </button>

      {/* Drop zone */}
      <div
        onDragOver={e => { e.preventDefault(); setDragOver(true); }}
        onDragLeave={() => setDragOver(false)}
        onDrop={e => { e.preventDefault(); setDragOver(false); handleFile(e.dataTransfer.files[0]); }}
        style={{ border: `2px dashed ${dragOver ? MAROON : "#e0e0e0"}`, borderRadius: 12, padding: "28px", textAlign: "center", background: dragOver ? "#fff8f8" : "#fafafa", transition: "all 0.2s", marginBottom: 16, cursor: "pointer" }}
        onClick={() => document.getElementById("bulk-upload-input").click()}>
        <div style={{ fontSize: 28, marginBottom: 8 }}>📊</div>
        <div style={{ fontWeight: 600, fontSize: 14, color: "#555", marginBottom: 4 }}>Drop CSV/Excel file here or click to browse</div>
        <div style={{ fontSize: 12, color: "#aaa" }}>CSV or Excel exported as CSV</div>
        <input id="bulk-upload-input" type="file" accept=".csv,.xlsx,.xls,.xlsm" onChange={e => handleFile(e.target.files[0])} style={{ display: "none" }} />
      </div>

      {/* Errors */}
      {errors.length > 0 && (
        <div style={{ background:"#fff1f0", border:"1px solid #fca5a5", borderRadius:8, padding:"12px 16px", marginBottom:14 }}>
          <div style={{ fontSize:12,fontWeight:700,color:"#991b1b",marginBottom:6 }}>Parse warnings ({errors.length})</div>
          {errors.slice(0,5).map((e,i) => <div key={i} style={{ fontSize:12,color:"#991b1b",marginBottom:2 }}>• {e}</div>)}
          {errors.length > 5 && <div style={{ fontSize:12,color:"#991b1b" }}>...and {errors.length-5} more</div>}
        </div>
      )}

      {/* Preview table */}
      {preview.length > 0 && (
        <div style={{ marginBottom:16 }}>
          <div style={{ fontSize:13,fontWeight:700,color:"#333",marginBottom:10 }}>{preview.length} accounts ready to create</div>
          <div style={{ maxHeight:240,overflowY:"auto",border:"1px solid #f0f0f0",borderRadius:10 }}>
            <table style={{ width:"100%",borderCollapse:"collapse",fontSize:13 }}>
              <thead>
                <tr style={{ background:"#f8f8f8",borderBottom:"1px solid #f0f0f0" }}>
                  {["Name","Email","Role","Group","Default Password"].map(h => (
                    <th key={h} style={{ textAlign:"left",padding:"8px 12px",fontSize:11,fontWeight:700,color:"#9ca3af",textTransform:"uppercase",letterSpacing:0.4 }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {preview.map((u,i) => (
                  <tr key={i} style={{ borderBottom:"1px solid #f8f8f8" }}>
                    <td style={{ padding:"9px 12px",fontWeight:600,color:"#1a1a1a" }}>{u.name||"—"}</td>
                    <td style={{ padding:"9px 12px",color:"#555" }}>{u.email}</td>
                    <td style={{ padding:"9px 12px" }}>
                      <span style={{ background:u.role==="admin"?"#fff1f0":u.role==="board"?"#eff6ff":"#f0fdf4",color:u.role==="admin"?"#991b1b":u.role==="board"?"#1d4ed8":"#166534",padding:"2px 8px",borderRadius:20,fontSize:11,fontWeight:700 }}>{u.role}</span>
                    </td>
                    <td style={{ padding:"9px 12px",color:"#555" }}>{u.group||"—"}</td>
                    <td style={{ padding:"9px 12px",color:"#888",fontSize:12,fontFamily:"monospace" }}>{u.password}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <button onClick={runImport} disabled={importing}
            style={{ ...btn, marginTop:14 }}>
            {importing ? <><span style={{ display:"inline-block",width:14,height:14,border:"2px solid rgba(255,255,255,0.3)",borderTopColor:"white",borderRadius:"50%",animation:"spin 0.7s linear infinite" }} />Creating {preview.length} accounts...</> : `Create ${preview.length} Accounts →`}
          </button>
        </div>
      )}

      {/* Results */}
      {results && (
        <div>
          {results.succeeded.length > 0 && (
            <div style={{ background:"#f0fdf4",border:"1px solid #86efac",borderRadius:8,padding:"12px 16px",marginBottom:10 }}>
              <div style={{ fontSize:13,fontWeight:700,color:"#166534",marginBottom:6 }}>✓ {results.succeeded.length} accounts created successfully</div>
              <div style={{ fontSize:12,color:"#166534",opacity:0.8 }}>Users can now log in with their default password and change it on first use.</div>
            </div>
          )}
          {results.failed.length > 0 && (
            <div style={{ background:"#fff1f0",border:"1px solid #fca5a5",borderRadius:8,padding:"12px 16px" }}>
              <div style={{ fontSize:13,fontWeight:700,color:"#991b1b",marginBottom:6 }}>✗ {results.failed.length} failed</div>
              {results.failed.map((f,i) => <div key={i} style={{ fontSize:12,color:"#991b1b",marginBottom:2 }}>• {f.email}: {f.reason}</div>)}
            </div>
          )}
        </div>
      )}
    </div>
  );
}




export default function AdminPortal() {
  const [activeTab, setActiveTab] = useState("dashboard");
  const [students, setStudents] = useState([]);
  const [users, setUsers] = useState([]);
  const [boardMembers, setBoardMembers] = useState([]);
  const [newUser, setNewUser] = useState({ email: "", password: "", role: "student", name: "", member_type: "" });
  const [creating, setCreating] = useState(false);
  const [msg, setMsg] = useState("");
  const [loading, setLoading] = useState(true);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const { toasts, showToast, dismiss } = useToast();
  useEffect(() => {
    if (!msg) return;
    showToast(msg, msg.startsWith("Error") ? "error" : msg.toLowerCase().includes("clearing") || msg.toLowerCase().includes("generating") || msg.toLowerCase().includes("loading") ? "info" : "success");
  }, [msg, showToast]);
  const [generating, setGenerating] = useState(false);
  const [scheduling, setScheduling] = useState(false);
  const [resumeBooks, setResumeBooks] = useState([]);
  const [lastSchedule, setLastSchedule] = useState(null);
  const [scheduleData, setScheduleData] = useState(null);
  const [scheduleHistory, setScheduleHistory] = useState([]);
  const [resumeBooksPublished, setResumeBooksPublished] = useState(false);
  const [schedulePublished, setSchedulePublished] = useState(false);
  const [schedulePublishedStudents, setSchedulePublishedStudents] = useState(false);
  const [schedulePublishedBoard, setSchedulePublishedBoard] = useState(false);
  const [presentationsPublished, setPresentationsPublished] = useState(false);
  const [presentationsPublishedBoard, setPresentationsPublishedBoard] = useState(false);
  const [blackoutPreviewStudent, setBlackoutPreviewStudent] = useState(null);
  const [roundType, setRoundType] = useState("platinum");
  const [selectedBoard, setSelectedBoard] = useState(null);
  const [groupFilter, setGroupFilter] = useState("all");
  const [viewingStudent, setViewingStudent] = useState(null);
  const [submissionsLocked, setSubmissionsLocked] = useState(false);
  const [announcements, setAnnouncements] = useState([]);
  const [platinumPrefsLocked, setPlatinumPrefsLocked] = useState(false);
  const [goldPrefsLocked, setGoldPrefsLocked] = useState(false);
  const [silverPrefsLocked, setSilverPrefsLocked] = useState(false);
  const [websiteLocked, setWebsiteLocked] = useState(false);
  const [newAnnouncement, setNewAnnouncement] = useState({ title: "", body: "", target: "students", active: true, expiresAt: "" });
  const [editingAnn, setEditingAnn] = useState(null);
  const [expiryPreset, setExpiryPreset] = useState("never");
  const [statusFilter, setStatusFilter] = useState("all");
  const [studentSearch, setStudentSearch] = useState("");
  const [focused, setFocused] = useState(null);
  const [messages, setMessages] = useState([]);
  const [selectedMsg, setSelectedMsg] = useState(null);
  const [boardSearch, setBoardSearch] = useState("");
  const [boardTypeFilter, setBoardTypeFilter] = useState("all");
  const [sortField, setSortField] = useState("name");
  const [sortDir, setSortDir] = useState("asc");
  const [selectedStudents, setSelectedStudents] = useState(new Set());
  const [adminNote, setAdminNote] = useState("");
  const [draftPicks, setDraftPicks] = useState([]);
  const [draftPublished, setDraftPublished] = useState(false);
  const [draftNextRoundDate, setDraftNextRoundDate] = useState("");
  const [draftLiveRound, setDraftLiveRound] = useState("platinum");
  const [draftLiveCompany, setDraftLiveCompany] = useState("");
  const [draftLiveStudent, setDraftLiveStudent] = useState("");
  const [draftLiveSeason, setDraftLiveSeason] = useState("spring");
  const [programSettings, setProgramSettings] = useState(() => {
    try { return JSON.parse(localStorage.getItem("tripProgramSettings") || "null") || getDefaultSettings(); }
    catch { return getDefaultSettings(); }
  });
  const navigate = useNavigate();

  async function saveSettings(newSettings) {
    setProgramSettings(newSettings);
    localStorage.setItem("tripProgramSettings", JSON.stringify(newSettings));
    try {
      await setDoc(doc(db, "metadata", "programSettings"), { ...newSettings, updatedAt: new Date().toISOString() });
      setMsg("Settings saved successfully");
    } catch (e) { setMsg("Settings saved locally (Firestore sync failed)"); }
  }

  // Run once on mount only — loadAll is redefined every render, so adding it as a dep would re-trigger the load on every state update
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { loadAll(); }, []);

  async function loadAll() {
    setLoading(true);
    try {
      await Promise.all([loadStudents(), loadUsers(), loadBoardMembers(), loadMetadata(), loadMessages(), loadAnnouncements(), loadDraftResults()]);
    } catch (e) {
      setMsg("Failed to load data — check your connection and refresh.");
    }
    setLoading(false);
  }
  function downloadCSV() {
    const headers = ["First Name","Last Name","Email","Group","Class Year","Shirt Size","Status","Resume","Photo","Updated"];
    const rows = filteredStudents.map(s => [
      s.firstName||"", s.lastName||"", s.email||"", s.group||"", s.classYear||"", s.shirtSize||"",
      getStudentStatus(s), s.resumeURL?"Yes":"No", s.photoURL?"Yes":"No",
      s.updatedAt ? new Date(s.updatedAt).toLocaleDateString() : ""
    ]);
    const csv = [headers, ...rows].map(r => r.map(c => `"${String(c).replace(/"/g,'""')}"`).join(",")).join("\n");
    const a = Object.assign(document.createElement("a"), { href: URL.createObjectURL(new Blob([csv], { type:"text/csv" })), download: `trip-students-${new Date().toISOString().slice(0,10)}.csv` });
    a.click(); URL.revokeObjectURL(a.href);
  }


  async function loadDraftResults() {
    try {
      const snap = await getDoc(doc(db, "metadata", "draftResults"));
      if (snap.exists()) {
        const d = snap.data();
        setDraftPicks(d.picks || []);
        setDraftPublished(d.published || false);
        setDraftNextRoundDate(d.nextRoundDate || "");
      }
    } catch(e) {}
  }

  async function logActivity(action, details = "") {
    try {
      const user = auth.currentUser;
      await addDoc(collection(db, "activityLog"), { action, details, admin: user?.email || "admin", timestamp: new Date().toISOString() });
    } catch(e) {}
  }

  function handleSort(field) {
    if (sortField === field) setSortDir(d => d === "asc" ? "desc" : "asc");
    else { setSortField(field); setSortDir("asc"); }
  }

  async function loadAnnouncements() {
    const snap = await getDocs(collection(db, "announcements"));
    setAnnouncements(snap.docs.map(d => ({ id: d.id, ...d.data() })).sort((a,b) => new Date(b.createdAt) - new Date(a.createdAt)));
  }
  async function loadMessages() {
    const snap = await getDocs(collection(db, "contactMessages"));
    const msgs = snap.docs.map(d => ({ id: d.id, ...d.data() })).sort((a,b) => new Date(b.sentAt) - new Date(a.sentAt));
    setMessages(msgs);
  }
  async function loadStudents() { const snap = await getDocs(collection(db, "students")); setStudents(snap.docs.map(d => ({ id: d.id, ...d.data() }))); }
  async function loadUsers() { const snap = await getDocs(collection(db, "users")); setUsers(snap.docs.map(d => ({ id: d.id, ...d.data() }))); }
  async function loadBoardMembers() {
    const usersSnap = await getDocs(collection(db, "users"));
    const boardUsers = usersSnap.docs.filter(d => d.data().role === "board").map(d => ({ id: d.id, ...d.data() }));
    const prefsSnap = await getDocs(collection(db, "boardPreferences"));
    const prefsMap = {};
    prefsSnap.docs.forEach(d => { prefsMap[d.id] = d.data(); });
    const merged = boardUsers.map(u => ({ ...u, ...(prefsMap[u.id] || {}), id: u.id, companyName: prefsMap[u.id]?.profile?.companyName || u.displayEmail || u.name || u.email }));
    setBoardMembers(merged);
  }
  async function loadMetadata() {
    try {
      const rbSnap = await getDoc(doc(db, "metadata", "resumeBooks")); if (rbSnap.exists()) setResumeBooks(rbSnap.data().books || []);
      const schedSnap = await getDoc(doc(db, "metadata", "lastSchedule")); if (schedSnap.exists()) setLastSchedule(schedSnap.data());
      const schedDataSnap = await getDoc(doc(db, "metadata", "scheduleData")); if (schedDataSnap.exists()) setScheduleData(schedDataSnap.data());
      const histSnap = await getDoc(doc(db, "metadata", "scheduleHistory")); if (histSnap.exists()) setScheduleHistory(histSnap.data().runs || []);
      const settingsSnap = await getDoc(doc(db, "metadata", "programSettings"));
      if (settingsSnap.exists()) {
        const s = settingsSnap.data();
        delete s.updatedAt;
        setProgramSettings(s);
        localStorage.setItem("tripProgramSettings", JSON.stringify(s));
      }
      const pubSnap = await getDoc(doc(db, "metadata", "publishStatus"));
      if (pubSnap.exists()) {
        setResumeBooksPublished(pubSnap.data().resumeBooks || false);
        setSchedulePublished(pubSnap.data().schedule || false);
        setSchedulePublishedStudents(pubSnap.data().scheduleStudents || false);
        setSchedulePublishedBoard(pubSnap.data().scheduleBoard || false);
        setSubmissionsLocked(pubSnap.data().submissionsLocked || false);
        setPlatinumPrefsLocked(pubSnap.data().platinumPrefsLocked || false);
        setGoldPrefsLocked(pubSnap.data().goldPrefsLocked || false);
        setSilverPrefsLocked(pubSnap.data().silverPrefsLocked || false);
        setWebsiteLocked(pubSnap.data().websiteLocked || false);
        setPresentationsPublished(pubSnap.data().presentations || false);
        setPresentationsPublishedBoard(pubSnap.data().presentationsBoard || false);
      }
    } catch (e) {}
  }

  async function createUser(e) {
    e.preventDefault(); setCreating(true); setMsg("");
    try {
      // Duplicate guard
      const dupEmail = newUser.role === "board"
        ? users.some(u => (u.displayEmail || "").toLowerCase() === newUser.email.toLowerCase())
        : users.some(u => u.email?.toLowerCase() === newUser.email.toLowerCase());
      if (dupEmail) {
        setMsg(`Error: An account with this ${newUser.role === "board" ? "company name" : "email"} already exists`);
        setCreating(false);
        return;
      }
      // Route through Cloud Function so admin stays signed in (client-side createUserWithEmailAndPassword
      // auto-signs-in the new user and would kick the admin out of their session)
      const token = await auth.currentUser.getIdToken();
      const res = await fetch(`${FUNCTIONS_BASE}/bulk_create_user`, {
        method: "POST",
        headers: { "Content-Type": "application/json", "Authorization": `Bearer ${token}` },
        body: JSON.stringify({
          email: newUser.email,
          name: newUser.name,
          role: newUser.role,
          password: newUser.password,
          group: newUser.group || "",
          member_type: newUser.member_type || "",
        })
      });
      const data = await res.json();
      if (data.success) {
        const label = newUser.role === "board" ? `Board account for "${newUser.email}"` : `Account created for ${newUser.email}`;
        setMsg(label + ` (${newUser.role})`);
        setNewUser({ email: "", password: "", role: "student", name: "", member_type: "" });
        loadUsers();
      } else {
        setMsg("Error: " + data.error);
      }
    } catch (err) { setMsg("Error: " + err.message); }
    setCreating(false);
  }

  async function handleGenerateBooks(group = null) {
    setGenerating(true); setMsg("");
    try {
      const token = await auth.currentUser.getIdToken();
      const res = await fetch(`${FUNCTIONS_BASE}/generate_resume_books`, { method: "POST", headers: { "Content-Type": "application/json", "Authorization": `Bearer ${token}` }, body: JSON.stringify(group ? { group } : {}) });
      const data = await res.json();
      if (data.success) { setMsg(`Generated ${data.books.length} resume book${data.books.length !== 1 ? "s" : ""}`); setResumeBooks(data.books); logActivity("Generated Resume Books", group ? `Group ${group}` : "All groups"); }
      else setMsg("Error: " + data.error);
    } catch (err) { setMsg("Error: " + err.message); }
    setGenerating(false);
  }

  async function handleRunScheduler() {
    const notReady = boardMembers.filter(b => !b.submitted);
    if (notReady.length > 0) {
      const names = notReady.map(b => b.profile?.companyName || b.name || "Unknown").join(", ");
      const proceed = window.confirm(`⚠ ${notReady.length} compan${notReady.length === 1 ? "y hasn't" : "ies haven't"} submitted preferences:\n\n${names}\n\nThey will receive random student assignments. Proceed with ${roundType} scheduler?`);
      if (!proceed) return;
    } else {
      if (!window.confirm(`Run ${roundType} scheduler? This will overwrite previous results.`)) return;
    }
    setScheduling(true); setMsg("");
    try {
      const token = await auth.currentUser.getIdToken();
      const res = await fetch(`${FUNCTIONS_BASE}/run_scheduler`, { method: "POST", headers: { "Content-Type": "application/json", "Authorization": `Bearer ${token}` }, body: JSON.stringify({ round: roundType }) });
      const data = await res.json();
      if (data.success) {
        setMsg("Schedule generated successfully"); setLastSchedule(data); logActivity("Ran Scheduler", `Round: ${roundType}`);
        const newRun = { generatedAt: new Date().toISOString(), round: roundType, url: data.url, students: students.length, companies: boardMembers.filter(b=>b.submitted).length };
        const updatedHistory = [newRun, ...scheduleHistory].slice(0, 10);
        setScheduleHistory(updatedHistory);
        await setDoc(doc(db, "metadata", "scheduleHistory"), { runs: updatedHistory }, { merge: true });
      } else setMsg("Error: " + data.error);
    } catch (err) { setMsg("Error: " + err.message); }
    setScheduling(false);
  }

  async function deleteUser(userId, userEmail) {
    if (!window.confirm(`Permanently remove ${userEmail || "this user"}? This deletes their account and all data and cannot be undone.`)) return;
    try {
      const token = await auth.currentUser.getIdToken();
      const res = await fetch(`${FUNCTIONS_BASE}/delete_user`, {
        method: "POST",
        headers: { "Content-Type": "application/json", "Authorization": `Bearer ${token}` },
        body: JSON.stringify({ uid: userId })
      });
      const data = await res.json();
      if (data.success) { setMsg(`User ${userEmail} permanently removed`); loadAll(); }
      else setMsg("Error: " + data.error);
    } catch(err) { setMsg("Error: " + err.message); }
  }

  async function clearAllData(type) {
    const confirmMsg = type === "students"
      ? "DELETE all student submissions? This cannot be undone."
      : type === "board"
      ? "DELETE all board member preferences and RSVPs? This cannot be undone."
      : "DELETE ALL program data? This is irreversible.";
    if (!window.confirm(confirmMsg)) return;
    if (type === "all") {
      const doubleConfirm = window.prompt('Type "NEW YEAR" to confirm complete data reset:');
      if (doubleConfirm !== "NEW YEAR") { setMsg("Reset cancelled."); return; }
    }
    setMsg("Clearing data...");
    try {
      const token = await auth.currentUser.getIdToken();
      if (type === "students") {
        // Only clear submission fields, keep name/group/email so students tab still shows them.
        // Batched (500 writes/batch max) so a dropped connection mid-clear can't leave the
        // collection half-cleared the way hundreds of sequential awaited setDoc calls could.
        const snap = await getDocs(collection(db, "students"));
        const docs = snap.docs;
        for (let i = 0; i < docs.length; i += 500) {
          const batch = writeBatch(db);
          for (const d of docs.slice(i, i + 500)) {
            const data = d.data();
            const kept = {
              firstName: data.firstName || "",
              lastName: data.lastName || "",
              email: data.email || "",
              group: data.group || "",
              createdAt: data.createdAt || "",
            };
            batch.set(doc(db, "students", d.id), { ...kept, clearedAt: new Date().toISOString() });
          }
          await batch.commit();
        }
        setMsg("Student submissions cleared. Names and groups preserved.");
        loadAll();
      } else if (type === "board") {
        // Preserve companyName, memberType, interviewRooms - only clear submissions/preferences/rsvp.
        // Batched for the same atomicity/reliability reason as the student clear above.
        const snap = await getDocs(collection(db, "boardPreferences"));
        const docs = snap.docs;
        for (let i = 0; i < docs.length; i += 500) {
          const batch = writeBatch(db);
          for (const d of docs.slice(i, i + 500)) {
            const data = d.data();
            const profile = data.profile || {};
            const kept = {
              profile: {
                companyName: profile.companyName || "",
                memberType: profile.memberType || "",
                interviewRooms: profile.interviewRooms || "1",
              },
              submitted: false,
              clearedAt: new Date().toISOString(),
            };
            batch.set(doc(db, "boardPreferences", d.id), kept);
          }
          await batch.commit();
        }
        setMsg("Board data cleared. Company names, member types and room counts preserved.");
        loadAll();
      } else {
        const res = await fetch(`${FUNCTIONS_BASE}/clear_program_data`, {
          method: "POST",
          headers: { "Content-Type": "application/json", "Authorization": `Bearer ${token}` },
          body: JSON.stringify({ type })
        });
        const data = await res.json();
        if (data.success) {
          if (type === "all") { setResumeBooks([]); setLastSchedule(null); setScheduleHistory([]); }
          setMsg(`Data cleared successfully. Ready for new program year.`);
          loadAll();
        } else setMsg("Error: " + data.error);
      }
    } catch(err) { setMsg("Error: " + err.message); }
  }
  async function updateBlockedStudents(company, next) {
    await setDoc(doc(db, "boardPreferences", company.id), { blockedStudents: next }, { merge: true });
    setSelectedBoard(prev => prev && prev.id === company.id ? { ...prev, blockedStudents: next } : prev);
    setBoardMembers(prev => prev.map(b => b.id === company.id ? { ...b, blockedStudents: next } : b));
  }

  async function updateMaxPreferred(company, value) {
    // Empty/0 clears the override and falls back to the rooms x 20 formula
    // the Board Portal uses by default.
    const n = parseInt(value, 10);
    const maxPreferred = Number.isFinite(n) && n > 0 ? n : null;
    const nextProfile = { ...(company.profile || {}), maxPreferred };
    await setDoc(doc(db, "boardPreferences", company.id), { profile: nextProfile }, { merge: true });
    setSelectedBoard(prev => prev && prev.id === company.id ? { ...prev, profile: nextProfile } : prev);
    setBoardMembers(prev => prev.map(b => b.id === company.id ? { ...b, profile: nextProfile } : b));
  }

  async function updateRoomNumbers(company, value) {
    // Comma-separated pinned room number(s), e.g. "6" or "2, 3". Must match
    // the company's room count or the scheduler falls back to auto-numbering.
    // Empty clears the override.
    const roomNumbers = value.trim();
    const nextProfile = { ...(company.profile || {}), roomNumbers: roomNumbers || null };
    await setDoc(doc(db, "boardPreferences", company.id), { profile: nextProfile }, { merge: true });
    setSelectedBoard(prev => prev && prev.id === company.id ? { ...prev, profile: nextProfile } : prev);
    setBoardMembers(prev => prev.map(b => b.id === company.id ? { ...b, profile: nextProfile } : b));
  }

  async function toggleSubmissionLock(userId, type, currentlyOpen) {
    const willLock = currentlyOpen;
    if (!window.confirm(willLock ? "Re-lock this submission?" : "Reopen this submission?")) return;
    if (type === "student") await setDoc(doc(db, "students", userId), { submissionLocked: willLock }, { merge: true });
    else await setDoc(doc(db, "boardPreferences", userId), { submitted: willLock }, { merge: true });
    setMsg(willLock ? "Submission re-locked." : "Submission reopened."); loadAll();
  }

  async function handleBlackoutRequest(studentId, studentName, action) {
    const status = action === "approve" ? "approved" : "denied";
    await setDoc(doc(db, "students", studentId), {
      blackoutRequestStatus: status,
      blackoutReviewedAt: new Date().toISOString()
    }, { merge: true });
    setMsg(`Blackout request ${status} for ${studentName}`);
    loadStudents();
  }

  async function handleBoardBlackoutRequest(companyId, companyName, action) {
    const status = action === "approve" ? "approved" : "denied";
    await setDoc(doc(db, "boardPreferences", companyId), {
      blockoutRequestStatus: status,
      blockoutReviewedAt: new Date().toISOString()
    }, { merge: true });
    setMsg(`Block out request ${status} for ${companyName}`);
    loadAll();
  }

  async function handleSpringException(studentId, studentName, action) {
    const status = action === "approve" ? "approved" : "denied";
    await setDoc(doc(db, "students", studentId), {
      springExceptionStatus: status,
      springExceptionReviewedAt: new Date().toISOString()
    }, { merge: true });
    setMsg(`Spring exception ${status} for ${studentName}`);
    loadStudents();
  }

  async function notifyTierOfDraftedPicks(tier) {
    const tierMembers = boardMembers.filter(b => b.profile?.memberType?.toLowerCase().includes(tier) && b.submitted);
    // A student is only truly "no longer available" once they've hit their
    // required pick count (Group 17 needs 2 internships; everyone else 1) —
    // not after any single pick.
    const pickCounts = {};
    draftPicks.forEach(p => { pickCounts[p.studentName] = (pickCounts[p.studentName]||0) + 1; });
    const requiredPicksFor = name => {
      const s = students.find(st => (st.firstName+" "+st.lastName) === name);
      return s && s.group === "17" ? 2 : 1;
    };
    for (const tm of tierMembers) {
      const affectedPrefs = (tm.preferences||[]).filter(p => (pickCounts[p.name]||0) >= requiredPicksFor(p.name));
      if (affectedPrefs.length > 0) {
        await addDoc(collection(db, "announcements"), {
          title: "Your Candidate Preferences Have Been Updated",
          body: affectedPrefs.length + " student(s) from your preference list were drafted in an earlier round and are no longer available: " + affectedPrefs.map(p=>p.name).join(", ") + ". Please review your updated list.",
          target: "board", recipientUid: tm.id, active: true, createdAt: new Date().toISOString(),
        });
      }
    }
  }

  async function setTierLock(tier, next) {
    const setters = { platinum: setPlatinumPrefsLocked, gold: setGoldPrefsLocked, silver: setSilverPrefsLocked };
    const flag = `${tier}PrefsLocked`;
    const label = tier.charAt(0).toUpperCase() + tier.slice(1);
    if (next) {
      const tierAll = boardMembers.filter(b => b.profile?.memberType?.toLowerCase().includes(tier));
      const tierSub = tierAll.filter(b => b.submitted);
      const tierPend = tierAll.filter(b => !b.submitted);
      const pendNames = tierPend.map(b => b.profile?.companyName || b.name || "Unknown").join(", ");
      const msg2 = `Lock ${label} preferences?\n\n✓ Submitted (${tierSub.length}): ${tierSub.map(b=>b.profile?.companyName||b.name||"?").join(", ")||"none"}\n⏳ Pending (${tierPend.length}): ${pendNames||"none"}\n\nPending companies will have their current selections locked as-is.`;
      if (!window.confirm(msg2)) return;
    }
    await setDoc(doc(db, "metadata", "publishStatus"), { [flag]: next }, { merge: true });
    if (next && tier !== "platinum") {
      await notifyTierOfDraftedPicks(tier);
      setMsg(`${label} preferences locked. Announcements sent to affected companies.`);
    } else {
      setMsg(next ? `${label} preferences locked.` : `${label} preferences unlocked.`);
    }
    setters[tier](next); loadAll();
  }

  async function setAllTierLocks(next) {
    if (next && !window.confirm("Lock ALL preference tiers (Platinum, Gold, Silver)? Pending companies in each tier will have their current selections locked as-is.")) return;
    await setDoc(doc(db, "metadata", "publishStatus"), { platinumPrefsLocked: next, goldPrefsLocked: next, silverPrefsLocked: next }, { merge: true });
    if (next) { await notifyTierOfDraftedPicks("gold"); await notifyTierOfDraftedPicks("silver"); }
    setPlatinumPrefsLocked(next); setGoldPrefsLocked(next); setSilverPrefsLocked(next);
    setMsg(next ? "All preference tiers locked." : "All preference tiers unlocked.");
    loadAll();
  }

  // Mirrors StudentPortal's isSectionDone/coreSections so "Done %" and the
  // Complete badge here match what the student actually sees on their side.
  function isStudentSectionDone(s, id) {
    switch (id) {
      case "profile":
        return !!(s.firstName && s.lastName && s.undergradDegree && s.classYear && s.hometown && s.shirtSize);
      case "ferpa":
        return !!(s.ferpaConsentStatus === "signed");
      case "academic":
        if (s.group === "16") return true;
        return !!(s.continueTrip);
      case "internships": {
        const internshipType = s.internshipType || "g16_all";
        if (s.group === "16") return !!(s.springInternshipCompany);
        if (s.group === "17") {
          const hasLink = !!(s.presentationLink);
          if (internshipType === "summer") return !!(s.summerInternshipCompany) && hasLink;
          if (internshipType === "both") return !!(s.springInternshipCompany && s.summerInternshipCompany) && hasLink;
          return !!(s.springInternshipCompany) && hasLink;
        }
        if (s.group === "18") return !!s.presentationTopic && (internshipType === "investment" ? !!s.investmentDecision : !!s.summerInternshipCompany);
        if (internshipType === "summer") return !!(s.summerInternshipCompany);
        if (internshipType === "both") return !!(s.springInternshipCompany && s.summerInternshipCompany);
        return !!(s.springInternshipCompany);
      }
      case "statement":
        return !!(s.personalStatement && s.personalStatement.length >= 50);
      case "interests":
        // Raw Firestore docs omit slider fields entirely until first save,
        // unlike the student form which always initializes them to 5.
        return ALL_ROLES.some(([, key]) => (s[key] ?? 5) !== 5);
      case "documents":
        return !!(s.resumeURL);
      default:
        return false;
    }
  }
  function getStudentCompletion(s) {
    const coreSections = s.group === "16"
      ? ["profile", "ferpa", "internships", "statement", "interests", "documents"]
      : ["profile", "ferpa", "academic", "internships", "statement", "interests", "documents"];
    return Math.round(coreSections.filter(id => isStudentSectionDone(s, id)).length / coreSections.length * 100);
  }
  function getStudentStatus(s) {
    if (!s.resumeURL && !s.photoURL) return "missing_both";
    if (!s.resumeURL) return "missing_resume";
    if (!s.photoURL) return "missing_photo";
    return getStudentCompletion(s) === 100 ? "complete" : "in_progress";
  }

  const submittedBoard = boardMembers.filter(b => b.submitted).length;
  const shirtSizeCount = students.filter(s => s.shirtSize).length;
  const studentsComplete = students.filter(s => getStudentStatus(s) === "complete").length;
  const studentsUnlocked = students.filter(s => s.submissionLocked === false).length;
  const studentsWithResume = students.filter(s => s.resumeURL).length;
  const studentsWithPhoto = students.filter(s => s.photoURL).length;
  const totalRooms = boardMembers.reduce((sum,b) => sum + (parseInt(b.profile?.interviewRooms)||0), 0);
  const estimatedSlots = totalRooms * 14;
  const roomsByTier = boardMembers.reduce((acc,b) => {
    const mt = (b.profile?.memberType || "").toLowerCase();
    const rooms = parseInt(b.profile?.interviewRooms) || 0;
    const tier = mt.includes("platinum") ? "platinum" : mt.includes("gold") ? "gold" : mt.includes("silver") ? "silver" : null;
    if (tier) acc[tier] += rooms;
    return acc;
  }, { platinum:0, gold:0, silver:0 });
  // RSVP checkboxes are keyed by each event's dynamic id (see BoardPortal's
  // rsvpCount), not by fixed date-based field names — those were retired
  // when Board Week became admin-configurable, so counts must be derived
  // from the current programSettings.boardWeek.days shape, not hardcoded keys.
  const rsvpDayGroups = (programSettings?.boardWeek?.days || []).map(day => ({
    dayLabel: day.label,
    events: (day.events || []).filter(e => e.enabled).map(e => ({
      id: e.id,
      label: e.name || "Event",
      isInterview: e.type === "interviews",
      count: boardMembers.filter(b => !!b.profile?.[e.id]).length,
    })),
  })).filter(g => g.events.length > 0);
  const maxRsvp = Math.max(1, ...rsvpDayGroups.flatMap(g => g.events.map(e => e.count)));
  const rsvpColSplit = Math.ceil(rsvpDayGroups.length / 2);
  const rsvpCol1 = rsvpDayGroups.slice(0, rsvpColSplit);
  const rsvpCol2 = rsvpDayGroups.slice(rsvpColSplit);
  const demandMap = {};
  boardMembers.forEach(b => (b.preferences||[]).forEach(p => { demandMap[p.name] = (demandMap[p.name]||0)+1; }));
  const topCandidates = Object.entries(demandMap).sort((a,b)=>b[1]-a[1]).slice(0,8);
  const maxDemand = topCandidates[0]?.[1] || 1;
  const readinessChecks = [students.length>0, studentsWithResume/Math.max(students.length,1)>0.9, submittedBoard>0, submittedBoard/Math.max(boardMembers.length,1)>0.8, totalRooms>0];
  const readinessPct = Math.round((readinessChecks.filter(Boolean).length/readinessChecks.length)*100);
  const mkSpark = (val, n = 7) => { const v = Math.max(0, parseInt(String(val).replace(/\D/g,""))||0); if (!v) return Array(n).fill(0); const base = Math.max(0, v - Math.ceil(v * 0.28)); return Array.from({length:n},(_,i)=>{ const t = base + ((v-base)*i)/(n-1); const noise = (v*0.04)*(((i*7+v*3)%5)-2)/5; return Math.max(0,Math.round(t+noise)); }); };
  const pendingBlackouts = students.filter(s => s.blackoutRequestStatus === "pending");
  const alerts = [
    ...pendingBlackouts.map(s=>({type:"warning",msg:`${s.firstName||"Student"} ${s.lastName||""} — blackout request pending approval`,tab:"students"})),
    ...students.filter(s=>!s.resumeURL).map(s=>({type:"warning",msg:`${s.firstName||"Student"} ${s.lastName||""} — missing resume`,tab:"students"})),
    ...students.filter(s=>!s.photoURL).map(s=>({type:"warning",msg:`${s.firstName||"Student"} ${s.lastName||""} — missing photo`,tab:"students"})),
    ...boardMembers.filter(b=>!b.submitted).map(b=>({type:"warning",msg:`${b.profile?.companyName||"Board member"} — preferences not submitted`,tab:"board"})),
  ].slice(0,8);
  const filteredStudents = students.filter(s => {
    if (groupFilter !== "all" && s.group !== groupFilter) return false;
    if (statusFilter === "complete" && getStudentStatus(s) !== "complete") return false;
    if (statusFilter === "missing_both" && getStudentStatus(s) !== "missing_both") return false;
    if (statusFilter === "missing_resume" && s.resumeURL) return false;
    if (statusFilter === "missing_photo" && s.photoURL) return false;
    if (studentSearch && !`${s.firstName} ${s.lastName} ${s.email}`.toLowerCase().includes(studentSearch.toLowerCase())) return false;
    return true;
  }).sort((a, b) => {
    let av, bv;
    if (sortField === "name") { av = `${a.firstName} ${a.lastName}`.toLowerCase(); bv = `${b.firstName} ${b.lastName}`.toLowerCase(); }
    else if (sortField === "group") { av = a.group || ""; bv = b.group || ""; }
    else if (sortField === "status") { av = getStudentStatus(a); bv = getStudentStatus(b); }
    else if (sortField === "completion") { av = getStudentCompletion(a); bv = getStudentCompletion(b); }
    else if (sortField === "updated") { av = a.updatedAt || ""; bv = b.updatedAt || ""; }
    else { av = ""; bv = ""; }
    if (av < bv) return sortDir === "asc" ? -1 : 1;
    if (av > bv) return sortDir === "asc" ? 1 : -1;
    return 0;
  });
  const filteredBoard = boardMembers.filter(b => {
    const name = (b.profile?.companyName || b.displayEmail || b.name || "").toLowerCase();
    const contact = `${b.profile?.mainContactFirst || ""} ${b.profile?.mainContactLast || ""}`.toLowerCase();
    if (boardSearch && !name.includes(boardSearch.toLowerCase()) && !contact.includes(boardSearch.toLowerCase())) return false;
    if (boardTypeFilter !== "all") {
      const mType = (b.profile?.memberType || "").toLowerCase();
      if (!mType.includes(boardTypeFilter)) return false;
    }
    return true;
  }).sort((a, b) => (a.profile?.companyName || a.displayEmail || a.name || "").localeCompare(b.profile?.companyName || b.displayEmail || b.name || ""));

  const navItems = [
    { id: "dashboard", label: "Dashboard", icon: "⬡" },
    { id: "students", label: "Students", icon: "◎", badge: students.filter(s=>getStudentStatus(s)!=="complete").length },
    { id: "board", label: "Board Members", icon: "◈", badge: boardMembers.filter(b=>!b.submitted).length },
    { id: "users", label: "User Management", icon: "◉" },
    { id: "resumebooks", label: "Resume Books", icon: "▤" },
    { id: "scheduling", label: "Scheduling", icon: "▦" },
    { id: "presentations", label: "Presentations", icon: "◇" },
    { id: "settings", label: "Program Settings", icon: "◌" },
    { id: "messages", label: "Messages", icon: "✉", badge: messages.filter(m=>!m.read).length },
    { id: "announcements", label: "Announcements", icon: "📢" },
    { id: "draft", label: "Draft", icon: "◈" },
  ];

  const card = { background: "white", borderRadius: 14, padding: "24px 28px", marginBottom: 18, boxShadow: "0 1px 3px rgba(0,0,0,0.05), 0 4px 12px rgba(0,0,0,0.04)" };
  const inp = id => ({ width: "100%", padding: "10px 14px", border: `1.5px solid ${focused===id?MAROON:"#ebebeb"}`, borderRadius: 10, fontSize: 14, outline: "none", boxSizing: "border-box", background: "white", transition: "all 0.2s", boxShadow: focused===id?"0 0 0 3px rgba(111,0,0,0.07)":"none" });
  const lbl = { display: "block", fontSize: 11, fontWeight: 700, marginBottom: 7, color: "#9ca3af", textTransform: "uppercase", letterSpacing: 0.6 };
  const btn = { background: `linear-gradient(135deg,${MAROON},#a00000)`, color: "white", border: "none", padding: "11px 24px", borderRadius: 10, fontWeight: 700, fontSize: 14, cursor: "pointer", boxShadow: "0 4px 12px rgba(111,0,0,0.2)", transition: "all 0.2s", display: "inline-flex", alignItems: "center", gap: 8 };

  return (
    <div style={{ display: "flex", minHeight: "100vh", background: "#F3F4F6", fontFamily: "Inter, -apple-system, sans-serif" }}>
      <style>{`
        @keyframes fadeUp { from{opacity:0;transform:translateY(14px)} to{opacity:1;transform:translateY(0)} }
        @keyframes spin { to{transform:rotate(360deg)} }
        @keyframes slideIn { from{opacity:0;transform:translateX(-6px)} to{opacity:1;transform:translateX(0)} }
        @keyframes shimmer { from{background-position:200% 0} to{background-position:-200% 0} }
        .skeleton { background:linear-gradient(90deg,#f0f2f5 25%,#e4e7ed 50%,#f0f2f5 75%); background-size:200% 100%; animation:shimmer 1.5s ease-in-out infinite; }
        .lift { transition: transform 0.18s, box-shadow 0.18s; }
        .lift:hover { transform: translateY(-2px) !important; box-shadow: 0 10px 32px rgba(0,0,0,0.13) !important; }
        .btn-press { transition: transform 0.12s, opacity 0.12s; }
        .btn-press:active { transform: scale(0.97); opacity: 0.88; }
        .row-hover:hover { background: #f5f7ff !important; }
        .row-hover { transition: background 0.12s; cursor: pointer; }
        input:focus, textarea:focus, select:focus { outline:none; border-color:#6f0000 !important; box-shadow:0 0 0 3px rgba(111,0,0,0.1) !important; }
        .mobile-toggle { display:none !important; }
        .sidebar-overlay { display:none; }
        @media (max-width:768px) {
          .admin-sidebar { transform:translateX(-220px); transition:transform 0.28s cubic-bezier(0.4,0,0.2,1) !important; }
          .admin-sidebar.open { transform:translateX(0) !important; }
          .admin-main { margin-left:0 !important; padding:24px 20px !important; }
          .mobile-toggle { display:flex !important; }
          .sidebar-overlay.open { display:block !important; }
        }
      `}</style>

      <div className={`admin-sidebar${sidebarOpen?" open":""}`} style={{ width: 220, background: `linear-gradient(180deg,${DARK} 0%,#2A0A0A 100%)`, display: "flex", flexDirection: "column", position: "fixed", top: 0, left: 0, height: "100vh", zIndex: 100 }}>
        <div style={{ padding: "26px 18px 22px", borderBottom: "1px solid rgba(255,255,255,0.06)" }}>
          <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
            <div style={{ width: 32, height: 32, background: `linear-gradient(135deg,${MAROON},#a00000)`, borderRadius: 8, display: "flex", alignItems: "center", justifyContent: "center", fontWeight: 900, color: "white", fontSize: 14, boxShadow: "0 4px 10px rgba(111,0,0,0.4)" }}>T</div>
            <div><div style={{ color: "white", fontWeight: 800, fontSize: 14 }}>TRIP</div><div style={{ color: "rgba(255,255,255,0.3)", fontSize: 10, letterSpacing: 1, textTransform: "uppercase" }}>Admin</div></div>
          </div>
        </div>
        <div style={{ padding: "14px 12px", flex: 1, overflowY: "auto" }}>
          <div style={{ fontSize: 10, color: "rgba(255,255,255,0.25)", letterSpacing: 1.5, textTransform: "uppercase", padding: "0 8px", marginBottom: 8 }}>Navigation</div>
          {navItems.map(item => (
            <div key={item.id} onClick={() => setActiveTab(item.id)}
              style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "10px 12px", borderRadius: 8, marginBottom: 2, cursor: "pointer", background: activeTab===item.id?"rgba(255,255,255,0.1)":"transparent", borderLeft: activeTab===item.id?`2px solid ${MAROON}`:"2px solid transparent", transition: "all 0.15s" }}
              onMouseOver={e => { if (activeTab!==item.id) e.currentTarget.style.background="rgba(255,255,255,0.05)"; }}
              onMouseOut={e => { if (activeTab!==item.id) e.currentTarget.style.background="transparent"; }}>
              <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                <span style={{ fontSize: 13, color: activeTab===item.id?"white":"rgba(255,255,255,0.4)" }}>{item.icon}</span>
                <span style={{ fontSize: 13, fontWeight: activeTab===item.id?600:400, color: activeTab===item.id?"white":"rgba(255,255,255,0.45)" }}>{item.label}</span>
              </div>
              {item.badge > 0 && <span style={{ background: "#dc2626", color: "white", borderRadius: 10, padding: "1px 7px", fontSize: 10, fontWeight: 800 }}>{item.badge}</span>}
            </div>
          ))}
        </div>
        <div style={{ padding: "14px 12px", borderTop: "1px solid rgba(255,255,255,0.06)" }}>
          <div style={{ background: "rgba(255,255,255,0.04)", borderRadius: 8, padding: "10px 12px", marginBottom: 8 }}>
            <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 5 }}>
              <span style={{ color: "rgba(255,255,255,0.35)", fontSize: 11 }}>Readiness</span>
              <span style={{ color: readinessPct>=80?"#86efac":"#fde68a", fontSize: 11, fontWeight: 700 }}>{readinessPct}%</span>
            </div>
            <div style={{ background: "rgba(255,255,255,0.1)", borderRadius: 3, height: 3 }}>
              <div style={{ background: readinessPct>=80?"#22c55e":`linear-gradient(90deg,${MAROON},#ff6b6b)`, height: 3, borderRadius: 3, width: `${readinessPct}%` }} />
            </div>
          </div>
          <div onClick={() => { signOut(auth); navigate("/august"); }} style={{ display:"flex",alignItems:"center",gap:8,padding:"8px 12px",borderRadius:8,cursor:"pointer",color:"rgba(255,255,255,0.3)",fontSize:13,transition:"all 0.15s" }}
            onMouseOver={e=>{e.currentTarget.style.color="rgba(255,255,255,0.7)";e.currentTarget.style.background="rgba(255,255,255,0.05)";}}
            onMouseOut={e=>{e.currentTarget.style.color="rgba(255,255,255,0.3)";e.currentTarget.style.background="transparent";}}>
            → Sign Out
          </div>
        </div>
      </div>

      <ToastContainer toasts={toasts} onDismiss={dismiss} />
      <div className={`sidebar-overlay${sidebarOpen?" open":""}`} onClick={() => setSidebarOpen(false)} style={{ position:"fixed",inset:0,background:"rgba(0,0,0,0.5)",zIndex:99,backdropFilter:"blur(2px)" }} />
      <button className="mobile-toggle" onClick={() => setSidebarOpen(o => !o)} style={{ position:"fixed",top:14,left:14,zIndex:200,background:MAROON,border:"none",borderRadius:8,width:38,height:38,cursor:"pointer",color:"white",fontSize:18,alignItems:"center",justifyContent:"center" }}>☰</button>
      <div style={{ marginLeft: 220, flex: 1, padding: "30px 40px" }} className="admin-main">

        {activeTab === "dashboard" && (
          <div style={{ animation: "fadeUp 0.4s ease both" }}>
            <div style={{ display:"flex",justifyContent:"space-between",alignItems:"flex-end",marginBottom:24 }}>
              <div><h1 style={{ fontSize:24,fontWeight:800,color:"#0f0f0f",marginBottom:4,letterSpacing:-0.5 }}>Command Center</h1><p style={{ color:"#9ca3af",fontSize:14 }}>TRIP program operations — interview week readiness</p></div>
              <button onClick={loadAll} style={{ background:"white",border:"1.5px solid #e8e8e8",color:"#555",padding:"9px 18px",borderRadius:8,fontSize:13,cursor:"pointer",fontWeight:600 }}>↻ Refresh</button>
            </div>
            {loading && (
              <div>
                <Skeleton h={110} r={14} mb={22} />
                <div style={{ display:"grid",gridTemplateColumns:"repeat(4,1fr)",gap:14,marginBottom:22 }}>
                  {[1,2,3,4].map(i=><Skeleton key={i} h={110} r={14} />)}
                </div>
                <Skeleton h={200} r={14} mb={18} />
              </div>
            )}
            {!loading && <>
            <div style={{ background:readinessPct>=80?"linear-gradient(135deg,#14532d,#166534)":readinessPct>=60?"linear-gradient(135deg,#78350f,#92400e)":`linear-gradient(135deg,#450a0a,${MAROON})`, borderRadius:14, padding:"22px 28px", marginBottom:22, color:"white", display:"flex", justifyContent:"space-between", alignItems:"center" }}>
              <div>
                <div style={{ fontSize:11,opacity:0.6,letterSpacing:2,textTransform:"uppercase",marginBottom:6 }}>Interview Week Readiness</div>
                <div style={{ fontSize:44,fontWeight:900,letterSpacing:-2,lineHeight:1,marginBottom:8 }}>{readinessPct}<span style={{ fontSize:20,opacity:0.6 }}>%</span></div>
                <div style={{ background:"rgba(255,255,255,0.15)",borderRadius:4,height:6,width:220 }}>
                  <div style={{ background:readinessPct>=80?"#86efac":"white",height:6,borderRadius:4,width:`${readinessPct}%`,transition:"width 0.6s" }} />
                </div>
              </div>
              <div style={{ display:"flex",flexDirection:"column",gap:8,alignItems:"flex-end" }}>
                {readinessChecks.map((ok,i)=>(
                  <div key={i} style={{ display:"flex",alignItems:"center",gap:8,fontSize:12,opacity:0.8 }}>
                    <span style={{ color:ok?"#86efac":"#fca5a5" }}>{ok?"✓":"✗"}</span>
                    <span>{["Students loaded","Resume coverage >90%","Board members connected","Board submissions >80%","Rooms configured"][i]}</span>
                  </div>
                ))}
              </div>
            </div>
            <div style={{ display:"grid",gridTemplateColumns:"repeat(4,1fr)",gap:14,marginBottom:22 }}>
              <StatCard label="Total Students" value={students.length} sub={studentsUnlocked>0 ? `${studentsUnlocked} unlocked` : `${studentsComplete} profiles complete`} color={MAROON} icon="◎" onClick={()=>setActiveTab("students")} sparkData={mkSpark(students.length)} />
              <StatCard label="Board Submitted" value={`${submittedBoard}/${boardMembers.length}`} sub={submittedBoard===boardMembers.length&&boardMembers.length>0?"All done":`${boardMembers.length-submittedBoard} pending`} color="#1d4ed8" icon="◈" onClick={()=>setActiveTab("board")} sparkData={mkSpark(submittedBoard)} />
              <StatCard label="Interview Rooms" value={totalRooms} sub={`~${estimatedSlots} slots`} color="#0f766e" icon="▦" onClick={()=>setActiveTab("scheduling")} sparkData={mkSpark(totalRooms)} />
              <StatCard label="Resume Books" value={resumeBooks.length} sub={resumeBooks.length>0?"Ready":"Not generated"} color="#7c3aed" icon="▤" onClick={()=>setActiveTab("resumebooks")} sparkData={mkSpark(resumeBooks.length)} />
            </div>

            <div className="lift" style={{ ...card, marginBottom:18 }}>
              <div style={{ display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:14 }}>
                <div style={{ fontSize:13,fontWeight:700,color:"#0f0f0f" }}>Shirt Size Distribution</div>
                <div style={{ fontSize:12,color:"#9ca3af" }}>{shirtSizeCount} of {students.length} submitted</div>
              </div>
              <div style={{ display:"flex",flexDirection:"column",gap:8 }}>
                {["XS","S","M","L","XL","XXL"].map(size => {
                  const cnt = students.filter(s => s.shirtSize === size).length;
                  const pct = shirtSizeCount > 0 ? Math.round((cnt/shirtSizeCount)*100) : 0;
                  return (
                    <div key={size} style={{ display:"flex",alignItems:"center",gap:10 }}>
                      <div style={{ width:28,fontSize:12,fontWeight:700,color:"#555",textAlign:"right",flexShrink:0 }}>{size}</div>
                      <div style={{ flex:1,height:22,background:"#f0f0f0",borderRadius:6,overflow:"hidden" }}>
                        <div style={{ width:pct+"%",height:"100%",background:"linear-gradient(90deg,#6f0000,#a00000)",borderRadius:6,minWidth:cnt>0?4:0 }} />
                      </div>
                      <div style={{ width:44,fontSize:12,fontWeight:700,color:"#1a1a1a",flexShrink:0 }}>{cnt} <span style={{ fontSize:10,color:"#aaa" }}>({pct}%)</span></div>
                    </div>
                  );
                })}
              </div>
            </div>
            <div style={{ display:"grid",gridTemplateColumns:"1fr 1fr 1fr",gap:18,marginBottom:18 }}>
              <div style={{ ...card, gridColumn:"1 / 3" }}>
                <div style={{ fontSize:11,fontWeight:700,color:"#9ca3af",textTransform:"uppercase",letterSpacing:0.8,marginBottom:18 }}>Event Attendance (from RSVPs)</div>

                {rsvpDayGroups.length === 0 ? (
                  <div style={{ fontSize:13,color:"#d1d5db",textAlign:"center",padding:"20px 0" }}>No RSVP-able events configured — add events under Program Settings → Board Week.</div>
                ) : (
                  <div style={{ display:"grid",gridTemplateColumns:"1fr 1fr",gap:16 }}>
                    {[rsvpCol1, rsvpCol2].map((col, colIdx) => col.length > 0 && (
                      <div key={colIdx}>
                        {col.map(group => (
                          <div key={group.dayLabel} style={{ marginBottom:14 }}>
                            <div style={{ fontSize:12,fontWeight:700,color:"#555",marginBottom:12,paddingBottom:6,borderBottom:"1px solid #f0f0f0" }}>{group.dayLabel}</div>
                            {group.events.map(e=><BarChart key={e.id} label={e.label} value={e.count} max={maxRsvp} color={e.count>0?(e.isInterview?"#1d4ed8":MAROON):"#e0e0e0"} />)}
                          </div>
                        ))}
                      </div>
                    ))}
                    {boardMembers.filter(b=>b.submitted).length===0&&<div style={{ gridColumn:"1 / 3",fontSize:13,color:"#d1d5db",textAlign:"center",padding:"20px 0" }}>No RSVP data yet</div>}
                  </div>
                )}
              </div>
              <div style={card}>
                <div style={{ fontSize:11,fontWeight:700,color:"#9ca3af",textTransform:"uppercase",letterSpacing:0.8,marginBottom:14 }}>Alerts & Actions</div>
                {alerts.length===0?<AlertItem type="success" message="All checks passed" />:alerts.map((a,i)=><AlertItem key={i} type={a.type} message={a.msg} action="View" onAction={()=>setActiveTab(a.tab)} />)}
              </div>
            </div>
            <div style={{ display:"grid",gridTemplateColumns:"1fr 1fr",gap:18 }}>
              <div style={card}>
                <div style={{ fontSize:11,fontWeight:700,color:"#9ca3af",textTransform:"uppercase",letterSpacing:0.8,marginBottom:18 }}>Most Requested Candidates</div>
                {topCandidates.length===0?<div style={{ fontSize:13,color:"#d1d5db",textAlign:"center",padding:"16px 0" }}>No preference data yet</div>:topCandidates.map(([name,count],i)=>(
                  <div key={name} style={{ display:"flex",alignItems:"center",gap:10,marginBottom:10 }}>
                    <div style={{ width:22,height:22,borderRadius:"50%",background:i<3?`linear-gradient(135deg,${MAROON},#a00000)`:"#f0f0f0",color:i<3?"white":"#888",display:"flex",alignItems:"center",justifyContent:"center",fontSize:10,fontWeight:800,flexShrink:0 }}>{i+1}</div>
                    <div style={{ flex:1 }}>
                      <div style={{ fontSize:13,fontWeight:600,color:"#333",marginBottom:3 }}>{name}</div>
                      <div style={{ background:"#f0f0f0",borderRadius:3,height:5 }}><div style={{ background:i<3?MAROON:"#9ca3af",height:5,borderRadius:3,width:`${(count/maxDemand)*100}%` }} /></div>
                    </div>
                    <span style={{ fontSize:12,fontWeight:700,color:"#555",minWidth:60,textAlign:"right" }}>{count} req{count!==1?"s":""}</span>
                  </div>
                ))}
              </div>
              <div>
                <div style={{ ...card,marginBottom:14 }}>
                  <div style={{ fontSize:11,fontWeight:700,color:"#9ca3af",textTransform:"uppercase",letterSpacing:0.8,marginBottom:14 }}>Program Completion</div>
                  {[{label:"Student profiles",value:studentsComplete,total:students.length,color:MAROON},{label:"Resumes uploaded",value:studentsWithResume,total:students.length,color:"#1d4ed8"},{label:"Photos uploaded",value:studentsWithPhoto,total:students.length,color:"#0f766e"},{label:"Board preferences",value:submittedBoard,total:boardMembers.length,color:"#7c3aed"}].map(item=>(
                    <div key={item.label} style={{ marginBottom:14 }}>
                      <div style={{ display:"flex",justifyContent:"space-between",marginBottom:5 }}><span style={{ fontSize:13,color:"#555" }}>{item.label}</span><span style={{ fontSize:12,fontWeight:700,color:"#333" }}>{item.value}/{item.total}</span></div>
                      <div style={{ background:"#f0f0f0",borderRadius:4,height:6 }}><div style={{ background:item.total>0&&item.value===item.total?"#22c55e":item.color,height:6,borderRadius:4,width:item.total>0?`${Math.round((item.value/item.total)*100)}%`:"0%",transition:"width 0.5s" }} /></div>
                    </div>
                  ))}
                </div>
                <div style={card}>
                  <div style={{ fontSize:11,fontWeight:700,color:"#9ca3af",textTransform:"uppercase",letterSpacing:0.8,marginBottom:12 }}>Quick Actions</div>
                  <div style={{ display:"flex",flexDirection:"column",gap:8 }}>
                    {[{label:"+ Create Account",tab:"users",primary:true},{label:"Generate Resume Books",tab:"resumebooks",primary:false},{label:"Run Scheduler",tab:"scheduling",primary:false},{label:"Presentation Schedule",tab:"presentations",primary:false},{label:"Program Settings",tab:"settings",primary:false}].map(a=>(
                      <button key={a.tab} onClick={()=>setActiveTab(a.tab)} style={{ padding:"10px 16px",borderRadius:8,border:a.primary?"none":"1.5px solid #e8e8e8",background:a.primary?`linear-gradient(135deg,${MAROON},#a00000)`:"white",color:a.primary?"white":"#333",fontWeight:600,fontSize:13,cursor:"pointer",textAlign:"left",transition:"all 0.15s",boxShadow:a.primary?"0 4px 12px rgba(111,0,0,0.2)":"none" }}
                        onMouseOver={e=>{if(!a.primary)e.currentTarget.style.borderColor=MAROON;}} onMouseOut={e=>{if(!a.primary)e.currentTarget.style.borderColor="#e8e8e8";}}>
                        {a.label}
                      </button>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </>}
          </div>
        )}
        {activeTab === "students" && (

          <div style={{ animation:"fadeUp 0.4s ease both" }}>
            <div style={{ display:"flex",justifyContent:"space-between",alignItems:"flex-start",marginBottom:22 }}>
              <div><h1 style={{ fontSize:24,fontWeight:800,color:"#0f0f0f",marginBottom:4,letterSpacing:-0.5 }}>Students</h1><p style={{ color:"#9ca3af",fontSize:14 }}>{filteredStudents.length} of {students.length} shown</p></div>
              <div style={{ display:"flex",gap:8,flexWrap:"wrap" }}>
                <button onClick={loadStudents} style={{ background:"white",border:"1.5px solid #e8e8e8",color:"#555",padding:"9px 18px",borderRadius:8,fontSize:13,cursor:"pointer",fontWeight:600 }}>↻ Refresh</button>
                <button onClick={downloadCSV} style={{ background:"white",border:"1.5px solid #e8e8e8",color:"#555",padding:"9px 18px",borderRadius:8,fontSize:13,cursor:"pointer",fontWeight:600 }}>↓ Export CSV</button>
                <button onClick={async () => { const next = !submissionsLocked; await setDoc(doc(db, "metadata", "publishStatus"), { submissionsLocked: next }, { merge: true }); setSubmissionsLocked(next); setMsg(next ? "Submissions locked" : "Submissions unlocked"); logActivity(next ? "Locked Submissions" : "Unlocked Submissions"); }} style={{ padding:"9px 18px",borderRadius:8,background:submissionsLocked?"#fff1f0":"linear-gradient(135deg,#6f0000,#a00000)",color:submissionsLocked?"#dc2626":"white",border:submissionsLocked?"1.5px solid #fca5a5":"none",fontWeight:700,fontSize:13,cursor:"pointer" }}>{submissionsLocked ? "Locked" : "Lock Submissions"}</button>
              </div>
            </div>
            {students.filter(s=>s.springException && s.springException !== "" && s.springException.toLowerCase() !== "this does not apply to me").length > 0 && (
              <div style={{ background:"#eff6ff",border:"1px solid #93c5fd",borderRadius:12,padding:"16px 20px",marginBottom:18 }}>
                <div style={{ fontSize:13,fontWeight:700,color:"#1d4ed8",marginBottom:12 }}>Internship Exceptions ({students.filter(s=>s.springException && s.springException !== "" && s.springException.toLowerCase() !== "this does not apply to me").length})</div>
                {students.filter(s=>s.springException && s.springException !== "" && s.springException.toLowerCase() !== "this does not apply to me").map(s=>(
                  <div key={s.id} style={{ display:"flex",alignItems:"center",justifyContent:"space-between",padding:"10px 14px",background:"white",borderRadius:8,marginBottom:8,border:"1px solid #bfdbfe" }}>
                    <div>
                      <div style={{ fontWeight:700,fontSize:13,color:"#1a1a1a" }}>{s.firstName} {s.lastName} — Group {s.group}</div>
                      <div style={{ fontSize:12,color:"#1d4ed8",marginTop:2 }}>{s.springException}</div>
                      {s.springExceptionStatus && <span style={{ fontSize:11,fontWeight:700,color:s.springExceptionStatus==="approved"?"#166534":s.springExceptionStatus==="denied"?"#991b1b":"#92400e",marginTop:4,display:"block" }}>{s.springExceptionStatus==="approved"?"✓ Approved":s.springExceptionStatus==="denied"?"✗ Denied":"⏳ Pending"}</span>}
                    </div>
                    <div style={{ display:"flex",gap:6 }}>
                      {s.springExceptionStatus !== "approved" && (
                        <button onClick={()=>handleSpringException(s.id,`${s.firstName} ${s.lastName}`,"approve")} style={{ background:"#f0fdf4",border:"1px solid #86efac",color:"#166534",padding:"5px 12px",borderRadius:6,fontSize:11,cursor:"pointer",fontWeight:700 }}>✓ Approve</button>
                      )}
                      {s.springExceptionStatus !== "denied" && (
                        <button onClick={()=>handleSpringException(s.id,`${s.firstName} ${s.lastName}`,"deny")} style={{ background:"#fff1f0",border:"1px solid #fca5a5",color:"#dc2626",padding:"5px 12px",borderRadius:6,fontSize:11,cursor:"pointer",fontWeight:700 }}>✗ Deny</button>
                      )}
                      <button onClick={()=>{setViewingStudent(s);setAdminNote(s.adminNotes||"");}} style={{ background:"#eff6ff",border:"1px solid #93c5fd",color:"#1d4ed8",padding:"5px 12px",borderRadius:6,fontSize:11,cursor:"pointer",fontWeight:700 }}>View</button>
                    </div>
                  </div>
                ))}
              </div>
            )}
            {students.filter(s=>s.continueTrip==="No"||s.continueTrip==="Unsure").length > 0 && (
              <div style={{ background:"#fff1f0",border:"1px solid #fca5a5",borderRadius:12,padding:"16px 20px",marginBottom:18 }}>
                <div style={{ fontSize:13,fontWeight:700,color:"#991b1b",marginBottom:12 }}>⚠ Not Continuing TRIP ({students.filter(s=>s.continueTrip==="No"||s.continueTrip==="Unsure").length})</div>
                {students.filter(s=>s.continueTrip==="No"||s.continueTrip==="Unsure").map(s=>(
                  <div key={s.id} style={{ display:"flex",alignItems:"center",justifyContent:"space-between",padding:"10px 14px",background:"white",borderRadius:8,marginBottom:8,border:"1px solid #fca5a5" }}>
                    <div>
                      <div style={{ fontWeight:700,fontSize:13,color:"#1a1a1a" }}>{s.firstName} {s.lastName} — Group {s.group}</div>
                      <div style={{ fontSize:12,color:"#991b1b",marginTop:2 }}>Continue TRIP: {s.continueTrip}</div>
                    </div>
                    <button onClick={()=>{setViewingStudent(s);setAdminNote(s.adminNotes||"");}} style={{ background:"#fff1f0",border:"1px solid #fca5a5",color:"#991b1b",padding:"5px 12px",borderRadius:6,fontSize:11,cursor:"pointer",fontWeight:700 }}>View</button>
                  </div>
                ))}
              </div>
            )}
            {students.filter(s=>s.blackoutRequestStatus==="pending").length > 0 && (
              <div style={{ background:"#fffbeb",border:"1px solid #fcd34d",borderRadius:12,padding:"16px 20px",marginBottom:18 }}>
                <div style={{ fontSize:13,fontWeight:700,color:"#92400e",marginBottom:12 }}>⏳ Pending Blackout Requests ({students.filter(s=>s.blackoutRequestStatus==="pending").length})</div>
                {students.filter(s=>s.blackoutRequestStatus==="pending").map(s=>(
                  <div key={s.id} style={{ display:"flex",alignItems:"center",justifyContent:"space-between",padding:"10px 14px",background:"white",borderRadius:8,marginBottom:8,border:"1px solid #fde68a" }}>
                    <div>
                      <div style={{ fontWeight:700,fontSize:13,color:"#1a1a1a" }}>{s.firstName} {s.lastName} — Group {s.group}</div>
                      <div style={{ fontSize:12,color:"#92400e",marginTop:2 }}>{s.blackoutReason || "No reason provided"}</div>
                    </div>
                    <div style={{ display:"flex",gap:8,flexShrink:0,marginLeft:16 }}>
                      <button onClick={()=>handleBlackoutRequest(s.id,`${s.firstName} ${s.lastName}`,"approve")} style={{ background:"#f0fdf4",border:"1px solid #86efac",color:"#166534",padding:"7px 16px",borderRadius:8,fontSize:12,cursor:"pointer",fontWeight:700 }}>✓ Approve</button>
                      <button onClick={()=>handleBlackoutRequest(s.id,`${s.firstName} ${s.lastName}`,"deny")} style={{ background:"#fff1f0",border:"1px solid #fca5a5",color:"#dc2626",padding:"7px 16px",borderRadius:8,fontSize:12,cursor:"pointer",fontWeight:700 }}>✗ Deny</button>
                    </div>
                  </div>
                ))}
              </div>
            )}
            <div style={{ display:"flex",gap:10,marginBottom:18,flexWrap:"wrap" }}>
              <input value={studentSearch} onChange={e=>setStudentSearch(e.target.value)} placeholder="Search by name or email..." style={{ ...inp("ss"),maxWidth:260,padding:"9px 14px" }} onFocus={()=>setFocused("ss")} onBlur={()=>setFocused(null)} />
              <div style={{ display:"flex",background:"white",borderRadius:8,border:"1.5px solid #e8e8e8",overflow:"hidden" }}>
                {["all", ...(programSettings.groups||[{value:"16"},{value:"17"},{value:"18"}]).map(g=>g.value)].map(g=><button key={g} onClick={()=>setGroupFilter(g)} style={{ padding:"8px 14px",border:"none",background:groupFilter===g?MAROON:"transparent",color:groupFilter===g?"white":"#555",fontWeight:600,fontSize:12,cursor:"pointer" }}>{g==="all"?"All Groups":`Group ${g}`}</button>)}
              </div>
              <div style={{ display:"flex",background:"white",borderRadius:8,border:"1.5px solid #e8e8e8",overflow:"hidden" }}>
                {[["all","All"],["complete","Complete"],["missing_resume","No Resume"],["missing_photo","No Photo"],["missing_both","Incomplete"]].map(([val,lbl2])=><button key={val} onClick={()=>setStatusFilter(val)} style={{ padding:"8px 12px",border:"none",background:statusFilter===val?MAROON:"transparent",color:statusFilter===val?"white":"#555",fontWeight:600,fontSize:11,cursor:"pointer" }}>{lbl2}</button>)}
              </div>
            </div>
            {selectedStudents.size > 0 && (
              <div style={{ background:"#0f172a",borderRadius:12,padding:"12px 20px",marginBottom:14,display:"flex",alignItems:"center",gap:16,animation:"slideIn 0.2s ease" }}>
                <span style={{ color:"white",fontWeight:700,fontSize:14 }}>{selectedStudents.size} selected</span>
                <button onClick={async () => {
                  if (!window.confirm(`Reopen submissions for ${selectedStudents.size} student${selectedStudents.size!==1?"s":""}?`)) return;
                  await Promise.all([...selectedStudents].map(id => setDoc(doc(db, "students", id), { submissionLocked: false }, { merge: true })));
                  setMsg(`${selectedStudents.size} submissions reopened`); setSelectedStudents(new Set()); loadStudents();
                }} style={{ background:`linear-gradient(135deg,${MAROON},#a00000)`,color:"white",border:"none",padding:"7px 16px",borderRadius:8,fontWeight:700,fontSize:12,cursor:"pointer" }}>↺ Reopen All</button>
                <button onClick={() => setSelectedStudents(new Set())} style={{ background:"rgba(255,255,255,0.08)",color:"rgba(255,255,255,0.6)",border:"1px solid rgba(255,255,255,0.12)",padding:"7px 14px",borderRadius:8,fontSize:12,cursor:"pointer",marginLeft:"auto" }}>Clear</button>
              </div>
            )}
            <div style={card}>
              <table style={{ width:"100%",borderCollapse:"collapse",fontSize:13 }}>
                <thead>
                  <tr style={{ borderBottom:"2px solid #f0f0f0" }}>
                    <th style={{ padding:"10px 12px",width:36 }}>
                      <input type="checkbox" checked={filteredStudents.length > 0 && filteredStudents.every(s => selectedStudents.has(s.id))}
                        ref={el => { if (el) el.indeterminate = selectedStudents.size > 0 && !filteredStudents.every(s => selectedStudents.has(s.id)); }}
                        onChange={e => { const next = new Set(selectedStudents); if (e.target.checked) filteredStudents.forEach(s => next.add(s.id)); else filteredStudents.forEach(s => next.delete(s.id)); setSelectedStudents(next); }}
                        style={{ cursor:"pointer",accentColor:MAROON }} />
                    </th>
                    {[["name","Name"],["group","Group"],["","Class"],["","Shirt"],["status","Status"],["completion","Done %"],["updated","Updated"],["",""]].map(([field,label]) => (
                      <th key={label} onClick={() => field && handleSort(field)}
                        style={{ textAlign:"left",padding:"10px 12px",color: field && sortField===field?"#1a1a1a":"#aaa",fontWeight:700,fontSize:11,textTransform:"uppercase",letterSpacing:0.5,cursor:field?"pointer":"default",userSelect:"none",whiteSpace:"nowrap" }}>
                        {label}{field && sortField===field ? (sortDir==="asc"?" ▲":" ▼") : ""}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {filteredStudents.map(s=>(
                    <tr key={s.id} className="row-hover" style={{ borderBottom:"1px solid #f8f8f8", background: selectedStudents.has(s.id) ? "#fff8f8" : undefined }}>
                      <td style={{ padding:"10px 12px" }}>
                        <input type="checkbox" checked={selectedStudents.has(s.id)}
                          onChange={e => { const next = new Set(selectedStudents); e.target.checked ? next.add(s.id) : next.delete(s.id); setSelectedStudents(next); }}
                          style={{ cursor:"pointer",accentColor:MAROON }} />
                      </td>
                      <td style={{ padding:"10px 12px" }}>
                        <div style={{ display:"flex",alignItems:"center",gap:10 }}>
                          {s.photoURL ? <img src={s.photoURL} alt="" style={{ width:32,height:32,borderRadius:"50%",objectFit:"cover",border:"2px solid #f0f0f0",flexShrink:0 }} /> : <div style={{ width:32,height:32,borderRadius:"50%",background:"#f0f0f0",display:"flex",alignItems:"center",justifyContent:"center",fontSize:13,color:"#bbb",flexShrink:0 }}>◎</div>}
                          <div><div style={{ fontWeight:700,color:"#1a1a1a" }}>{s.firstName} {s.lastName}</div><div style={{ fontSize:11,color:"#aaa",marginTop:1 }}>{s.email}</div></div>
                        </div>
                      </td>
                      <td style={{ padding:"10px 12px" }}><span style={{ background:"#fff0f0",color:MAROON,padding:"3px 8px",borderRadius:6,fontSize:11,fontWeight:700 }}>Grp {s.group}</span></td>
                      <td style={{ padding:"10px 12px",color:"#666" }}>{s.classYear||"—"}</td>
                      <td style={{ padding:"10px 12px",color:"#666" }}>{s.shirtSize||"—"}</td>
                      <td style={{ padding:"10px 12px" }}><StatusPill status={getStudentStatus(s)} /></td>
                      <td style={{ padding:"10px 12px",minWidth:120 }}><MiniBar pct={getStudentCompletion(s)} /></td>
                      <td style={{ padding:"10px 12px",color:"#bbb",fontSize:12 }}>{s.updatedAt?new Date(s.updatedAt).toLocaleDateString():"—"}</td>
                      <td style={{ padding:"10px 12px" }}>
                        <div style={{ display:"flex",gap:6,flexWrap:"wrap",alignItems:"center" }}>
                          <button onClick={()=>{setViewingStudent(s);setAdminNote(s.adminNotes||"");}} style={{ background:"linear-gradient(135deg,#6f0000,#a00000)",color:"white",border:"none",padding:"4px 10px",borderRadius:6,fontSize:11,cursor:"pointer",fontWeight:700 }}>View</button>
                          <button onClick={()=>toggleSubmissionLock(s.id,"student", s.submissionLocked===false)} style={{ background:"none",border:"1px solid #e8e8e8",color:"#666",padding:"4px 10px",borderRadius:6,fontSize:11,cursor:"pointer" }}>{s.submissionLocked===false ? "Lock" : "Reopen"}</button>
                          {s.blackoutRequestStatus === "pending" && (
                            <>
                              <button onClick={()=>handleBlackoutRequest(s.id,`${s.firstName} ${s.lastName}`,"approve")} style={{ background:"#f0fdf4",border:"1px solid #86efac",color:"#166534",padding:"4px 10px",borderRadius:6,fontSize:11,cursor:"pointer",fontWeight:700 }}>✓ Approve Blackout</button>
                              <button onClick={()=>handleBlackoutRequest(s.id,`${s.firstName} ${s.lastName}`,"deny")} style={{ background:"#fff1f0",border:"1px solid #fca5a5",color:"#dc2626",padding:"4px 10px",borderRadius:6,fontSize:11,cursor:"pointer",fontWeight:700 }}>✗ Deny</button>
                            </>
                          )}
                          {s.blackoutRequestStatus === "approved" && s.blackoutTimes?.length > 0 && (
                            <span onClick={e=>{e.stopPropagation();setBlackoutPreviewStudent(s);}} style={{ background:"#eff6ff",color:"#1d4ed8",border:"1px solid #93c5fd",padding:"3px 8px",borderRadius:6,fontSize:10,fontWeight:700,cursor:"pointer" }}>{s.blackoutTimes.length} slots blocked ↗</span>
                          )}
                          {s.blackoutRequestStatus === "approved" && !s.blackoutTimes?.length && (
                            <span style={{ background:"#f0fdf4",color:"#166534",border:"1px solid #86efac",padding:"3px 8px",borderRadius:6,fontSize:10,fontWeight:700 }}>✓ Approved</span>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                  {loading && [1,2,3,4,5].map(i=><tr key={i}><td colSpan={9} style={{ padding:"10px 12px" }}><Skeleton h={38} r={6} /></td></tr>)}
                  {!loading && filteredStudents.length===0&&<tr><td colSpan={9} style={{ padding:"48px 32px",textAlign:"center" }}><div style={{ fontSize:36,marginBottom:12,opacity:0.2 }}>◎</div><div style={{ fontWeight:700,fontSize:15,color:"#555",marginBottom:6 }}>No students match your filters</div><div style={{ fontSize:13,color:"#aaa" }}>Try adjusting the group or status filter above</div></td></tr>}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {viewingStudent && (
          <div onClick={() => setViewingStudent(null)} style={{ position:"fixed",inset:0,background:"rgba(0,0,0,0.75)",zIndex:1000,display:"flex",alignItems:"center",justifyContent:"center",padding:20,backdropFilter:"blur(8px)" }}>
            <div onClick={e=>e.stopPropagation()} style={{ background:"rgba(15,23,42,0.92)",border:"1px solid rgba(255,255,255,0.1)",borderRadius:20,padding:"32px 36px",width:"100%",maxWidth:680,maxHeight:"85vh",overflowY:"auto",boxShadow:"0 32px 80px rgba(0,0,0,0.6),0 0 0 1px rgba(255,255,255,0.05)" }}>
              <div style={{ display:"flex",justifyContent:"space-between",alignItems:"flex-start",marginBottom:24 }}>
                <div style={{ display:"flex",alignItems:"center",gap:14 }}>
                  {viewingStudent.photoURL ? <img src={viewingStudent.photoURL} alt="" style={{ width:64,height:64,borderRadius:"50%",objectFit:"cover",border:"2px solid rgba(111,0,0,0.8)",boxShadow:"0 0 0 3px rgba(111,0,0,0.25)" }} /> : <div style={{ width:64,height:64,borderRadius:"50%",background:"rgba(255,255,255,0.08)",display:"flex",alignItems:"center",justifyContent:"center",fontSize:26,color:"rgba(255,255,255,0.3)" }}>?</div>}
                  <div>
                    <div style={{ fontSize:20,fontWeight:800,color:"white" }}>{viewingStudent.firstName} {viewingStudent.lastName}</div>
                    <div style={{ fontSize:13,color:"rgba(255,255,255,0.4)",marginTop:3 }}>Group {viewingStudent.group} · {viewingStudent.classYear||"N/A"} · {viewingStudent.email}</div>
                  </div>
                </div>
                <button onClick={()=>setViewingStudent(null)} style={{ background:"rgba(255,255,255,0.06)",border:"1px solid rgba(255,255,255,0.1)",borderRadius:8,width:32,height:32,cursor:"pointer",color:"rgba(255,255,255,0.5)",fontSize:16,display:"flex",alignItems:"center",justifyContent:"center" }}>×</button>
              </div>
              <div style={{ display:"grid",gridTemplateColumns:"1fr 1fr",gap:8,marginBottom:20 }}>
                {[["Undergrad Degree",viewingStudent.undergradDegree],["Minor",viewingStudent.minor],["Expected Graduation",viewingStudent.expectedGraduation],["Seeking MFM",viewingStudent.seekingMFM],["Hometown",viewingStudent.hometown],["Shirt Size",viewingStudent.shirtSize],["Continue TRIP",viewingStudent.continueTrip],["Blackout Status",viewingStudent.blackoutRequestStatus]].filter(([,v])=>v).map(([label,value])=>(
                  <div key={label} style={{ background:"rgba(255,255,255,0.06)",border:"1px solid rgba(255,255,255,0.08)",borderRadius:10,padding:"10px 14px" }}>
                    <div style={{ fontSize:10,color:"rgba(255,255,255,0.35)",fontWeight:700,textTransform:"uppercase",letterSpacing:0.8,marginBottom:4 }}>{label}</div>
                    <div style={{ fontSize:13,fontWeight:600,color:"rgba(255,255,255,0.85)" }}>{value}</div>
                  </div>
                ))}
              </div>
              {viewingStudent.personalStatement && (
                <div style={{ background:"rgba(255,255,255,0.05)",border:"1px solid rgba(255,255,255,0.08)",borderRadius:10,padding:"14px 16px",marginBottom:14 }}>
                  <div style={{ fontSize:10,color:"rgba(255,255,255,0.35)",fontWeight:700,textTransform:"uppercase",letterSpacing:0.8,marginBottom:8 }}>Personal Statement</div>
                  <div style={{ fontSize:13,color:"rgba(255,255,255,0.7)",lineHeight:1.7 }}>{viewingStudent.personalStatement}</div>
                </div>
              )}
              {viewingStudent.blackoutRequestStatus === "approved" && (
                <div style={{ background:"rgba(255,255,255,0.05)",border:"1px solid rgba(255,255,255,0.08)",borderRadius:10,padding:"14px 16px",marginBottom:14 }}>
                  <div style={{ fontSize:10,color:"rgba(255,255,255,0.35)",fontWeight:700,textTransform:"uppercase",letterSpacing:0.8,marginBottom:10 }}>
                    Blocked Interview Times {viewingStudent.blackoutTimes?.length > 0 ? `(${viewingStudent.blackoutTimes.length} slots)` : "— none selected yet"}
                  </div>
                  {viewingStudent.blackoutTimes?.length > 0 ? (() => {
                    const dayLabels = {};
                    (programSettings?.boardWeek?.days || []).forEach((d, i) => { dayLabels[String(i + 1)] = d.label || `Day ${i + 1}`; });
                    const byDay = {};
                    (viewingStudent.blackoutTimes || []).forEach(b => {
                      const day = b?.day ?? b?.[0]; const time = b?.time ?? b?.[1];
                      if (!day || !time) return;
                      (byDay[day] = byDay[day] || []).push(time);
                    });
                    return Object.entries(byDay).sort(([a],[b])=>a-b).map(([day, times]) => (
                      <div key={day} style={{ marginBottom:8 }}>
                        <div style={{ fontSize:11,fontWeight:700,color:"rgba(255,255,255,0.5)",marginBottom:4 }}>{dayLabels[day] || `Day ${day}`}</div>
                        <div style={{ display:"flex",flexWrap:"wrap",gap:4 }}>
                          {times.sort().map(t => (
                            <span key={t} style={{ background:"rgba(220,38,38,0.15)",border:"1px solid rgba(220,38,38,0.35)",color:"rgba(248,113,113,0.9)",padding:"2px 8px",borderRadius:4,fontSize:11,fontWeight:600 }}>{t}</span>
                          ))}
                        </div>
                      </div>
                    ));
                  })() : <div style={{ fontSize:12,color:"rgba(255,255,255,0.3)" }}>Student has not selected any slots yet.</div>}
                </div>
              )}
              {(viewingStudent.springInternshipCompany||viewingStudent.summerInternshipCompany||viewingStudent.firstInternshipCompany||viewingStudent.investmentDecision) && (
                <div style={{ background:"rgba(255,255,255,0.05)",border:"1px solid rgba(255,255,255,0.08)",borderRadius:10,padding:"14px 16px",marginBottom:14 }}>
                  <div style={{ fontSize:10,color:"rgba(255,255,255,0.35)",fontWeight:700,textTransform:"uppercase",letterSpacing:0.8,marginBottom:12 }}>Internship / Commodities Analysis</div>
                  {viewingStudent.springInternshipCompany && <div style={{ marginBottom:8 }}><span style={{ fontWeight:700,fontSize:13,color:"white" }}>{viewingStudent.springInternshipCompany}:</span> <span style={{ fontSize:13,color:"rgba(255,255,255,0.6)" }}>{viewingStudent.springInternship}</span></div>}
                  {viewingStudent.summerInternshipCompany && <div style={{ marginBottom:8 }}><span style={{ fontWeight:700,fontSize:13,color:"white" }}>{viewingStudent.summerInternshipCompany}:</span> <span style={{ fontSize:13,color:"rgba(255,255,255,0.6)" }}>{viewingStudent.summerInternship}</span></div>}
                  {viewingStudent.firstInternshipCompany && <div style={{ marginBottom:8 }}><span style={{ fontWeight:700,fontSize:13,color:"white" }}>{viewingStudent.firstInternshipCompany}:</span> <span style={{ fontSize:13,color:"rgba(255,255,255,0.6)" }}>{viewingStudent.firstInternship}</span></div>}
                  {viewingStudent.investmentDecision && <div><span style={{ fontWeight:700,fontSize:13,color:"white" }}>Presentation Topic:</span> <span style={{ fontSize:13,color:"rgba(255,255,255,0.6)" }}>{viewingStudent.investmentDecision}</span></div>}
                </div>
              )}
              <div style={{ background:"rgba(255,255,255,0.05)",border:"1px solid rgba(255,255,255,0.08)",borderRadius:10,padding:"14px 16px",marginBottom:14 }}>
                <div style={{ fontSize:10,color:"rgba(255,255,255,0.35)",fontWeight:700,textTransform:"uppercase",letterSpacing:0.8,marginBottom:8 }}>Admin Notes (private)</div>
                <textarea value={adminNote} onChange={e=>setAdminNote(e.target.value)} rows={3} placeholder="Internal notes — not visible to student…"
                  style={{ width:"100%",background:"rgba(255,255,255,0.07)",border:"1px solid rgba(255,255,255,0.12)",borderRadius:8,padding:"10px 12px",color:"rgba(255,255,255,0.85)",fontSize:13,resize:"vertical",outline:"none",boxSizing:"border-box",lineHeight:1.6 }} />
                <button onClick={async ()=>{ await setDoc(doc(db,"students",viewingStudent.id),{adminNotes:adminNote},{merge:true}); showToast("Note saved","success"); }} style={{ marginTop:8,background:"rgba(255,255,255,0.1)",border:"1px solid rgba(255,255,255,0.15)",color:"rgba(255,255,255,0.8)",padding:"7px 16px",borderRadius:7,fontSize:12,fontWeight:700,cursor:"pointer" }}>Save Note</button>
              </div>
              <div style={{ display:"flex",gap:10,marginTop:16,flexWrap:"wrap" }}>
                {viewingStudent.resumeURL && <a href={viewingStudent.resumeURL} target="_blank" rel="noreferrer" style={{ background:"linear-gradient(135deg,#7a0000,#b00000)",color:"white",padding:"10px 20px",borderRadius:8,fontWeight:700,fontSize:13,textDecoration:"none",boxShadow:"0 4px 14px rgba(111,0,0,0.4)" }}>View Resume →</a>}
                {viewingStudent.ferpaConsentStatus === "signed" && <span style={{ background:"rgba(34,197,94,0.15)",border:"1px solid rgba(34,197,94,0.3)",color:"#86efac",padding:"10px 16px",borderRadius:8,fontWeight:700,fontSize:12 }}>FERPA Signed</span>}
                <button onClick={()=>{toggleSubmissionLock(viewingStudent.id,"student", viewingStudent.submissionLocked===false);setViewingStudent(null);}} style={{ background:"rgba(255,255,255,0.06)",border:"1px solid rgba(255,255,255,0.15)",color:"rgba(255,255,255,0.8)",padding:"10px 20px",borderRadius:8,fontWeight:700,fontSize:13,cursor:"pointer" }}>{viewingStudent.submissionLocked===false ? "Lock Submission" : "Reopen Submission"}</button>
              </div>
            </div>
          </div>
        )}

        {blackoutPreviewStudent && (
          <div onClick={()=>setBlackoutPreviewStudent(null)} style={{ position:"fixed",inset:0,background:"rgba(0,0,0,0.6)",zIndex:1100,display:"flex",alignItems:"center",justifyContent:"center",padding:20,backdropFilter:"blur(4px)" }}>
            <div onClick={e=>e.stopPropagation()} style={{ background:"white",borderRadius:16,padding:"28px 32px",width:"100%",maxWidth:480,maxHeight:"80vh",overflowY:"auto",boxShadow:"0 24px 60px rgba(0,0,0,0.25)" }}>
              <div style={{ display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:20 }}>
                <div>
                  <div style={{ fontWeight:800,fontSize:17,color:"#1a1a1a" }}>{blackoutPreviewStudent.firstName} {blackoutPreviewStudent.lastName}</div>
                  <div style={{ fontSize:12,color:"#9ca3af",marginTop:2 }}>Group {blackoutPreviewStudent.group} · {blackoutPreviewStudent.blackoutTimes?.length || 0} blocked slots</div>
                </div>
                <button onClick={()=>setBlackoutPreviewStudent(null)} style={{ background:"none",border:"1px solid #e8e8e8",borderRadius:8,width:30,height:30,cursor:"pointer",color:"#888",fontSize:16,display:"flex",alignItems:"center",justifyContent:"center" }}>×</button>
              </div>
              {(() => {
                const dayLabels = {};
                (programSettings?.boardWeek?.days || []).forEach((d, i) => { dayLabels[String(i + 1)] = d.label || `Day ${i + 1}`; });
                const byDay = {};
                (blackoutPreviewStudent.blackoutTimes||[]).forEach(b=>{
                  const day=b?.day??b?.[0]; const time=b?.time??b?.[1];
                  if(!day||!time)return;
                  (byDay[day]=byDay[day]||[]).push(time);
                });
                return Object.entries(byDay).sort(([a],[b])=>a-b).map(([day,times])=>(
                  <div key={day} style={{ marginBottom:16 }}>
                    <div style={{ fontSize:12,fontWeight:700,color:"#555",marginBottom:8 }}>{dayLabels[day]||`Day ${day}`}</div>
                    <div style={{ display:"flex",flexWrap:"wrap",gap:6 }}>
                      {times.sort().map(t=>(
                        <span key={t} style={{ background:"#fff1f0",border:"1px solid #fca5a5",color:"#991b1b",padding:"4px 10px",borderRadius:6,fontSize:12,fontWeight:600 }}>{t}</span>
                      ))}
                    </div>
                  </div>
                ));
              })()}
            </div>
          </div>
        )}

        {activeTab === "board" && (
          <div style={{ animation:"fadeUp 0.4s ease both" }}>
            <div style={{ marginBottom:22 }}>
              <h1 style={{ fontSize:24,fontWeight:800,color:"#0f0f0f",marginBottom:4,letterSpacing:-0.5 }}>Board Members</h1>
              <p style={{ color:"#9ca3af",fontSize:14,marginBottom:10 }}>{submittedBoard} of {boardMembers.length} submitted preferences · {totalRooms} total rooms requested</p>
              <div style={{ display:"flex",gap:8,flexWrap:"wrap" }}>
                {[["platinum","Platinum"],["gold","Gold"],["silver","Silver"]].map(([tier,label])=>(
                  <div key={tier} style={{ display:"flex",alignItems:"center",gap:6,background:"white",border:`1px solid ${TIER_COLORS[tier]}40`,borderRadius:8,padding:"6px 12px" }}>
                    <span style={{ width:8,height:8,borderRadius:"50%",background:TIER_COLORS[tier],flexShrink:0 }} />
                    <span style={{ fontSize:12,fontWeight:600,color:"#555" }}>{label}</span>
                    <span style={{ fontSize:12,fontWeight:800,color:"#1a1a1a" }}>{roomsByTier[tier]}</span>
                    <span style={{ fontSize:11,color:"#9ca3af" }}>room{roomsByTier[tier]===1?"":"s"}</span>
                  </div>
                ))}
              </div>
            </div>
            {!selectedBoard && (
              <div style={{ display:"flex",gap:10,marginBottom:18,flexWrap:"wrap" }}>
                <input value={boardSearch} onChange={e=>setBoardSearch(e.target.value)} placeholder="Search company or contact..." style={{ ...inp("bs"),maxWidth:280,padding:"9px 14px" }} onFocus={()=>setFocused("bs")} onBlur={()=>setFocused(null)} />
                <div style={{ display:"flex",background:"white",borderRadius:8,border:"1.5px solid #e8e8e8",overflow:"hidden" }}>
                  {[["all","All"],["platinum","Platinum"],["gold","Gold"],["silver","Silver"]].map(([val,lbl2])=>(
                    <button key={val} onClick={()=>setBoardTypeFilter(val)} style={{ padding:"8px 14px",border:"none",background:boardTypeFilter===val?MAROON:"transparent",color:boardTypeFilter===val?"white":"#555",fontWeight:600,fontSize:12,cursor:"pointer" }}>{lbl2}</button>
                  ))}
                </div>
              </div>
            )}
            {boardMembers.filter(b => b.ferpaConsent === "declined").length > 0 && (
              <div style={{ background:"#fff1f0",border:"1px solid #fca5a5",borderRadius:12,padding:"16px 20px",marginBottom:18 }}>
                <div style={{ fontSize:13,fontWeight:700,color:"#991b1b",marginBottom:12 }}>⚠ Board FERPA Non-Consent ({boardMembers.filter(b=>b.ferpaConsent==="declined").length})</div>
                {boardMembers.filter(b=>b.ferpaConsent==="declined").map(b=>(
                  <div key={b.id} style={{ display:"flex",alignItems:"center",justifyContent:"space-between",padding:"10px 14px",background:"white",borderRadius:8,marginBottom:8,border:"1px solid #fca5a5" }}>
                    <div>
                      <div style={{ fontWeight:700,fontSize:13,color:"#1a1a1a" }}>{b.profile?.companyName || b.name || "Unknown"}</div>
                      <div style={{ fontSize:12,color:"#991b1b",marginTop:2 }}>Declined FERPA agreement on {b.ferpaConsentAt ? new Date(b.ferpaConsentAt).toLocaleDateString() : "unknown date"}</div>
                    </div>
                    <button onClick={async () => { await setDoc(doc(db,"boardPreferences",b.id),{ferpaConsentStatus:"acknowledged"},{merge:true}); loadAll(); }} style={{ background:"#f0fdf4",border:"1px solid #86efac",color:"#166534",padding:"7px 16px",borderRadius:8,fontSize:12,cursor:"pointer",fontWeight:700 }}>✓ Acknowledge</button>
                  </div>
                ))}
              </div>
            )}
            {selectedBoard ? (
              <div>
                <button onClick={()=>setSelectedBoard(null)} style={{ background:"white",border:"1.5px solid #e8e8e8",color:"#555",padding:"9px 18px",borderRadius:8,fontSize:13,cursor:"pointer",fontWeight:600,marginBottom:20 }}>← Back</button>
                <div style={{ display:"grid",gridTemplateColumns:"1fr 1fr",gap:18 }}>
                  <div style={card}>
                    <div style={{ fontSize:11,fontWeight:700,color:"#9ca3af",textTransform:"uppercase",letterSpacing:0.6,marginBottom:14 }}>Company Profile</div>
                    {selectedBoard.profile?[["Company",selectedBoard.profile.companyName],["Type",selectedBoard.profile.memberType],["Contact",`${selectedBoard.profile.mainContactFirst||""} ${selectedBoard.profile.mainContactLast||""}`.trim()],["Email",selectedBoard.profile.email],["Phone",selectedBoard.profile.phone],["Address",selectedBoard.profile.address],["Rooms",selectedBoard.profile.interviewRooms],["Spring Internships Offered",selectedBoard.profile.springInternshipsOffered],["Summer Internships Offered",selectedBoard.profile.summerInternshipsOffered],["Attendees",selectedBoard.profile.totalAttendees],["Dietary",selectedBoard.profile.dietaryRestrictions]].filter(([,v])=>v).map(([l,v])=>(
                      <div key={l} style={{ display:"flex",justifyContent:"space-between",padding:"9px 0",borderBottom:"1px solid #f5f5f5",fontSize:13 }}><span style={{ color:"#9ca3af",fontWeight:600 }}>{l}</span><span style={{ color:"#333" }}>{v}</span></div>
                    )):<div style={{ color:"#d1d5db",fontSize:13 }}>No profile submitted</div>}
                    <div style={{ display:"flex",justifyContent:"space-between",alignItems:"center",padding:"9px 0",fontSize:13 }}>
                      <span style={{ color:"#9ca3af",fontWeight:600 }}>Preferred Candidate Cap</span>
                      <input
                        type="number" min={0}
                        key={selectedBoard.id}
                        defaultValue={selectedBoard.profile?.maxPreferred || ""}
                        placeholder={`${(parseInt(selectedBoard.profile?.interviewRooms)||1)*20} (default)`}
                        onBlur={e => { if (e.target.value !== String(selectedBoard.profile?.maxPreferred||"")) updateMaxPreferred(selectedBoard, e.target.value); }}
                        style={{ width:130,padding:"6px 9px",border:"1.5px solid #ebebeb",borderRadius:6,fontSize:12,textAlign:"right" }}
                      />
                    </div>
                    <div style={{ fontSize:11,color:"#9ca3af",lineHeight:1.5 }}>Overrides the default rooms × 20 cap on how many candidates this company can select. Leave blank to use the default.</div>
                    <div style={{ display:"flex",justifyContent:"space-between",alignItems:"center",padding:"9px 0",fontSize:13,marginTop:6,borderTop:"1px solid #f5f5f5" }}>
                      <span style={{ color:"#9ca3af",fontWeight:600 }}>Room Number</span>
                      <input
                        type="text"
                        key={`rn-${selectedBoard.id}`}
                        defaultValue={selectedBoard.profile?.roomNumbers || ""}
                        placeholder="auto-assigned"
                        onBlur={e => { if (e.target.value.trim() !== (selectedBoard.profile?.roomNumbers || "")) updateRoomNumbers(selectedBoard, e.target.value); }}
                        style={{ width:130,padding:"6px 9px",border:"1.5px solid #ebebeb",borderRadius:6,fontSize:12,textAlign:"right" }}
                      />
                    </div>
                    <div style={{ fontSize:11,color:"#9ca3af",lineHeight:1.5 }}>Pin this company to specific room number(s), e.g. "6" or "2, 3" for a 2-room company. Must match the room count above or the scheduler falls back to auto-numbering. Leave blank to auto-assign.</div>
                  </div>
                  <div style={card}>
                    <div style={{ fontSize:11,fontWeight:700,color:"#9ca3af",textTransform:"uppercase",letterSpacing:0.6,marginBottom:14 }}>Additional Attendees ({(selectedBoard.additionalAttendees||[]).filter(a=>a.name).length})</div>
                    {(selectedBoard.additionalAttendees||[]).filter(a=>a.name).map((a,i)=>(
                      <div key={i} style={{ padding:"9px 0", borderBottom:"1px solid #f5f5f5" }}>
                        <div style={{ display:"flex",justifyContent:"space-between",fontSize:13 }}><span style={{ fontWeight:600,color:"#333" }}>{a.name}</span><span style={{ color:"#9ca3af" }}>{a.email}</span></div>
                        {a.dietaryRestrictions && <div style={{ fontSize:12,color:MAROON,marginTop:2 }}>Dietary: {a.dietaryRestrictions}</div>}
                      </div>
                    ))}
                    {!(selectedBoard.additionalAttendees||[]).filter(a=>a.name).length && <div style={{ color:"#d1d5db",fontSize:13 }}>None listed</div>}
                  </div>
                  <div style={card}>
                    <div style={{ fontSize:11,fontWeight:700,color:"#9ca3af",textTransform:"uppercase",letterSpacing:0.6,marginBottom:14 }}>Blocked Students ({(selectedBoard.blockedStudents||[]).length})</div>
                    <div style={{ fontSize:12,color:"#9ca3af",marginBottom:12,lineHeight:1.5 }}>Students blocked here will never be assigned to this company by the scheduler and can't be selected in their preference list.</div>
                    <select value="" onChange={e=>{
                      const s = students.find(st=>st.id===e.target.value);
                      if (!s) return;
                      const name = `${s.firstName} ${s.lastName}`;
                      const next = [...(selectedBoard.blockedStudents||[]), { id: s.id, name }];
                      updateBlockedStudents(selectedBoard, next);
                    }} style={{ ...inp("blkadd"), marginBottom:12 }}>
                      <option value="">+ Block a student...</option>
                      {students
                        .filter(s => !(selectedBoard.blockedStudents||[]).some(b=>b.id===s.id))
                        .sort((a,b)=>`${a.lastName}`.localeCompare(b.lastName))
                        .map(s => <option key={s.id} value={s.id}>{s.firstName} {s.lastName} (Group {s.group})</option>)}
                    </select>
                    {(selectedBoard.blockedStudents||[]).map(b => (
                      <div key={b.id} style={{ display:"flex",alignItems:"center",justifyContent:"space-between",padding:"8px 12px",background:"#fff1f0",border:"1px solid #fca5a5",borderRadius:8,marginBottom:6 }}>
                        <span style={{ fontSize:13,color:"#991b1b",fontWeight:600 }}>{b.name}</span>
                        <button onClick={()=>updateBlockedStudents(selectedBoard, (selectedBoard.blockedStudents||[]).filter(x=>x.id!==b.id))} style={{ background:"none",border:"none",color:"#dc2626",cursor:"pointer",fontSize:13,fontWeight:700 }}>Unblock</button>
                      </div>
                    ))}
                    {!(selectedBoard.blockedStudents||[]).length && <div style={{ color:"#d1d5db",fontSize:13 }}>No students blocked</div>}
                  </div>
                  <div style={card}>
                    <div style={{ fontSize:11,fontWeight:700,color:"#9ca3af",textTransform:"uppercase",letterSpacing:0.6,marginBottom:14 }}>Block Out Request {selectedBoard.blockoutRequestStatus ? `(${selectedBoard.blockoutRequestStatus})` : ""}</div>
                    {!selectedBoard.blockoutRequestStatus && <div style={{ color:"#d1d5db",fontSize:13 }}>No request submitted</div>}
                    {selectedBoard.blockoutRequestStatus && (selectedBoard.blockoutTimes||[]).length === 0 && (
                      <div style={{ color:"#d1d5db",fontSize:13,marginBottom:14 }}>No specific times were selected</div>
                    )}
                    {(selectedBoard.blockoutTimes||[]).length > 0 && (
                      <div style={{ display:"flex",flexWrap:"wrap",gap:6,marginBottom:14 }}>
                        {(selectedBoard.blockoutTimes||[]).map((b,i)=>(
                          <span key={i} style={{ fontSize:11,fontWeight:600,color:MAROON,background:"#fff0f0",border:"1px solid #fca5a5",borderRadius:6,padding:"3px 8px" }}>Day {b.day} · {b.time}</span>
                        ))}
                      </div>
                    )}
                    {selectedBoard.blockoutRequestStatus === "pending" && (
                      <div style={{ display:"flex",gap:10 }}>
                        <button onClick={()=>handleBoardBlackoutRequest(selectedBoard.id, selectedBoard.profile?.companyName||selectedBoard.name, "approve")} style={{ background:`linear-gradient(135deg,${MAROON},#a00000)`,color:"white",border:"none",padding:"9px 18px",borderRadius:8,fontWeight:700,fontSize:13,cursor:"pointer" }}>Approve</button>
                        <button onClick={()=>handleBoardBlackoutRequest(selectedBoard.id, selectedBoard.profile?.companyName||selectedBoard.name, "deny")} style={{ background:"white",border:"1.5px solid #fca5a5",color:"#dc2626",padding:"9px 18px",borderRadius:8,fontWeight:700,fontSize:13,cursor:"pointer" }}>Deny</button>
                      </div>
                    )}
                    {selectedBoard.blockoutRequestStatus === "approved" && (
                      <div style={{ background:"#f0fdf4",border:"1px solid #86efac",color:"#166534",padding:"8px 14px",borderRadius:8,fontSize:12,fontWeight:600,display:"inline-block" }}>✓ Approved — applied to scheduler</div>
                    )}
                    {selectedBoard.blockoutRequestStatus === "denied" && (
                      <div style={{ background:"#fff1f0",border:"1px solid #fca5a5",color:"#991b1b",padding:"8px 14px",borderRadius:8,fontSize:12,fontWeight:600,display:"inline-block" }}>✗ Denied</div>
                    )}
                  </div>
                  <div style={card}>
                    <div style={{ fontSize:11,fontWeight:700,color:"#9ca3af",textTransform:"uppercase",letterSpacing:0.6,marginBottom:14 }}>Preferences ({(selectedBoard.preferences||[]).length})</div>
                    <div style={{ maxHeight:300,overflowY:"auto" }}>
                      {(selectedBoard.preferences||[]).map((p,i)=>(
                        <div key={p.id} style={{ display:"flex",alignItems:"center",gap:10,padding:"9px 0",borderBottom:"1px solid #f8f8f8" }}>
                          <div style={{ width:22,height:22,background:i<3?`linear-gradient(135deg,${MAROON},#a00000)`:"#f0f0f0",color:i<3?"white":"#888",borderRadius:"50%",display:"flex",alignItems:"center",justifyContent:"center",fontSize:10,fontWeight:800,flexShrink:0 }}>{i+1}</div>
                          <span style={{ fontSize:13,color:"#333" }}>{p.name}</span>
                        </div>
                      ))}
                      {!(selectedBoard.preferences?.length)&&<div style={{ color:"#d1d5db",fontSize:13 }}>None submitted</div>}
                    </div>
                  </div>
                </div>
                <button onClick={()=>toggleSubmissionLock(selectedBoard.id,"board", !selectedBoard.submitted)} style={{ marginTop:12,background:"none",border:`1.5px solid ${MAROON}`,color:MAROON,padding:"10px 20px",borderRadius:8,fontWeight:600,fontSize:13,cursor:"pointer" }}>{selectedBoard.submitted ? "Reopen Preferences" : "Lock Preferences"}</button>

                <div style={{ marginTop:24 }}>
                  <div style={{ fontSize:11,fontWeight:700,color:"#9ca3af",textTransform:"uppercase",letterSpacing:0.6,marginBottom:14 }}>
                    Interview Schedule{scheduleData?.round ? ` — ${scheduleData.round.charAt(0).toUpperCase()+scheduleData.round.slice(1)} round, generated ${new Date(scheduleData.generatedAt).toLocaleString()}` : ""}
                  </div>
                  {(() => {
                    const companyItems = scheduleData?.companySchedules?.[selectedBoard.profile?.companyName] || [];
                    if (!companyItems.length) {
                      return <div style={{ ...card, color:"#d1d5db",fontSize:13,textAlign:"center",padding:"28px" }}>No schedule generated yet for this company — run the scheduler from the Scheduling tab.</div>;
                    }
                    const parseTime = t => {
                      const m = String(t).match(/(\d+):(\d+)\s*(AM|PM)/i);
                      if (!m) return 0;
                      let h = parseInt(m[1]); const mn = parseInt(m[2]);
                      if (m[3].toUpperCase()==="PM" && h!==12) h+=12;
                      if (m[3].toUpperCase()==="AM" && h===12) h=0;
                      return h*60+mn;
                    };
                    const configuredLabels = (programSettings?.boardWeek?.days||[]).map(d=>d.label);
                    const distinctDays = [...new Set(companyItems.map(i=>i.day))];
                    const orderedDays = distinctDays.sort((a,b)=>configuredLabels.indexOf(a)-configuredLabels.indexOf(b));
                    return orderedDays.map(day => {
                      const dayItems = companyItems.filter(i=>i.day===day);
                      const rooms = [...new Set(dayItems.map(i=>i.room))].sort((a,b)=>{
                        const na = parseInt(String(a).replace(/\D/g,""))||0;
                        const nb = parseInt(String(b).replace(/\D/g,""))||0;
                        return na-nb;
                      });
                      const times = [...new Set(dayItems.map(i=>i.time))].sort((a,b)=>parseTime(a)-parseTime(b));
                      return (
                        <div key={day} style={{ background:"white",borderRadius:14,marginBottom:16,overflow:"hidden",boxShadow:"0 1px 3px rgba(0,0,0,0.05)" }}>
                          <div style={{ background:`linear-gradient(135deg,${MAROON},#a00000)`,padding:"14px 20px",display:"flex",justifyContent:"space-between",alignItems:"center" }}>
                            <div style={{ color:"white",fontWeight:800,fontSize:15 }}>{day}</div>
                            <div style={{ color:"rgba(255,255,255,0.7)",fontSize:12 }}>{dayItems.length} interview{dayItems.length!==1?"s":""} · {rooms.length} room{rooms.length!==1?"s":""}</div>
                          </div>
                          <div style={{ overflowX:"auto" }}>
                            <table style={{ width:"100%",borderCollapse:"collapse",minWidth:400 }}>
                              <thead>
                                <tr style={{ background:"#f8f8f8",borderBottom:"2px solid #f0f0f0" }}>
                                  <th style={{ textAlign:"left",padding:"10px 16px",fontSize:11,fontWeight:700,color:"#9ca3af",textTransform:"uppercase",letterSpacing:0.5,whiteSpace:"nowrap",borderRight:"2px solid #f0f0f0" }}>Time</th>
                                  {rooms.map(room=>(
                                    <th key={room} style={{ textAlign:"left",padding:"10px 16px",fontSize:11,fontWeight:700,color:"#9ca3af",textTransform:"uppercase",letterSpacing:0.5,borderRight:"1px solid #f5f5f5" }}>Room {String(room).replace("Room ","")}</th>
                                  ))}
                                </tr>
                              </thead>
                              <tbody>
                                {times.map((time,ti)=>(
                                  <tr key={time} style={{ borderBottom:"1px solid #f0f0f0",background:ti%2===0?"white":"#fafcff" }}>
                                    <td style={{ padding:"12px 16px",fontWeight:800,color:MAROON,fontSize:13,whiteSpace:"nowrap",borderRight:"2px solid #f0f0f0" }}>{time}</td>
                                    {rooms.map(room=>{
                                      const item = dayItems.find(i=>i.time===time && i.room===room);
                                      return (
                                        <td key={room} style={{ padding:"10px 16px",fontSize:13,background:item?.preferred?"rgba(29,78,216,0.04)":undefined,borderRight:"1px solid #f5f5f5" }}>
                                          {item ? (
                                            <div>
                                              <div style={{ fontWeight:700,color:item.preferred?"#1d4ed8":"#1a1a1a",marginBottom:3,display:"flex",alignItems:"center",gap:5 }}>
                                                {item.preferred && <span style={{ color:"#1d4ed8",fontSize:12 }}>★</span>}
                                                {item.student}
                                              </div>
                                              <span style={{ fontSize:10,fontWeight:700,color:item.preferred?"#1d4ed8":"#64748b",background:item.preferred?"#eff6ff":"#f1f5f9",border:`1px solid ${item.preferred?"#93c5fd":"#e2e8f0"}`,padding:"1px 6px",borderRadius:4,letterSpacing:0.3 }}>{item.preferred?"★ PREFERRED":"RANDOM"}</span>
                                            </div>
                                          ) : <span style={{ color:"#e2e8f0",fontSize:16 }}>·</span>}
                                        </td>
                                      );
                                    })}
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          </div>
                        </div>
                      );
                    });
                  })()}
                </div>
              </div>
            ) : (
              <div style={card}>
                <table style={{ width:"100%",borderCollapse:"collapse",fontSize:13 }}>
                  <thead><tr style={{ borderBottom:"2px solid #f0f0f0" }}>{["Company","Contact","Type","Rooms","Preferences","Status",""].map(h=><th key={h} style={{ textAlign:"left",padding:"10px 12px",color:"#aaa",fontWeight:700,fontSize:11,textTransform:"uppercase",letterSpacing:0.5 }}>{h}</th>)}</tr></thead>
                  <tbody>
                    {filteredBoard.map(b=>(
                      <tr key={b.id} style={{ borderBottom:"1px solid #f8f8f8" }} onMouseOver={e=>e.currentTarget.style.background="#fafafa"} onMouseOut={e=>e.currentTarget.style.background="transparent"}>
                        <td style={{ padding:"13px 12px",fontWeight:700,color:"#1a1a1a" }}>{b.profile?.companyName || b.displayEmail || b.name || b.email?.replace('@trip.board','') || '—'}</td>
                        <td style={{ padding:"13px 12px",color:"#666",fontSize:13 }}>{b.profile?.mainContactFirst} {b.profile?.mainContactLast}</td>
                        <td style={{ padding:"13px 12px" }}><span style={{ fontSize:11,background:"#f5f5f5",color:"#555",padding:"3px 8px",borderRadius:6,fontWeight:600 }}>{b.profile?.memberType?.replace(" Board Member","")||"—"}</span></td>
                        <td style={{ padding:"13px 12px",fontSize:13,color:"#555" }}>{b.profile?.interviewRooms||"—"}</td>
                        <td style={{ padding:"13px 12px",minWidth:100 }}>
                          <div style={{ display:"flex",alignItems:"center",gap:6 }}>
                            <div style={{ flex:1,background:"#f0f0f0",borderRadius:3,height:5 }}><div style={{ background:MAROON,height:5,borderRadius:3,width:`${Math.min(100, Math.round(((b.preferences||[]).length/20)*100))}%` }} /></div>
                            <span style={{ fontSize:11,color:"#aaa",fontWeight:600 }}>{(b.preferences||[]).length}</span>
                          </div>
                        </td>
                        <td style={{ padding:"13px 12px" }}>{b.submitted?<span style={{ background:"#f0fdf4",color:"#166534",border:"1px solid #86efac",padding:"3px 10px",borderRadius:20,fontSize:11,fontWeight:700 }}>✓ Submitted</span>:<span style={{ background:"#fffbeb",color:"#92400e",border:"1px solid #fcd34d",padding:"3px 10px",borderRadius:20,fontSize:11,fontWeight:700 }}>Pending</span>}</td>
                        <td style={{ padding:"13px 12px" }}><button onClick={()=>setSelectedBoard(b)} style={{ background:"none",border:`1.5px solid ${MAROON}`,color:MAROON,padding:"5px 12px",borderRadius:6,fontSize:12,cursor:"pointer",fontWeight:600 }}>View →</button></td>
                      </tr>
                    ))}
                    {filteredBoard.length===0&&<tr><td colSpan={7} style={{ padding:32,textAlign:"center",color:"#d1d5db" }}>{boardSearch||boardTypeFilter!=="all"?"No companies match your filter":"No board member data yet"}</td></tr>}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}

        {activeTab === "users" && (
          <div style={{ animation:"fadeUp 0.4s ease both" }}>
            <div style={{ display:"flex",justifyContent:"space-between",alignItems:"flex-start",marginBottom:22 }}>
              <div><h1 style={{ fontSize:24,fontWeight:800,color:"#0f0f0f",marginBottom:4,letterSpacing:-0.5 }}>User Management</h1><p style={{ color:"#9ca3af",fontSize:14 }}>Create and manage all portal accounts</p></div>
            </div>
            {/* Delete by group */}
            <div style={{ ...card, border:"1px solid #fca5a5", marginBottom:18 }}>
              <div style={{ fontSize:13,fontWeight:700,color:"#991b1b",marginBottom:6 }}>Delete Students by Group</div>
              <p style={{ fontSize:13,color:"#6b7280",marginBottom:16,lineHeight:1.6 }}>
                Permanently removes all student accounts and data for a specific group. Use this each year to remove the graduating group. Cannot be undone.
              </p>
              <div style={{ display:"flex",gap:10 }}>
                {(programSettings.groups || [{value:"16"},{value:"17"},{value:"18"}]).map(grpDef => {
                  const g = grpDef.value;
                  const groupStudents = users.filter(u => {
                    const studentDoc = students.find(s => s.id === u.id);
                    return u.role === "student" && studentDoc?.group === g;
                  });
                  return (
                    <div key={g} style={{ flex:1,background:"#fafafa",borderRadius:10,padding:"14px 16px",border:"1px solid #f0f0f0",textAlign:"center" }}>
                      <div style={{ fontWeight:800,fontSize:20,color:"#991b1b",marginBottom:2 }}>{groupStudents.length}</div>
                      <div style={{ fontSize:12,color:"#555",fontWeight:600,marginBottom:4 }}>{grpDef.label || `Group ${g}`}</div>
                      <div style={{ fontSize:11,color:"#aaa",marginBottom:10 }}>{grpDef.description || ""}</div>
                      <button onClick={async () => {
                        if (!window.confirm(`Permanently delete all ${groupStudents.length} ${grpDef.label || "Group "+g} student accounts and data? This cannot be undone.`)) return;
                        const doubleCheck = window.prompt(`Type "DELETE GROUP ${g}" to confirm:`);
                        if (doubleCheck !== `DELETE GROUP ${g}`) { setMsg("Cancelled."); return; }
                        setMsg(`Deleting ${grpDef.label || "Group "+g} students...`);
                        let deleted = 0;
                        for (const u of groupStudents) {
                          try {
                            const token = await auth.currentUser.getIdToken();
                            await fetch(`${FUNCTIONS_BASE}/delete_user`, {
                              method:"POST",
                              headers:{"Content-Type":"application/json","Authorization":`Bearer ${token}`},
                              body:JSON.stringify({uid:u.id})
                            });
                            deleted++;
                          } catch(e) {}
                        }
                        setMsg(`Deleted ${deleted} ${grpDef.label || "Group "+g} student accounts.`);
                        loadAll();
                      }}
                        style={{ width:"100%",background:"none",border:"1.5px solid #fca5a5",color:"#dc2626",padding:"8px",borderRadius:8,fontSize:12,cursor:"pointer",fontWeight:700 }}>
                        Delete {grpDef.label || "Group "+g}
                      </button>
                    </div>
                  );
                })}
              </div>
            </div>

            {/* Bulk Import */}
            <BulkImportCard MAROON={MAROON} card={card} btn={btn} auth={auth} FUNCTIONS_BASE={FUNCTIONS_BASE} onDone={() => { loadUsers(); setMsg("Bulk import complete"); }} />

            <div style={card}>
              <div style={{ fontSize:11,fontWeight:700,color:"#9ca3af",textTransform:"uppercase",letterSpacing:0.6,marginBottom:18 }}>Create Single Account</div>
              <form onSubmit={createUser}>
                <div style={{ display:"grid",gridTemplateColumns:"1fr 1fr",gap:14,marginBottom:14 }}>
                  <div><label style={lbl}>Full Name{newUser.role === "board" ? " (optional)" : ""}</label><input value={newUser.name} onChange={e=>setNewUser(u=>({...u,name:e.target.value}))} style={inp("un")} required={newUser.role !== "board"} onFocus={()=>setFocused("un")} onBlur={()=>setFocused(null)} /></div>
                  <div><label style={lbl}>{newUser.role === "board" ? "Company Name (used to log in)" : "Email"}</label><input type={newUser.role === "board" ? "text" : "email"} value={newUser.email} onChange={e=>setNewUser(u=>({...u,email:e.target.value}))} placeholder={newUser.role === "board" ? "e.g. Exxon Mobil" : "user@tamu.edu"} style={inp("ue")} required onFocus={()=>setFocused("ue")} onBlur={()=>setFocused(null)} /></div>
                  <div><label style={lbl}>Password</label><input type="password" value={newUser.password} onChange={e=>setNewUser(u=>({...u,password:e.target.value}))} style={inp("up")} required minLength={6} onFocus={()=>setFocused("up")} onBlur={()=>setFocused(null)} /></div>
                  <div><label style={lbl}>Role</label><select value={newUser.role} onChange={e=>setNewUser(u=>({...u,role:e.target.value,group:"",member_type:""}))} style={inp("ur")} onFocus={()=>setFocused("ur")} onBlur={()=>setFocused(null)}><option value="student">Student</option><option value="board">Board Member</option><option value="admin">Admin</option></select></div>
                  {newUser.role === "student" && (
                    <div><label style={lbl}>Group</label>
                      <select value={newUser.group||""} onChange={e=>setNewUser(u=>({...u,group:e.target.value}))} style={inp("ug")} onFocus={()=>setFocused("ug")} onBlur={()=>setFocused(null)}>
                        <option value="">Select group...</option>
                        {(programSettings.groups||[{value:"16",label:"Group 16"},{value:"17",label:"Group 17"},{value:"18",label:"Group 18"}]).map(g=>(
                          <option key={g.value} value={g.value}>{g.label} — {g.description||""}</option>
                        ))}
                      </select>
                    </div>
                  )}
                  {newUser.role === "board" && (
                    <div><label style={lbl}>Member Type</label>
                      <select value={newUser.member_type||""} onChange={e=>setNewUser(u=>({...u,member_type:e.target.value}))} style={inp("umt")} onFocus={()=>setFocused("umt")} onBlur={()=>setFocused(null)}>
                        <option value="">Select type...</option>
                        <option value="platinum">Platinum</option>
                        <option value="gold">Gold</option>
                        <option value="silver">Silver</option>
                      </select>
                    </div>
                  )}
                </div>
                <button type="submit" disabled={creating} style={btn}>{creating?<><span style={{ display:"inline-block",width:14,height:14,border:"2px solid rgba(255,255,255,0.3)",borderTopColor:"white",borderRadius:"50%",animation:"spin 0.7s linear infinite" }} />Creating...</>:"Create Account →"}</button>
              </form>
            </div>
            <div style={card}>
              <div style={{ fontSize:11,fontWeight:700,color:"#9ca3af",textTransform:"uppercase",letterSpacing:0.6,marginBottom:18 }}>All Users ({users.length})</div>
              <table style={{ width:"100%",borderCollapse:"collapse",fontSize:13 }}>
                <thead><tr style={{ borderBottom:"2px solid #f0f0f0" }}>{["Name","Email","Role","Created",""].map(h=><th key={h} style={{ textAlign:"left",padding:"10px 12px",color:"#aaa",fontWeight:700,fontSize:11,textTransform:"uppercase",letterSpacing:0.5 }}>{h}</th>)}</tr></thead>
                <tbody>
                  {users.map(u=>(
                    <tr key={u.id} style={{ borderBottom:"1px solid #f8f8f8" }} onMouseOver={e=>e.currentTarget.style.background="#fafafa"} onMouseOut={e=>e.currentTarget.style.background="transparent"}>
                      <td style={{ padding:"12px 12px",fontWeight:700,color:"#1a1a1a" }}>{u.name||"—"}</td>
                      <td style={{ padding:"12px 12px",color:"#666" }}>{u.role === "board" ? (u.displayEmail || u.email.replace("@trip.board","")) : u.email}</td>
                      <td style={{ padding:"12px 12px" }}><span style={{ background:u.role==="admin"?"#fff1f0":u.role==="board"?"#eff6ff":"#f0fdf4",color:u.role==="admin"?MAROON:u.role==="board"?"#1d4ed8":"#166534",border:`1px solid ${u.role==="admin"?"#fca5a5":u.role==="board"?"#93c5fd":"#86efac"}`,padding:"3px 10px",borderRadius:20,fontSize:11,fontWeight:700 }}>{u.role}</span></td>
                      <td style={{ padding:"12px 12px",color:"#bbb",fontSize:12 }}>{u.createdAt?new Date(u.createdAt).toLocaleDateString():"—"}</td>
                      <td style={{ padding:"12px 12px" }}><button onClick={()=>deleteUser(u.id, u.email)} style={{ background:"none",border:"1px solid #fca5a5",color:"#dc2626",padding:"4px 10px",borderRadius:6,fontSize:11,cursor:"pointer" }}>Remove</button></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {activeTab === "resumebooks" && (
          <div style={{ animation:"fadeUp 0.4s ease both" }}>
            <div style={{ marginBottom:22 }}><h1 style={{ fontSize:24,fontWeight:800,color:"#0f0f0f",marginBottom:4,letterSpacing:-0.5 }}>Resume Books</h1><p style={{ color:"#9ca3af",fontSize:14 }}>Generate PDF resume books. Role interest scores compiled as page 1 of each book.</p></div>
            <div style={{ display:"grid",gridTemplateColumns:"repeat(3,1fr)",gap:14,marginBottom:18 }}>
              {(programSettings.groups || [{value:"16"},{value:"17"},{value:"18"}]).map(grpDef=>{
                const g=grpDef.value;
                const grp=students.filter(s=>s.group===g); const ready=grp.filter(s=>s.resumeURL&&s.photoURL).length; const book=resumeBooks.find(b=>b.group===g);
                return (
                  <div key={g} style={{ ...card,padding:"20px 22px",marginBottom:0 }}>
                    <div style={{ display:"flex",justifyContent:"space-between",marginBottom:12 }}>
                      <div><div style={{ fontWeight:800,fontSize:15,color:"#1a1a1a" }}>Group {g}</div><div style={{ fontSize:12,color:"#aaa",marginTop:2 }}>{grp.length} students · {ready} ready</div></div>
                      {book&&<span style={{ background:"#f0fdf4",color:"#166534",border:"1px solid #86efac",padding:"3px 8px",borderRadius:20,fontSize:11,fontWeight:700 }}>Generated</span>}
                    </div>
                    <MiniBar pct={grp.length>0?Math.round((ready/grp.length)*100):0} />
                    <div style={{ display:"flex",gap:8,marginTop:14 }}>
                      <button onClick={()=>handleGenerateBooks(g)} disabled={generating} style={{ flex:1,...btn,padding:"9px 12px",fontSize:12,justifyContent:"center" }}>Generate</button>
                      {book&&<a href={book.url+"?t="+Date.now()} target="_blank" rel="noreferrer" style={{ flex:1,background:"#f5f5f5",color:"#333",padding:"9px 12px",borderRadius:8,fontSize:12,textDecoration:"none",fontWeight:600,textAlign:"center" }}>Download</a>}
                    </div>
                  </div>
                );
              })}
            </div>
            <div style={card}>
              <div style={{ fontSize:11,fontWeight:700,color:"#9ca3af",textTransform:"uppercase",letterSpacing:0.6,marginBottom:8 }}>Generate All Groups</div>
              <p style={{ color:"#9ca3af",fontSize:13,marginBottom:18,lineHeight:1.6 }}>Compiles all profiles into PDFs. Role interest summary on page 1. May take 60 seconds.</p>
              {generating&&<div style={{ background:"#f8f8f8",borderRadius:10,padding:"14px 18px",marginBottom:18,display:"flex",alignItems:"center",gap:10 }}><span style={{ display:"inline-block",width:14,height:14,border:"2px solid rgba(111,0,0,0.3)",borderTopColor:MAROON,borderRadius:"50%",animation:"spin 0.7s linear infinite" }} /><span style={{ fontSize:13,fontWeight:600,color:"#555" }}>Generating...</span></div>}
              <div style={{ display:"flex",gap:12,alignItems:"center",flexWrap:"wrap" }}>
                <button onClick={()=>handleGenerateBooks()} disabled={generating} style={btn}>{generating?"Generating...":"Generate All Resume Books →"}</button>
                {resumeBooks.length > 0 && (
                  <div style={{ display:"flex", gap:10, alignItems:"center" }}>
                    {resumeBooksPublished && <div style={{ background:"#f0fdf4", border:"1px solid #86efac", color:"#166534", padding:"11px 20px", borderRadius:10, fontWeight:700, fontSize:14 }}>✓ Resume Books Published</div>}
                    <button onClick={async () => {
                      const next = !resumeBooksPublished;
                      await setDoc(doc(db, "metadata", "publishStatus"), { resumeBooks: next, schedule: schedulePublished }, { merge: true });
                      setResumeBooksPublished(next);
                      setMsg(next ? "✓ Resume books published — board members can now view them" : "Resume books unpublished"); logActivity(next ? "Published Resume Books" : "Unpublished Resume Books");
                    }} style={{ padding:"11px 24px",borderRadius:10,background:resumeBooksPublished?"white":"linear-gradient(135deg,#6f0000,#a00000)",color:resumeBooksPublished?"#dc2626":"white",border:resumeBooksPublished?"1.5px solid #fca5a5":"none",fontWeight:700,fontSize:14,cursor:"pointer" }}>
                      {resumeBooksPublished ? "Unpublish" : "Publish to Board Members →"}
                    </button>
                  </div>
                )}
              </div>
            </div>
          </div>
        )}

        {activeTab === "scheduling" && (
          <div style={{ animation:"fadeUp 0.4s ease both" }}>
            <div style={{ marginBottom:22 }}><h1 style={{ fontSize:24,fontWeight:800,color:"#0f0f0f",marginBottom:4,letterSpacing:-0.5 }}>Interview Scheduling</h1><p style={{ color:"#9ca3af",fontSize:14 }}>Configure and run the interview scheduler</p></div>
            <div style={{ ...card,marginBottom:18 }}>
              <div style={{ fontSize:11,fontWeight:700,color:"#9ca3af",textTransform:"uppercase",letterSpacing:0.6,marginBottom:10 }}>Preference Locks</div>
              <p style={{ fontSize:13,color:"#555",marginBottom:16,lineHeight:1.6 }}>Lock each tier's candidate preferences independently once its round wraps up. Locking Gold or Silver also notifies companies in that tier whose preferred students were already drafted in an earlier round.</p>
              <div style={{ display:"grid",gridTemplateColumns:"repeat(3,1fr)",gap:12,marginBottom:16 }}>
                {[
                  { tier:"platinum", label:"Platinum", locked:platinumPrefsLocked },
                  { tier:"gold", label:"Gold", locked:goldPrefsLocked },
                  { tier:"silver", label:"Silver", locked:silverPrefsLocked },
                ].map(t => (
                  <div key={t.tier} style={{ background:t.locked?"#fff1f0":"#f8f8f8",border:`1px solid ${t.locked?"#fca5a5":TIER_COLORS[t.tier]+"40"}`,borderRadius:10,padding:"14px 16px" }}>
                    <div style={{ display:"flex",alignItems:"center",justifyContent:"space-between",marginBottom:10 }}>
                      <div style={{ display:"flex",alignItems:"center",gap:7,fontWeight:800,fontSize:14,color:t.locked?"#991b1b":"#333" }}>
                        <span style={{ width:8,height:8,borderRadius:"50%",background:TIER_COLORS[t.tier],flexShrink:0 }} />
                        {t.label}
                      </div>
                      {t.locked && <span style={{ fontSize:11,fontWeight:700,color:"#dc2626" }}>🔒 Locked</span>}
                    </div>
                    <button onClick={() => setTierLock(t.tier, !t.locked)} style={{ width:"100%",padding:"9px 8px",borderRadius:8,background:t.locked?"white":`linear-gradient(135deg,${MAROON},#a00000)`,color:t.locked?"#dc2626":"white",border:t.locked?"1.5px solid #fca5a5":"none",fontWeight:700,fontSize:12,cursor:"pointer" }}>
                      {t.locked ? "Unlock" : "Lock →"}
                    </button>
                  </div>
                ))}
              </div>
              <div style={{ display:"flex",alignItems:"center",justifyContent:"space-between",borderTop:"1px solid #f0f0f0",paddingTop:14 }}>
                <div style={{ fontSize:12,color:"#9ca3af" }}>Locks Platinum, Gold, and Silver all at once — for the final submission deadline.</div>
                <button onClick={() => setAllTierLocks(!(platinumPrefsLocked && goldPrefsLocked && silverPrefsLocked))} style={{ padding:"8px 16px",borderRadius:8,background:"white",color:MAROON,border:`1.5px solid ${MAROON}`,fontWeight:700,fontSize:12,cursor:"pointer",flexShrink:0,marginLeft:16 }}>
                  {(platinumPrefsLocked && goldPrefsLocked && silverPrefsLocked) ? "Unlock All" : "Lock All →"}
                </button>
              </div>
            </div>
            <div style={{ ...card,border:`1px solid ${readinessPct===100?"#86efac":readinessPct>=60?"#fcd34d":"#fca5a5"}`,marginBottom:18 }}>
              <div style={{ fontSize:11,fontWeight:700,color:"#9ca3af",textTransform:"uppercase",letterSpacing:0.6,marginBottom:14 }}>Pre-Scheduler Validation</div>
              <div style={{ display:"grid",gridTemplateColumns:"repeat(3,1fr)",gap:12 }}>

                {[{label:"Students ready",value:students.length,ok:students.length>0,detail:`${studentsWithResume} with resume`},{label:"Board prefs in",value:`${submittedBoard}/${boardMembers.length}`,ok:submittedBoard>0,detail:submittedBoard===boardMembers.length?"All submitted":`${boardMembers.length-submittedBoard} pending`},{label:"Rooms configured",value:totalRooms,ok:totalRooms>0,detail:`~${estimatedSlots} slots`}].map(item=>(
                  <div key={item.label} style={{ background:item.ok?"#f0fdf4":"#fff1f0",borderRadius:10,padding:"14px 16px",border:`1px solid ${item.ok?"#86efac":"#fca5a5"}` }}>
                    <div style={{ fontSize:10,fontWeight:700,color:item.ok?"#166534":"#991b1b",textTransform:"uppercase",letterSpacing:0.5,marginBottom:5 }}>{item.label}</div>
                    <div style={{ fontSize:22,fontWeight:900,color:item.ok?"#166534":"#991b1b",marginBottom:3 }}>{item.value}</div>
                    <div style={{ fontSize:11,color:item.ok?"#166534":"#991b1b",opacity:0.8 }}>{item.detail}</div>
                  </div>
                ))}
              </div>
            </div>
            <div style={{ display:"grid",gridTemplateColumns:"1fr 1fr",gap:18,marginBottom:18 }}>
              <div style={card}>
                <div style={{ fontSize:11,fontWeight:700,color:"#9ca3af",textTransform:"uppercase",letterSpacing:0.6,marginBottom:16 }}>Configuration</div>
                <div style={{ marginBottom:16 }}>
                  <label style={lbl}>Round Type</label>
                  <div style={{ display:"flex",gap:8 }}>
                    {[["platinum","Platinum","2 days"],["goldsilver","Gold + Silver","1 day"]].map(([val,lbl2,sub])=>(
                      <button key={val} type="button" onClick={()=>setRoundType(val)} style={{ flex:1,padding:"11px 8px",border:`2px solid ${roundType===val?TIER_COLORS[val]:"#ebebeb"}`,borderRadius:8,background:roundType===val?`${TIER_COLORS[val]}12`:"white",cursor:"pointer",transition:"all 0.15s" }}>
                        <div style={{ fontWeight:700,fontSize:13,color:roundType===val?TIER_COLORS[val]:"#333" }}>{lbl2}</div>
                        <div style={{ fontSize:11,color:"#aaa",marginTop:2 }}>{sub}</div>
                      </button>
                    ))}
                  </div>
                </div>
              </div>
              <div style={{ ...card,display:"flex",flexDirection:"column",justifyContent:"space-between" }}>
                <div>
                  <div style={{ fontSize:11,fontWeight:700,color:"#9ca3af",textTransform:"uppercase",letterSpacing:0.6,marginBottom:12 }}>Run Scheduler</div>
                  <div style={{ fontSize:13,color:"#9ca3af",lineHeight:1.7,marginBottom:16 }}>Uses board member preferences and room configuration to generate an optimized interview schedule.</div>
                  <div style={{ display:"flex",gap:8,marginBottom:14 }}>
                    {[["Platinum",platinumPrefsLocked],["Gold",goldPrefsLocked],["Silver",silverPrefsLocked]].map(([label,locked])=>(
                      <div key={label} style={{ flex:1,background:locked?"#fff1f0":"#f0fdf4",border:`1px solid ${locked?"#fca5a5":"#86efac"}`,borderRadius:8,padding:"8px 10px",textAlign:"center" }}>
                        <div style={{ fontSize:11,fontWeight:700,color:locked?"#991b1b":"#166534" }}>{label}</div>
                        <div style={{ fontSize:11,color:locked?"#991b1b":"#166534",opacity:0.8 }}>{locked?"Locked":"Open"}</div>
                      </div>
                    ))}
                  </div>
                  <div style={{ background:"#f8f8f8",borderRadius:8,padding:"12px 14px",fontSize:13,color:"#555",marginBottom:16 }}><strong>{submittedBoard}</strong> compan{submittedBoard!==1?"ies":"y"} · <strong>{students.length}</strong> students · <strong>{totalRooms}</strong> rooms</div>
                </div>
                {scheduling?(
                  <div style={{ background:"#f8f8f8",borderRadius:10,padding:"16px" }}>
                    {["Collecting preferences...","Matching students...","Resolving conflicts...","Assigning time slots..."].map((step,i)=>(
                      <div key={step} style={{ display:"flex",alignItems:"center",gap:8,padding:"5px 0",fontSize:13,color:"#555" }}><div style={{ width:6,height:6,borderRadius:"50%",background:i===0?MAROON:"#e0e0e0" }} />{step}</div>
                    ))}
                  </div>
                ):(
                  <div style={{ display:"flex",gap:12,flexWrap:"wrap" }}>
                    <button onClick={handleRunScheduler} style={btn} onMouseOver={e=>e.currentTarget.style.transform="translateY(-1px)"} onMouseOut={e=>e.currentTarget.style.transform="translateY(0)"}>Run Scheduler →</button>
                    {lastSchedule && (
                      <div style={{ display:"flex",flexDirection:"column",gap:10,marginTop:4 }}>
                        <div style={{ display:"flex",gap:8,alignItems:"center" }}>
                          <div style={{ fontSize:12,fontWeight:700,color:"#9ca3af",width:140 }}>To Students:</div>
                          {schedulePublishedStudents && <div style={{ background:"#f0fdf4",border:"1px solid #86efac",color:"#166534",padding:"7px 14px",borderRadius:8,fontWeight:700,fontSize:12 }}>✓ Published</div>}
                          <button onClick={async () => {
                            const next = !schedulePublishedStudents;
                            await setDoc(doc(db, "metadata", "publishStatus"), { resumeBooks: resumeBooksPublished, schedule: schedulePublished, scheduleStudents: next, scheduleBoard: schedulePublishedBoard, submissionsLocked }, { merge: true });
                            setSchedulePublishedStudents(next);
                            setMsg(next ? "Schedule published to students" : "Student schedule unpublished"); logActivity(next ? "Published Schedule (Students)" : "Unpublished Schedule (Students)");
                          }} style={{ padding:"8px 18px",borderRadius:8,background:schedulePublishedStudents?"white":"linear-gradient(135deg,#6f0000,#a00000)",color:schedulePublishedStudents?"#dc2626":"white",border:schedulePublishedStudents?"1.5px solid #fca5a5":"none",fontWeight:700,fontSize:12,cursor:"pointer" }}>
                            {schedulePublishedStudents ? "Unpublish" : "Publish to Students →"}
                          </button>
                        </div>
                        <div style={{ display:"flex",gap:8,alignItems:"center" }}>
                          <div style={{ fontSize:12,fontWeight:700,color:"#9ca3af",width:140 }}>To Board Members:</div>
                          {schedulePublishedBoard && <div style={{ background:"#f0fdf4",border:"1px solid #86efac",color:"#166534",padding:"7px 14px",borderRadius:8,fontWeight:700,fontSize:12 }}>✓ Published</div>}
                          <button onClick={async () => {
                            const next = !schedulePublishedBoard;
                            await setDoc(doc(db, "metadata", "publishStatus"), { resumeBooks: resumeBooksPublished, schedule: schedulePublished, scheduleStudents: schedulePublishedStudents, scheduleBoard: next, submissionsLocked }, { merge: true });
                            setSchedulePublishedBoard(next);
                            setMsg(next ? "Schedule published to board members" : "Board schedule unpublished"); logActivity(next ? "Published Schedule (Board)" : "Unpublished Schedule (Board)");
                          }} style={{ padding:"8px 18px",borderRadius:8,background:schedulePublishedBoard?"white":"linear-gradient(135deg,#6f0000,#a00000)",color:schedulePublishedBoard?"#dc2626":"white",border:schedulePublishedBoard?"1.5px solid #fca5a5":"none",fontWeight:700,fontSize:12,cursor:"pointer" }}>
                            {schedulePublishedBoard ? "Unpublish" : "Publish to Board →"}
                          </button>
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>
            {lastSchedule&&(
              <div style={card}>
                <div style={{ display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:18 }}>
                  <div><div style={{ fontSize:11,fontWeight:700,color:"#9ca3af",textTransform:"uppercase",letterSpacing:0.6 }}>Last Schedule</div><div style={{ fontSize:12,color:"#aaa",marginTop:3 }}>Generated {new Date(lastSchedule.generatedAt).toLocaleString()} · Round: {lastSchedule.round}</div></div>
                  <a href={lastSchedule.url+"?t="+Date.now()} target="_blank" rel="noreferrer" style={{ background:"white",border:"1.5px solid #e8e8e8",color:"#333",padding:"8px 16px",borderRadius:8,fontSize:12,textDecoration:"none",fontWeight:600 }}>Download Excel →</a>
                </div>
                <div style={{ display:"grid",gridTemplateColumns:"repeat(3,1fr)",gap:12 }}>
                  {[{label:"Students",value:students.length,color:MAROON},{label:"Companies",value:submittedBoard,color:"#1d4ed8"},{label:"Round",value:lastSchedule.round?lastSchedule.round.charAt(0).toUpperCase()+lastSchedule.round.slice(1):"—",color:"#0f766e"}].map(item=>(
                    <div key={item.label} style={{ background:"#f8f8f8",borderRadius:10,padding:"14px 16px",borderLeft:`3px solid ${item.color}` }}><div style={{ fontSize:22,fontWeight:900,color:item.color,marginBottom:4 }}>{item.value}</div><div style={{ fontSize:12,color:"#888" }}>{item.label}</div></div>
                  ))}
                </div>
              </div>
            )}
            {scheduleHistory.length>0&&(
              <div style={card}>
                <div style={{ fontSize:11,fontWeight:700,color:"#9ca3af",textTransform:"uppercase",letterSpacing:0.6,marginBottom:14 }}>Run History</div>
                <table style={{ width:"100%",borderCollapse:"collapse",fontSize:13 }}>
                  <thead><tr style={{ borderBottom:"2px solid #f0f0f0" }}>{["Date","Round","Students","Companies",""].map(h=><th key={h} style={{ textAlign:"left",padding:"8px 12px",color:"#aaa",fontWeight:700,fontSize:11,textTransform:"uppercase",letterSpacing:0.5 }}>{h}</th>)}</tr></thead>
                  <tbody>
                    {scheduleHistory.map((run,i)=>(
                      <tr key={i} style={{ borderBottom:"1px solid #f8f8f8" }}>
                        <td style={{ padding:"10px 12px",color:"#555" }}>{new Date(run.generatedAt).toLocaleString()}</td>
                        <td style={{ padding:"10px 12px" }}><span style={{ background:"#f5f5f5",color:"#555",padding:"3px 8px",borderRadius:6,fontSize:11,fontWeight:600,textTransform:"capitalize" }}>{run.round}</span></td>
                        <td style={{ padding:"10px 12px",color:"#555" }}>{run.students}</td>
                        <td style={{ padding:"10px 12px",color:"#555" }}>{run.companies}</td>
                        <td style={{ padding:"10px 12px" }}><a href={run.url+"?t="+Date.now()} target="_blank" rel="noreferrer" style={{ fontSize:12,color:MAROON,fontWeight:600,textDecoration:"none" }}>Download</a></td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}

        {activeTab === "presentations" && (
          <PresentationsTab students={students} card={card} btn={btn} MAROON={MAROON} settings={programSettings}
            db={db}
            presentationsPublished={presentationsPublished}
            presentationsPublishedBoard={presentationsPublishedBoard}
            onPublishStudents={async (val) => {
              await setDoc(doc(db,"metadata","publishStatus"),{presentations:val},{merge:true});
              setPresentationsPublished(val);
              setMsg(val?"Presentations published to students":"Presentations unpublished from students");
            }}
            onPublishBoard={async (val) => {
              await setDoc(doc(db,"metadata","publishStatus"),{presentationsBoard:val},{merge:true});
              setPresentationsPublishedBoard(val);
              setMsg(val?"Presentations published to board members":"Presentations unpublished from board members");
            }}
          />
        )}

        {activeTab === "announcements" && (
          <div style={{ animation:"fadeUp 0.4s ease both" }}>
            <div style={{ marginBottom:22 }}><h1 style={{ fontSize:24,fontWeight:800,color:"#0f0f0f",marginBottom:4,letterSpacing:-0.5 }}>Announcements</h1><p style={{ color:"#9ca3af",fontSize:14 }}>Post announcements to students, board members, or the landing page</p></div>
            <div style={{ ...card, marginBottom:18 }}>
              <div style={{ fontSize:12,fontWeight:700,color:"#9ca3af",textTransform:"uppercase",letterSpacing:0.6,marginBottom:18 }}>New Announcement</div>
              <div style={{ display:"grid",gridTemplateColumns:"1fr 1fr",gap:12,marginBottom:12 }}>
                <div style={{ gridColumn:"1/-1" }}>
                  <label style={lbl}>Title</label>
                  <input value={newAnnouncement.title} onChange={e=>setNewAnnouncement(a=>({...a,title:e.target.value}))} placeholder="e.g. Schedule is now live!" style={inp("ant")} onFocus={()=>setFocused("ant")} onBlur={()=>setFocused(null)} />
                </div>
                <div style={{ gridColumn:"1/-1" }}>
                  <label style={lbl}>Message</label>
                  <textarea value={newAnnouncement.body} onChange={e=>setNewAnnouncement(a=>({...a,body:e.target.value}))} rows={3} placeholder="Write your announcement here..." style={{ ...inp("anb"), resize:"vertical" }} onFocus={()=>setFocused("anb")} onBlur={()=>setFocused(null)} />
                </div>
              </div>
              <div style={{ marginBottom:14 }}>
                <label style={lbl}>Show To</label>
                <div style={{ display:"flex",gap:8 }}>
                  {[["students","👨‍🎓 Students"],["board","🏢 Board Members"],["landing","🌐 Landing Page"],["all","📣 Everyone"]].map(([val,label])=>(
                    <div key={val} onClick={()=>setNewAnnouncement(a=>({...a,target:val}))} style={{ flex:1,padding:"10px 6px",borderRadius:10,border:`2px solid ${newAnnouncement.target===val?MAROON:"#e8e8e8"}`,background:newAnnouncement.target===val?"#fff0f0":"#fafafa",cursor:"pointer",textAlign:"center",fontSize:12,fontWeight:newAnnouncement.target===val?700:500,color:newAnnouncement.target===val?MAROON:"#777",transition:"all 0.15s" }}>{label}</div>
                  ))}
                </div>
              </div>
              <div style={{ marginBottom:16 }}>
                <label style={lbl}>Expiry</label>
                <select value={expiryPreset} onChange={e => {
                  const preset = e.target.value;
                  setExpiryPreset(preset);
                  const now = Date.now();
                  if (preset === "never") {
                    setNewAnnouncement(a => ({...a, expiresAt: ""}));
                  } else if (preset === "5min") {
                    setNewAnnouncement(a => ({...a, expiresAt: new Date(now + 5*60*1000).toISOString()}));
                  } else if (preset === "1hour") {
                    setNewAnnouncement(a => ({...a, expiresAt: new Date(now + 60*60*1000).toISOString()}));
                  } else if (preset === "6hours") {
                    setNewAnnouncement(a => ({...a, expiresAt: new Date(now + 6*60*60*1000).toISOString()}));
                  } else if (preset === "tomorrow") {
                    const d = new Date(); d.setDate(d.getDate() + 1); d.setHours(23, 59, 59, 0);
                    setNewAnnouncement(a => ({...a, expiresAt: d.toISOString()}));
                  } else if (preset === "3days") {
                    const d = new Date(); d.setDate(d.getDate() + 3); d.setHours(23, 59, 59, 0);
                    setNewAnnouncement(a => ({...a, expiresAt: d.toISOString()}));
                  } else if (preset === "1week") {
                    const d = new Date(); d.setDate(d.getDate() + 7); d.setHours(23, 59, 59, 0);
                    setNewAnnouncement(a => ({...a, expiresAt: d.toISOString()}));
                  } else if (preset === "custom") {
                    setNewAnnouncement(a => ({...a, expiresAt: ""}));
                  }
                }} style={{ ...inp("expiry"), fontSize:13, appearance:"none", backgroundImage:`url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='12' viewBox='0 0 12 12'%3E%3Cpath fill='%239ca3af' d='M6 8L1 3h10z'/%3E%3C/svg%3E")`, backgroundRepeat:"no-repeat", backgroundPosition:"calc(100% - 12px) center", paddingRight:36, cursor:"pointer" }} onFocus={()=>setFocused("expiry")} onBlur={()=>setFocused(null)}>
                  <option value="never">Never (show until deactivated)</option>
                  <option value="5min">5 minutes</option>
                  <option value="1hour">1 hour</option>
                  <option value="6hours">6 hours</option>
                  <option value="tomorrow">Tomorrow</option>
                  <option value="3days">3 days</option>
                  <option value="1week">1 week</option>
                  <option value="custom">Pick a custom time / date</option>
                </select>
                {expiryPreset === "custom" && (
                  <input type="datetime-local" value={newAnnouncement.expiresAt ? new Date(newAnnouncement.expiresAt).toLocaleString("sv-SE",{timeZone:Intl.DateTimeFormat().resolvedOptions().timeZone}).slice(0,16) : ""} onChange={e => setNewAnnouncement(a => ({...a, expiresAt: e.target.value ? new Date(e.target.value).toISOString() : ""}))} style={{ ...inp("ane"), fontSize:13, marginTop:8 }} onFocus={()=>setFocused("ane")} onBlur={()=>setFocused(null)} />
                )}
                <div style={{ fontSize:11, color: newAnnouncement.expiresAt ? "#6b7280" : "#9ca3af", marginTop:6, fontWeight:500 }}>
                  {newAnnouncement.expiresAt
                    ? "Expires " + new Date(newAnnouncement.expiresAt).toLocaleString("en-US",{month:"long",day:"numeric",year:"numeric",hour:"numeric",minute:"2-digit"})
                    : "No expiry set."}
                </div>
              </div>
              <button onClick={async () => {
                if (!newAnnouncement.title || !newAnnouncement.body) { alert("Please fill in title and message"); return; }
                await addDoc(collection(db, "announcements"), { ...newAnnouncement, createdAt: new Date().toISOString(), active: true });
                setNewAnnouncement({ title: "", body: "", target: "students", active: true, expiresAt: "" });
                setExpiryPreset("never");
                loadAnnouncements();
                setMsg("Announcement posted!");
              }} style={btn}>Post Announcement →</button>
            </div>
            <div style={{ fontSize:12,fontWeight:700,color:"#9ca3af",textTransform:"uppercase",letterSpacing:0.6,marginBottom:12 }}>All Announcements</div>
            {announcements.length === 0 && <div style={{ ...card, textAlign:"center", color:"#d1d5db", padding:32 }}>No announcements yet</div>}
            {announcements.map(ann => {
              const targetMeta = {
                students: { label:"👨‍🎓 Students", bg:"#eff6ff", color:"#1d4ed8", border:"#bfdbfe" },
                board:    { label:"🏢 Board",     bg:"#f5f3ff", color:"#6d28d9", border:"#ddd6fe" },
                landing:  { label:"🌐 Landing",   bg:"#f0fdf4", color:"#166534", border:"#bbf7d0" },
                all:      { label:"📣 Everyone",  bg:"#fff7ed", color:"#c2410c", border:"#fed7aa" },
              }[ann.target] || { label: ann.target, bg:"#f3f4f6", color:"#374151", border:"#d1d5db" };
              const isExpired = ann.expiresAt && new Date(ann.expiresAt) < new Date();
              return (
                <div key={ann.id} style={{ ...card, marginBottom:12, opacity: ann.active && !isExpired ? 1 : 0.55, border:`1px solid ${ann.active && !isExpired ? "#e8e8e8" : "#f0f0f0"}` }}>
                  <div style={{ display:"flex",justifyContent:"space-between",alignItems:"flex-start",marginBottom:10 }}>
                    <div style={{ flex:1, minWidth:0 }}>
                      <div style={{ display:"flex",alignItems:"center",gap:10,marginBottom:6,flexWrap:"wrap" }}>
                        <div style={{ fontWeight:700,fontSize:15,color:"#0f0f0f" }}>{ann.title}</div>
                        <span style={{ fontSize:11,fontWeight:700,padding:"2px 8px",borderRadius:20,background:targetMeta.bg,color:targetMeta.color,border:`1px solid ${targetMeta.border}`,whiteSpace:"nowrap" }}>{targetMeta.label}</span>
                        <span style={{ fontSize:11,fontWeight:700,padding:"2px 8px",borderRadius:20,background: ann.active && !isExpired ? "#f0fdf4" : "#fef2f2", color: ann.active && !isExpired ? "#166534" : "#dc2626", border: `1px solid ${ann.active && !isExpired ? "#bbf7d0" : "#fca5a5"}` }}>
                          {!ann.active ? "Inactive" : isExpired ? "Expired" : "Active"}
                        </span>
                      </div>
                      <div style={{ display:"flex",gap:16,flexWrap:"wrap" }}>
                        <span style={{ fontSize:11,color:"#9ca3af" }}>Created {new Date(ann.createdAt).toLocaleDateString("en-US",{month:"short",day:"numeric",year:"numeric"})}</span>
                        {ann.expiresAt
                          ? <span style={{ fontSize:11, color: isExpired ? "#dc2626" : "#9ca3af" }}>
                              {isExpired ? "Expired" : "Expires"} {new Date(ann.expiresAt).toLocaleDateString("en-US",{month:"short",day:"numeric",year:"numeric"})}
                            </span>
                          : <span style={{ fontSize:11,color:"#9ca3af" }}>No expiry</span>
                        }
                      </div>
                    </div>
                    <div style={{ display:"flex",gap:8,flexShrink:0,marginLeft:12 }}>
                      <button onClick={() => setEditingAnn({ ...ann })} style={{ background:"none",border:"1px solid #e8e8e8",color:"#555",padding:"5px 12px",borderRadius:7,fontSize:12,cursor:"pointer",fontWeight:600 }}>Edit</button>
                      <button onClick={async () => { await setDoc(doc(db,"announcements",ann.id),{...ann,active:!ann.active}); loadAnnouncements(); }} style={{ background:"none",border:"1px solid #e8e8e8",color:"#555",padding:"5px 12px",borderRadius:7,fontSize:12,cursor:"pointer",fontWeight:600 }}>{ann.active?"Deactivate":"Activate"}</button>
                      <button onClick={async () => { if(window.confirm("Delete this announcement?")) { await deleteDoc(doc(db,"announcements",ann.id)); loadAnnouncements(); }}} style={{ background:"none",border:"1px solid #fca5a5",color:"#dc2626",padding:"5px 12px",borderRadius:7,fontSize:12,cursor:"pointer",fontWeight:600 }}>Delete</button>
                    </div>
                  </div>
                  <div style={{ fontSize:13,color:"#555",lineHeight:1.6,borderTop:"1px solid #f3f4f6",paddingTop:10 }}>{ann.body}</div>
                  {editingAnn?.id === ann.id && (
                    <div style={{ marginTop:14, borderTop:"1px solid #f0f0f0", paddingTop:14, display:"grid", gridTemplateColumns:"1fr 1fr", gap:10 }}>
                      <div style={{ gridColumn:"1/-1" }}>
                        <label style={{ fontSize:11,fontWeight:700,color:"#9ca3af",textTransform:"uppercase",letterSpacing:0.6,display:"block",marginBottom:6 }}>Title</label>
                        <input value={editingAnn.title} onChange={e => setEditingAnn(a => ({...a,title:e.target.value}))} style={{ width:"100%",padding:"9px 12px",border:"1.5px solid #e8e8e8",borderRadius:8,fontSize:13,boxSizing:"border-box" }} />
                      </div>
                      <div style={{ gridColumn:"1/-1" }}>
                        <label style={{ fontSize:11,fontWeight:700,color:"#9ca3af",textTransform:"uppercase",letterSpacing:0.6,display:"block",marginBottom:6 }}>Body</label>
                        <textarea value={editingAnn.body} onChange={e => setEditingAnn(a => ({...a,body:e.target.value}))} rows={3} style={{ width:"100%",padding:"9px 12px",border:"1.5px solid #e8e8e8",borderRadius:8,fontSize:13,boxSizing:"border-box",resize:"vertical" }} />
                      </div>
                      <div>
                        <label style={{ fontSize:11,fontWeight:700,color:"#9ca3af",textTransform:"uppercase",letterSpacing:0.6,display:"block",marginBottom:6 }}>Expires At</label>
                        <input type="datetime-local" value={editingAnn.expiresAt ? new Date(editingAnn.expiresAt).toLocaleString("sv-SE",{timeZone:Intl.DateTimeFormat().resolvedOptions().timeZone}).slice(0,16) : ""} onChange={e => setEditingAnn(a => ({...a,expiresAt:e.target.value?new Date(e.target.value).toISOString():""}))} style={{ width:"100%",padding:"9px 12px",border:"1.5px solid #e8e8e8",borderRadius:8,fontSize:13,boxSizing:"border-box" }} />
                      </div>
                      <div style={{ display:"flex",alignItems:"flex-end",gap:8 }}>
                        <button onClick={async () => { await setDoc(doc(db,"announcements",editingAnn.id),{...editingAnn}); setEditingAnn(null); loadAnnouncements(); }} style={{ background:`linear-gradient(135deg,#6f0000,#a00000)`,color:"white",border:"none",padding:"9px 18px",borderRadius:8,fontSize:13,cursor:"pointer",fontWeight:700 }}>Save Changes</button>
                        <button onClick={() => setEditingAnn(null)} style={{ background:"none",border:"1px solid #e8e8e8",color:"#555",padding:"9px 14px",borderRadius:8,fontSize:13,cursor:"pointer" }}>Cancel</button>
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}

        {activeTab === "messages" && (
          <div style={{ animation:"fadeUp 0.4s ease both" }}>
            <div style={{ marginBottom:22 }}>
              <h1 style={{ fontSize:24,fontWeight:800,color:"#0f0f0f",marginBottom:4,letterSpacing:-0.5 }}>Messages</h1>
              <p style={{ color:"#9ca3af",fontSize:14 }}>Contact form submissions from the landing page</p>
            </div>
            {messages.length === 0 ? (
              <div style={{ ...card, textAlign:"center",padding:"40px",color:"#d1d5db" }}>No messages yet</div>
            ) : (
              <div style={{ display:"grid",gridTemplateColumns:selectedMsg?"1fr 1fr":"1fr",gap:18 }}>
                <div style={card}>
                  {messages.map(m => (
                    <div key={m.id} onClick={async () => {
                      setSelectedMsg(m);
                      if (!m.read) {
                        await setDoc(doc(db, "contactMessages", m.id), { read: true }, { merge: true });
                        loadMessages();
                      }
                    }} style={{ padding:"14px 16px",borderRadius:10,cursor:"pointer",background:selectedMsg?.id===m.id?"#fff0f0":m.read?"white":"#fffbeb",border:`1px solid ${selectedMsg?.id===m.id?MAROON:m.read?"#f0f0f0":"#fcd34d"}`,marginBottom:10,transition:"all 0.15s" }}
                      onMouseOver={e=>{ if(selectedMsg?.id!==m.id) e.currentTarget.style.borderColor=MAROON; }}
                      onMouseOut={e=>{ if(selectedMsg?.id!==m.id) e.currentTarget.style.borderColor=m.read?"#f0f0f0":"#fcd34d"; }}>
                      <div style={{ display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:4 }}>
                        <div style={{ fontWeight:700,fontSize:14,color:"#1a1a1a" }}>{m.name}</div>
                        {!m.read && <span style={{ background:"#dc2626",color:"white",borderRadius:20,padding:"1px 8px",fontSize:10,fontWeight:800 }}>New</span>}
                      </div>
                      <div style={{ fontSize:12,color:"#666",marginBottom:4 }}>{m.email}</div>
                      <div style={{ fontSize:13,color:"#555",overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap" }}>{m.message}</div>
                      <div style={{ fontSize:11,color:"#aaa",marginTop:6 }}>{new Date(m.sentAt).toLocaleString()}</div>
                    </div>
                  ))}
                </div>
                {selectedMsg && (
                  <div style={card}>
                    <div style={{ display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:20 }}>
                      <div style={{ fontSize:11,fontWeight:700,color:"#9ca3af",textTransform:"uppercase",letterSpacing:0.6 }}>Message Detail</div>
                      <button onClick={()=>setSelectedMsg(null)} style={{ background:"none",border:"none",fontSize:18,cursor:"pointer",color:"#aaa" }}>×</button>
                    </div>
                    <div style={{ marginBottom:14 }}>
                      <div style={{ fontSize:11,color:"#aaa",marginBottom:2 }}>FROM</div>
                      <div style={{ fontWeight:700,fontSize:15,color:"#1a1a1a" }}>{selectedMsg.name}</div>
                      <div style={{ fontSize:13,color:"#666" }}>{selectedMsg.email}</div>
                    </div>
                    <div style={{ marginBottom:14 }}>
                      <div style={{ fontSize:11,color:"#aaa",marginBottom:2 }}>RECEIVED</div>
                      <div style={{ fontSize:13,color:"#555" }}>{new Date(selectedMsg.sentAt).toLocaleString()}</div>
                    </div>
                    <div>
                      <div style={{ fontSize:11,color:"#aaa",marginBottom:8 }}>MESSAGE</div>
                      <div style={{ background:"#f8f8f8",borderRadius:10,padding:"16px",fontSize:14,color:"#333",lineHeight:1.7,whiteSpace:"pre-wrap" }}>{selectedMsg.message}</div>
                    </div>
                    <a href={`mailto:${selectedMsg.email}?subject=Re: TRIP Program Inquiry`}
                      style={{ display:"inline-block",marginTop:20,background:`linear-gradient(135deg,${MAROON},#a00000)`,color:"white",padding:"11px 24px",borderRadius:10,fontWeight:700,fontSize:14,textDecoration:"none",boxShadow:"0 4px 12px rgba(111,0,0,0.2)" }}>
                      Reply via Email →
                    </a>
                  </div>
                )}
              </div>
            )}
          </div>
        )}

        {activeTab === "draft" && (() => {
          const nonInterviewGroups = new Set((programSettings.groups||[]).filter(g=>!g.presentationType).map(g=>g.value));
          const eligibleStudents = students.filter(s => !nonInterviewGroups.has(s.group));
          // Group 17 students are seeking Spring & Summer internships, so they
          // stay in the pool until they've actually been picked twice — not
          // removed after their first internship. Everyone else needs 1 pick.
          const requiredPicks = s => s.group === "17" ? 2 : 1;
          const pickCounts = {};
          draftPicks.forEach(p => { pickCounts[p.studentName] = (pickCounts[p.studentName]||0) + 1; });
          const availableStudents = eligibleStudents.filter(s => (pickCounts[s.firstName+" "+s.lastName]||0) < requiredPicks(s));
          const roundColors = TIER_COLORS;
          const roundLabels = { platinum:"Platinum", gold:"Gold", silver:"Silver" };

          async function logPick() {
            if (!draftLiveCompany || !draftLiveStudent) return;
            const student = students.find(s => s.id === draftLiveStudent);
            const company = boardMembers.find(b => b.id === draftLiveCompany);
            if (!student || !company) return;
            const studentName = student.firstName+" "+student.lastName;
            const companyName = company.profile?.companyName || company.displayEmail || company.name || "Unknown";
            const newPick = { studentName, studentId: student.id, company: companyName, companyId: company.id, round: draftLiveRound, season: draftLiveSeason, ts: new Date().toISOString() };
            const newPicks = [...draftPicks, newPick];
            setDraftPicks(newPicks);
            setDraftLiveStudent("");
            // Write to draftResults (with company info)
            await setDoc(doc(db,"metadata","draftResults"), { picks: newPicks, published: draftPublished, nextRoundDate: draftNextRoundDate }, { merge: false });
            // Keep draftedStudents in sync for scheduler — push every pick
            // (don't dedupe by name) so a student needing 2 internships
            // (Group 17) can appear twice and be counted correctly.
            const dSnap = await getDoc(doc(db,"metadata","draftedStudents"));
            const ex = dSnap.exists() ? dSnap.data().drafted||{} : {};
            if (!Array.isArray(ex[draftLiveRound])) ex[draftLiveRound] = [];
            ex[draftLiveRound].push(studentName);
            await setDoc(doc(db,"metadata","draftedStudents"), { drafted: ex });
            showToast(`${studentName} → ${companyName}`, "success");
          }

          async function undoPick(idx) {
            const pick = draftPicks[idx];
            const newPicks = draftPicks.filter((_,i)=>i!==idx);
            setDraftPicks(newPicks);
            await setDoc(doc(db,"metadata","draftResults"), { picks: newPicks, published: draftPublished, nextRoundDate: draftNextRoundDate }, { merge: false });
            // Remove exactly one occurrence from draftedStudents — filtering
            // out every matching name would wrongly wipe a student's other
            // remaining pick(s) too.
            const dSnap = await getDoc(doc(db,"metadata","draftedStudents"));
            const ex = dSnap.exists() ? dSnap.data().drafted||{} : {};
            if (Array.isArray(ex[pick.round])) {
              const i = ex[pick.round].indexOf(pick.studentName);
              if (i !== -1) ex[pick.round].splice(i, 1);
            }
            await setDoc(doc(db,"metadata","draftedStudents"), { drafted: ex });
            showToast("Pick removed", "success");
          }

          async function saveNextRoundDate() {
            await setDoc(doc(db,"metadata","draftResults"), { picks: draftPicks, published: draftPublished, nextRoundDate: draftNextRoundDate }, { merge: false });
            showToast("Next round date saved", "success");
          }

          async function togglePublish() {
            const next = !draftPublished;
            setDraftPublished(next);
            await setDoc(doc(db,"metadata","draftResults"), { picks: draftPicks, published: next, nextRoundDate: draftNextRoundDate }, { merge: false });
            showToast(next ? "Draft results published — students can now view them" : "Draft results unpublished", next ? "success" : "success");
          }

          const roundTotals = { platinum: draftPicks.filter(p=>p.round==="platinum").length, gold: draftPicks.filter(p=>p.round==="gold").length, silver: draftPicks.filter(p=>p.round==="silver").length };

          return (
          <div style={{ animation:"fadeUp 0.4s ease both" }}>
            {/* Header */}
            <div style={{ display:"flex",justifyContent:"space-between",alignItems:"flex-end",marginBottom:22 }}>
              <div>
                <h1 style={{ fontSize:24,fontWeight:800,color:"#0f0f0f",marginBottom:4,letterSpacing:-0.5 }}>Draft</h1>
                <p style={{ color:"#9ca3af",fontSize:14 }}>Log picks live · publish results to students</p>
              </div>
              <button onClick={togglePublish} style={{ padding:"10px 22px",borderRadius:10,background:draftPublished?"#fff1f0":"linear-gradient(135deg,#6f0000,#a00000)",color:draftPublished?"#dc2626":"white",border:draftPublished?"1.5px solid #fca5a5":"none",fontWeight:700,fontSize:13,cursor:"pointer",boxShadow:draftPublished?"none":"0 4px 12px rgba(111,0,0,0.2)" }}>
                {draftPublished ? "⚠ Unpublish Results" : "Publish Results →"}
              </button>
            </div>

            {/* Stats row */}
            <div style={{ display:"grid",gridTemplateColumns:"repeat(5,1fr)",gap:14,marginBottom:22 }}>
              {[
                { label:"Total Drafted", value:draftPicks.length, color:MAROON },
                { label:"Platinum Picks", value:roundTotals.platinum, color:TIER_COLORS.platinum },
                { label:"Gold Picks", value:roundTotals.gold, color:TIER_COLORS.gold },
                { label:"Silver Picks", value:roundTotals.silver, color:TIER_COLORS.silver },
                { label:"Available", value:availableStudents.length, color:"#166534" },
              ].map(s=>(
                <div key={s.label} style={{ background:"white",borderRadius:12,padding:"16px 20px",boxShadow:"0 1px 3px rgba(0,0,0,0.05)",border:"1px solid #f0f0f0" }}>
                  <div style={{ fontSize:10,fontWeight:700,color:"#9ca3af",textTransform:"uppercase",letterSpacing:0.6,marginBottom:6 }}>{s.label}</div>
                  <div style={{ fontSize:28,fontWeight:900,color:s.color,letterSpacing:-1 }}>{s.value}</div>
                </div>
              ))}
            </div>

            {/* Live entry */}
            <div style={{ ...card,marginBottom:18,border:`1.5px solid ${MAROON}22` }}>
              <div style={{ fontSize:11,fontWeight:700,color:"#9ca3af",textTransform:"uppercase",letterSpacing:0.6,marginBottom:14 }}>Log Pick</div>
              <div style={{ display:"flex",gap:8,marginBottom:14 }}>
                {["platinum","gold","silver"].map(r=>(
                  <button key={r} onClick={()=>{setDraftLiveRound(r); setDraftLiveCompany("");}} style={{ padding:"8px 18px",borderRadius:8,border:`2px solid ${draftLiveRound===r?roundColors[r]:"#e8e8e8"}`,background:draftLiveRound===r?`${roundColors[r]}15`:"white",color:draftLiveRound===r?roundColors[r]:"#555",fontWeight:700,fontSize:12,cursor:"pointer",textTransform:"capitalize",transition:"all 0.15s" }}>
                    {roundLabels[r]}
                  </button>
                ))}
                <div style={{ width:1,background:"#e8e8e8",margin:"2px 4px" }} />
                {["spring","summer"].map(s=>(
                  <button key={s} onClick={()=>setDraftLiveSeason(s)} style={{ padding:"8px 18px",borderRadius:8,border:`2px solid ${draftLiveSeason===s?"#0f766e":"#e8e8e8"}`,background:draftLiveSeason===s?"#0f766e15":"white",color:draftLiveSeason===s?"#0f766e":"#555",fontWeight:700,fontSize:12,cursor:"pointer",textTransform:"capitalize",transition:"all 0.15s" }}>
                    {s === "spring" ? "Spring" : "Summer"}
                  </button>
                ))}
              </div>
              <div style={{ display:"grid",gridTemplateColumns:"1fr 1fr auto",gap:10,alignItems:"end" }}>
                <div>
                  <label style={{ display:"block",fontSize:11,fontWeight:700,color:"#9ca3af",textTransform:"uppercase",letterSpacing:0.6,marginBottom:6 }}>Company <span style={{ color:"#86efac",fontWeight:600,textTransform:"capitalize" }}>({roundLabels[draftLiveRound]} only)</span></label>
                  <select value={draftLiveCompany} onChange={e=>setDraftLiveCompany(e.target.value)} style={{ width:"100%",padding:"10px 12px",border:"1.5px solid #ebebeb",borderRadius:10,fontSize:14,outline:"none",background:"white",cursor:"pointer" }}>
                    <option value="">Select company…</option>
                    {[...boardMembers].filter(b=>b.profile?.memberType?.toLowerCase().includes(draftLiveRound)).sort((a,b)=>(a.profile?.companyName||a.name||"").localeCompare(b.profile?.companyName||b.name||"")).map(b=>(
                      <option key={b.id} value={b.id}>{b.profile?.companyName||b.displayEmail||b.name}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label style={{ display:"block",fontSize:11,fontWeight:700,color:"#9ca3af",textTransform:"uppercase",letterSpacing:0.6,marginBottom:6 }}>Student <span style={{ color:"#86efac",fontWeight:600 }}>({availableStudents.length} available)</span></label>
                  <select value={draftLiveStudent} onChange={e=>setDraftLiveStudent(e.target.value)} style={{ width:"100%",padding:"10px 12px",border:"1.5px solid #ebebeb",borderRadius:10,fontSize:14,outline:"none",background:"white",cursor:"pointer" }}>
                    <option value="">Select student…</option>
                    {[...availableStudents].sort((a,b)=>(a.firstName+" "+a.lastName).localeCompare(b.firstName+" "+b.lastName)).map(s=>(
                      <option key={s.id} value={s.id}>{s.firstName} {s.lastName} — Group {s.group}</option>
                    ))}
                  </select>
                </div>
                <button onClick={logPick} disabled={!draftLiveCompany||!draftLiveStudent} style={{ padding:"10px 24px",borderRadius:10,background:(!draftLiveCompany||!draftLiveStudent)?"#e8e8e8":`linear-gradient(135deg,${MAROON},#a00000)`,color:(!draftLiveCompany||!draftLiveStudent)?"#aaa":"white",border:"none",fontWeight:700,fontSize:14,cursor:(!draftLiveCompany||!draftLiveStudent)?"not-allowed":"pointer",boxShadow:(!draftLiveCompany||!draftLiveStudent)?"none":"0 4px 12px rgba(111,0,0,0.2)",transition:"all 0.15s",whiteSpace:"nowrap" }}>
                  Log Pick ↵
                </button>
              </div>
            </div>

            {/* Picks list */}
            <div style={{ ...card,marginBottom:18 }}>
              <div style={{ display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:14 }}>
                <div style={{ fontSize:11,fontWeight:700,color:"#9ca3af",textTransform:"uppercase",letterSpacing:0.6 }}>All Picks ({draftPicks.length})</div>
              </div>
              {draftPicks.length === 0 ? (
                <div style={{ textAlign:"center",padding:"32px",color:"#d1d5db" }}>
                  <div style={{ fontSize:32,marginBottom:8 }}>◈</div>
                  <div style={{ fontSize:14,fontWeight:600,color:"#9ca3af" }}>No picks logged yet</div>
                  <div style={{ fontSize:12,color:"#d1d5db",marginTop:4 }}>Use the form above to log picks as they're called out.</div>
                </div>
              ) : (
                <div style={{ display:"flex",flexDirection:"column",gap:6 }}>
                  {draftPicks.map((pick,idx)=>(
                    <div key={idx} style={{ display:"flex",alignItems:"center",gap:12,padding:"11px 14px",background:"#fafafa",borderRadius:10,border:"1px solid #f0f0f0" }}>
                      <div style={{ width:26,height:26,borderRadius:"50%",background:`${roundColors[pick.round]}20`,border:`1.5px solid ${roundColors[pick.round]}50`,display:"flex",alignItems:"center",justifyContent:"center",fontSize:11,fontWeight:900,color:roundColors[pick.round],flexShrink:0 }}>{idx+1}</div>
                      <div style={{ flex:1 }}>
                        <div style={{ fontWeight:700,fontSize:13,color:"#1a1a1a" }}>{pick.studentName}</div>
                        <div style={{ fontSize:11,color:"#9ca3af",marginTop:1 }}>{pick.company}</div>
                      </div>
                      <span style={{ fontSize:10,fontWeight:700,padding:"3px 10px",borderRadius:20,background:`${roundColors[pick.round]}15`,color:roundColors[pick.round],textTransform:"capitalize",border:`1px solid ${roundColors[pick.round]}30` }}>{pick.round}</span>
                      {pick.season && <span style={{ fontSize:10,fontWeight:700,padding:"3px 10px",borderRadius:20,background:"#0f766e15",color:"#0f766e",textTransform:"capitalize",border:"1px solid #0f766e30" }}>{pick.season}</span>}
                      <button onClick={()=>undoPick(idx)} style={{ background:"none",border:"1px solid #fca5a5",color:"#dc2626",padding:"4px 10px",borderRadius:6,fontSize:11,cursor:"pointer",fontWeight:600 }}>Undo</button>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Next round date + publish */}
            <div style={{ ...card }}>
              <div style={{ fontSize:11,fontWeight:700,color:"#9ca3af",textTransform:"uppercase",letterSpacing:0.6,marginBottom:14 }}>Next Round Settings</div>
              <p style={{ fontSize:13,color:"#6b7280",marginBottom:14,lineHeight:1.6 }}>Set the date/time when undrafted students should check back for their next round interview schedule. This message is shown on their results page.</p>
              <div style={{ display:"flex",gap:10,alignItems:"center" }}>
                <input type="datetime-local" value={draftNextRoundDate} onChange={e=>setDraftNextRoundDate(e.target.value)} style={{ flex:1,padding:"10px 14px",border:"1.5px solid #ebebeb",borderRadius:10,fontSize:14,outline:"none" }} />
                <button onClick={saveNextRoundDate} style={{ padding:"10px 20px",borderRadius:10,background:`linear-gradient(135deg,${MAROON},#a00000)`,color:"white",border:"none",fontWeight:700,fontSize:13,cursor:"pointer",whiteSpace:"nowrap" }}>Save Date</button>
              </div>
            </div>
          </div>
          );
        })()}

        {activeTab === "settings" && (
          <div style={{ animation: "fadeUp 0.4s ease both" }}>
            <SettingsTab settings={programSettings} onSave={saveSettings} card={card} btn={btn} MAROON={MAROON} />

            {/* Website Lock */}
            <div style={{ ...card, border: `1px solid ${websiteLocked ? "#fca5a5" : "#e8e8e8"}`, marginTop: 8, marginBottom: 16 }}>
              <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 20 }}>
                <div>
                  <div style={{ fontSize: 14, fontWeight: 800, color: websiteLocked ? "#991b1b" : "#1a1a1a", marginBottom: 4 }}>
                    {websiteLocked ? "⚠ Website is Locked" : "Lock Website"}
                  </div>
                  <p style={{ fontSize: 13, color: "#6b7280", margin: 0, lineHeight: 1.6 }}>
                    When locked, students and board members see a maintenance screen and cannot access any part of the platform. The admin portal is unaffected.
                  </p>
                </div>
                <button onClick={async () => {
                  const next = !websiteLocked;
                  if (next && !window.confirm("This will lock the website for all students and board members immediately. Proceed?")) return;
                  setWebsiteLocked(next);
                  await setDoc(doc(db, "metadata", "publishStatus"), { websiteLocked: next }, { merge: true });
                  showToast(next ? "Website locked — students and board members are blocked" : "Website unlocked", next ? "error" : "success");
                }} style={{ flexShrink: 0, padding: "10px 22px", borderRadius: 10, background: websiteLocked ? "linear-gradient(135deg,#6f0000,#a00000)" : "white", color: websiteLocked ? "white" : "#dc2626", border: websiteLocked ? "none" : "1.5px solid #fca5a5", fontWeight: 700, fontSize: 13, cursor: "pointer", whiteSpace: "nowrap", boxShadow: websiteLocked ? "0 4px 12px rgba(111,0,0,0.2)" : "none" }}>
                  {websiteLocked ? "Unlock Website" : "Lock Website"}
                </button>
              </div>
            </div>

            {/* New Year Reset */}
            <div style={{ ...card, border: "1px solid #fca5a5", marginTop: 8 }}>
              <div style={{ fontSize: 13, fontWeight: 800, color: "#991b1b", marginBottom: 6 }}>New Program Year Reset</div>
              <p style={{ fontSize: 13, color: "#6b7280", marginBottom: 20, lineHeight: 1.6 }}>
                Use these controls at the start of each new program year to clear submissions while keeping user accounts and settings intact.
                The full reset requires typing "NEW YEAR" to confirm.
              </p>
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 12 }}>
                <div style={{ background: "#fafafa", borderRadius: 10, padding: "16px", border: "1px solid #f0f0f0" }}>
                  <div style={{ fontWeight: 700, fontSize: 13, color: "#333", marginBottom: 6 }}>Clear Student Data</div>
                  <div style={{ fontSize: 12, color: "#aaa", marginBottom: 14, lineHeight: 1.5 }}>Deletes all student submissions, resumes, and photos references. Accounts remain.</div>
                  <button onClick={() => clearAllData("students")} style={{ width: "100%", background: "none", border: "1.5px solid #fcd34d", color: "#92400e", padding: "9px", borderRadius: 8, fontSize: 12, cursor: "pointer", fontWeight: 700 }}>
                    Clear Student Submissions
                  </button>
                </div>
                <div style={{ background: "#fafafa", borderRadius: 10, padding: "16px", border: "1px solid #f0f0f0" }}>
                  <div style={{ fontWeight: 700, fontSize: 13, color: "#333", marginBottom: 6 }}>Clear Board Data</div>
                  <div style={{ fontSize: 12, color: "#aaa", marginBottom: 14, lineHeight: 1.5 }}>Deletes all board member preferences, RSVPs, and candidate selections. Accounts remain.</div>
                  <button onClick={() => clearAllData("board")} style={{ width: "100%", background: "none", border: "1.5px solid #fcd34d", color: "#92400e", padding: "9px", borderRadius: 8, fontSize: 12, cursor: "pointer", fontWeight: 700 }}>
                    Clear Board Submissions
                  </button>
                </div>
                <div style={{ background: "#fff1f0", borderRadius: 10, padding: "16px", border: "1px solid #fca5a5" }}>
                  <div style={{ fontWeight: 700, fontSize: 13, color: "#991b1b", marginBottom: 6 }}>Full Program Reset</div>
                  <div style={{ fontSize: 12, color: "#aaa", marginBottom: 14, lineHeight: 1.5 }}>Clears all submissions, resume books, and schedules. Requires typing "NEW YEAR" to confirm.</div>
                  <button onClick={() => clearAllData("all")} style={{ width: "100%", background: "linear-gradient(135deg, #991b1b, #dc2626)", color: "white", border: "none", padding: "9px", borderRadius: 8, fontSize: 12, cursor: "pointer", fontWeight: 700, boxShadow: "0 4px 12px rgba(220,38,38,0.25)" }}>
                    Full Reset — New Year
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

      </div>
    </div>
  );
}
